// ─── MFML UNIVERSE ENCYCLOPEDIA ──────────────────────────────────────────────
// The primary knowledge source for the AI API. Character bios + this file
// should answer most questions. Only fall back to Discord RP data search
// when something here doesn't cover the topic.
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════════
// SPECIES
// ═══════════════════════════════════════════════════════════════════════════════
export const SPECIES = [
  {
    id: "vampire",
    name: "Vampire",
    category: "Undead / Supernatural",
    description: "Immortal blood-drinking beings organized into noble families and political hierarchies. Vampires in MFML range from ancient nobility (2,000+ years old) to newly turned. They have heightened senses, superhuman strength, and the ability to mark others with bites that carry social and magical significance.",
    subtypes: [
      { name: "Pureblood", description: "Born vampire, not turned. Higher status in vampire society. The Han family and Von Dabb family are prominent pureblood lines." },
      { name: "Turned", description: "Humans or other species converted to vampires through the turning process. Lower social status than purebloods but can be just as powerful." },
      { name: "Dhampir", description: "Half-vampire, half-human (or half-other species). Chiara is a dhampir — daughter of Dimitri (vampire) and Evelyn (goddess). Dhampirs may lose vampiric traits at puberty depending on bloodline curses." },
      { name: "Daywalker", description: "Vampires who can walk in sunlight. Rare and highly valued." },
      { name: "Vampire-Siren Hybrid", description: "Extremely rare. Sirens turned into vampires retain their compulsion abilities — a siren-vampire can compel almost anyone, including other vampires. Kya is one such hybrid." },
    ],
    abilities: [
      "Superhuman strength, speed, and senses",
      "Blood marking — bites carry social meaning (possession, soulmate marks, feeding marks)",
      "Heightened hearing (can overhear conversations across buildings)",
      "Eternal youth and near-immortality",
      "Blood bond rituals (vampire marriage equivalent)",
      "Compulsion (limited, stronger in elders)",
      "Some can survive on donated blood rather than hunting",
    ],
    weaknesses: [
      "Sunlight (most vampires, unless daywalker)",
      "Cannot taste food — can eat but get no flavor",
      "Heightened emotions can be destabilizing",
      "Silver and certain magical bindings",
      "Bloodlust if not managed",
    ],
    turningProcess: {
      description: "Vampires can turn other species into vampires. The process is irreversible — 'once a vampire always a vampire' is a fundamental rule. Turning involves a vampire draining the target and then feeding them vampiric blood.",
      rules: [
        "Irreversible — no known way to undo a full turning",
        "Soulmate bonds between vampires manifest as crescent-shaped marks on the wrist, visible through bites",
        "Newly turned vampires may struggle with bloodlust and heightened emotions",
        "Turning a werewolf into a vampire (or vice versa) is extremely rare and controversial — Bryce was turned from vampire to werewolf, a nearly unprecedented event",
        "Sirens who are turned retain siren compulsion abilities, making siren-vampires exceptionally dangerous",
        "Vampire children of mixed parentage (dhampirs) may lose vampiric traits at puberty depending on bloodline curses (see Apollo's curse on Dimitri's line)",
      ],
    },
    politicalSystem: {
      description: "Vampire politics are intricate, ruthless, and power-obsessed. The constant struggle for dominance defines their civilization.",
      hierarchy: [
        "Kings — rule individual vampire kingdoms/territories. Examples: Bryce (Von Dabb), Dimitri, Emerson",
        "Elders — ancient vampires on the Immortuos council. Must be extremely old (under 1,000 is almost impossible to become elder). Val is an elder.",
        "Elder's Circle — a governing council of the most powerful elders who make major decisions for vampire-kind",
        "Noble Families — prominent bloodlines with political power. Han family (Ezra, Jumin) and Von Dabb family (Bryce) are the strongest allied families",
        "Political Alliance Heads — roles like Lord Bradford (Simon) who manage inter-family politics",
        "Commonwealth — overseas vampire territories with different rules, often harsher. Known for slave trading and harems",
        "Blood slaves — illegal in reformed territories but still exist in underground markets. Evelyn's reform created ethical blood donation systems",
      ],
      keyRules: [
        "Vampires are inherently power-hungry — 'the temptation of power is always a vampire's downfall'",
        "Assassination and rebellion are constant threats for those in power",
        "Vampire marriages can be strategic political arrangements, not just romantic",
        "The Immortuos can declare war — the werewolf-vampire war of 1983 nearly wiped out both species",
        "Book keepers (historians like Val) control the narrative of vampire history — dangerous because they can twist records in their favor",
        "Vampire TV networks exist — vampires have their own private media channels",
      ],
    },
    socialStructure: "Vampires organize into noble families and political courts. The Immortuos is a governing body of powerful vampires. Kings and queens rule territories. Blood status (pureblood vs turned) creates a rigid class system. Blood bonds serve as unbreakable marriage contracts. Vampires have private television networks for their community.",
    culturalNotes: "Vampire society values power, lineage, and political maneuvering. Blood slaves and blood banks exist — Evelyn worked to establish ethical donated blood systems. Marking someone with a bite is a significant social act — it declares possession and can indicate soulmate bonds. Kelp tea is a real thing Alistair drinks seriously. Vampires can fly planes (a plane crash wouldn't kill them but would hurt). Many vampires wish they were werewolves instead — Evelyn said 'I wish I was born a werewolf. Life would have been so much easier.' A vampire who wanted power would 'tear each other apart reaching for your position' if a strong leader dies. Underground kingdoms like Dimitri's domain may keep illegal prisoners including collared water nymphs. Vampires have a private television network with their own news broadcasts.",
    keyCharacters: ["Alistair", "Ezra", "Bryce", "Dimitri", "Sebastian", "Evelyn (formerly)", "Lindon", "Vaughn", "Jumin", "Val", "James", "Simon/Lord Bradford"],
  },
  {
    id: "werewolf",
    name: "Werewolf",
    category: "Shifter / Supernatural",
    description: "Wolf-shifting beings organized into packs with strict hierarchies. Werewolves in MFML have a deep spiritual connection to their wolf forms, which are separate consciousnesses they must learn to coexist with. Pack bonds are central to their identity.",
    subtypes: [
      { name: "Alpha", description: "Pack leaders. Can give alpha commands that other wolves must obey. True Alphas earned the position; some inherit it by birthright." },
      { name: "Beta", description: "Second-in-command. First Beta is the most trusted advisor to the Alpha." },
      { name: "Omega", description: "Lowest rank but not necessarily weak. Can handle Alpha energy without being overwhelmed — some Omegas are uniquely powerful." },
      { name: "Rogue", description: "Wolves without a pack. Unprotected and generally distrusted. Some rogues like Rikki can still attend national werewolf meets." },
      { name: "True Alpha", description: "An Alpha whose position is earned through character, not just bloodline. Cruz is a True Alpha — Ford was the 'birthright' Alpha but Cruz earned it." },
    ],
    abilities: [
      "Shifting between human and wolf form",
      "Pack telepathy (can communicate mind-to-mind within pack bonds)",
      "Alpha commands (Alphas only — compels obedience)",
      "Mating marks — biting during intimate moments creates a permanent bond",
      "Heightened senses, strength, speed",
      "Accelerated healing",
      "Communication with goddess Diana in wolf form (rare, Alpha-specific)",
      "Wolf consciousness — the wolf is a separate entity within the person",
    ],
    weaknesses: [
      "Silver",
      "Wolfsbane",
      "Pack bonds can be severed (devastating psychologically)",
      "Losing control during full moons or intense emotions",
      "Cannot easily coexist with vampires (historical enmity, though treaties exist)",
    ],
    turningProcess: {
      description: "The werewolf curse is primarily that — a curse. 'It wipes out all that was before and leaves anew.' A werewolf bite during certain conditions (intentional or accidental) transforms the bitten into a werewolf.",
      rules: [
        "A bite from a werewolf can turn someone — it doesn't always have to be intentional (Rich accidentally bit his mate)",
        "The change takes days — the person transforms gradually, gaining wolf instincts and a wolf consciousness",
        "The wolf becomes 'one with us and in return we receive the benefits of their abilities'",
        "Newly turned wolves have higher metabolisms and larger appetites",
        "Some wolves are late bloomers — Audrey was a late bloomer with her wolf, like her mother",
        "The werewolf curse forces the family to 'live the same fate of changing with every moon, sharing our body with someone else'",
        "Heat cycles affect all true mates — 'the goddess created wolves to be very potent and typically true mates grow pregnant after the first mating session'",
        "A vampire can be turned into a werewolf (Bryce), but this is nearly unprecedented and wipes vampire traits",
      ],
    },
    wolfConsciousness: {
      description: "The wolf is a separate entity within the person — not just an animal form but a distinct consciousness with its own desires and instincts.",
      details: [
        "Wolf and human must learn to coexist — some wolves have solidarity with their wolf while others feel disconnected",
        "The wolf can push for certain actions (especially mating) — 'it didn't take much for him to convince that part of himself'",
        "Wolves communicate through pack telepathy — mates can send thoughts and emotions to each other",
        "Wolf form is faster for travel but some wolves prefer to stay human to 'feel normal'",
        "The wolf's rage can be uncontrollable in some bloodlines (Thor's rage inherited by Agmund)",
        "Late bloomers may not have awakened their wolf yet",
      ],
    },
    packLifeDetails: {
      description: "Pack houses are elaborate communal living spaces with modern amenities, elevators, multiple floors with different wings.",
      details: [
        "Pack houses have bunkers for emergencies (used during attacks)",
        "Living arrangements are determined by rank — guests get assigned wings (e.g., 'take the elevator to the fourth floor, guest suite in the D wing')",
        "The 37th floor apartment is where Ryder, Audrey, Alyce, and Allie live",
        "Patrol routes and border checkpoints protect pack territory",
        "Some packs have modern entertainment (movies, TV, video games)",
        "Pack 'fun days' include activities like water gun fights",
        "Rangers are guardians of werewolf-kind — 'there aren't better wolves out there than the rangers'",
        "Northern packs are generally allied; southern states require more caution",
      ],
    },
    socialStructure: "Packs are family units with strict hierarchies. The Alpha leads, the Beta advises, and ranks descend from there. Pack houses serve as elaborate communal homes with modern amenities, multiple floors, elevators, and different wings. National werewolf meets happen annually where all packs share information about supernatural creature activity. Rogues are outside this system and vulnerable — they 'don't usually have a bank account' and resort to petty theft. Mating bonds are considered sacred and nearly unbreakable. If an alpha dies, the mating bond dissipates — 'the way all werewolf bonds do the moment one moves onto the afterlife.'",
    culturalNotes: "Werewolves and vampires have a treaty to 'agree to disagree and stay the hell out of each other's way.' Cruz taking in Bryce (a former vampire) was unprecedented and controversial. Wolves view vampires as 'elitist pricks who don't even spend their money the fun way.' Pack hierarchy determines everything from living arrangements to romantic permissions. An alpha choosing a mate is a political decision — Ford thought he was 'getting the crème de la crème of mates.' Cruz describes love as 'like getting to read a really good book and understanding each page and wanting to cherish it.' Werewolves were historically hunted and persecuted. In some AUs werewolves are 'much more powerful since the war never happened.' The lunar goddess Diana plays a role in wolf pairings — 'if the Lunar goddess would have cock blocked them, she would have.' Pack members bear each other's mistakes — 'we fix things together.'",
    keyCharacters: ["Cruz", "Ford", "Nolan", "Ryder", "Audrey", "Lucius", "Bryce (converted)", "Rowan", "Lance", "Rich", "Seven", "Trey"],
  },
  {
    id: "siren",
    name: "Siren",
    category: "Aquatic / Supernatural",
    description: "Aquatic beings with compelling voices, tails, and deep connections to the ocean. Sirens in MFML have a rich royal court system and complex bonding rituals. Their voices can compel others, and their social bonds (tailbonds, landbonds) are among the most significant connections in the MFML universe.",
    subtypes: [
      { name: "Atlantic Royalty", description: "The ruling family of the Atlantic Siren Kingdom. Kya is Princess of the Atlantic Sirens." },
      { name: "Siren King/Queen", description: "Rulers of siren territories. Sebastian became King after Kya disappeared." },
      { name: "Siren-Vampire Hybrid", description: "Extremely rare. Retains siren compulsion abilities enhanced by vampire power." },
    ],
    abilities: [
      "Siren voice — can compel others, enhanced by emotion and training",
      "Tail transformation — shift between legs and tail in water",
      "Tailbonding — deep intimate bond formed between sirens (or siren and another being)",
      "Landbonding — a bond that connects a siren to a land-dwelling being across lifetimes",
      "Trident magic — royal sirens can wield trident power",
      "Natural grace in water (though new sirens need practice)",
      "Compulsion — siren-vampires can compel almost anyone",
    ],
    tailbondDetails: {
      description: "Tailbonding is the deepest siren connection — more intimate than any other bond type. It involves physical contact with tails/siren forms and creates an unbreakable spiritual link.",
      rules: [
        "Requires physical contact with tails — the tail is an erogenous zone for sirens ('Is touching your tail sexual to you?')",
        "Forcing a tailbond is treason in siren culture — Sebastian knew this prevented him from simply bonding with Kya",
        "Multiple tailbonds are possible but extremely unusual — Kya was shocked to be bonded to multiple people simultaneously",
        "A tailbond connects thoughts and sensory experiences at a deeper level than mating bonds",
        "Tailbonding ceremony happens in underwater air bubble halls for non-swimming partners",
        "Flipping a fin at someone is the siren equivalent of flipping someone off — 'like saying dickwad or asshole'",
        "Sirens who become vampires lose their tails but may retain other siren traits",
        "Training sessions for new sirens are intense and constant — Dillon runs them strictly",
      ],
    },
    courtProtocols: {
      description: "The Atlantic Siren Kingdom has elaborate court protocols, diplomatic procedures, and hierarchy.",
      details: [
        "Siren compounds house multiple families — Kya's family runs their compound and keeps the peace",
        "Royal advisors like Dillon manage training, ceremonies, and diplomatic relations — Dillon is strict but deeply cares about the royals",
        "Topps is head of household — manages scheduling, staff, and ensures castle operations run smoothly",
        "Ladies in waiting serve the royal family — trained to assist the princess in ocean or surface duties until she forfeits the crown",
        "Noble siren families jockey for influence: Voux family (prestigious, wealthy), Trist family (longest-established influential family), Quartz family (younger, more casual)",
        "Marriage applications can be submitted formally — Prince Aaron submitted one to Kya",
        "Royal weddings are elaborate affairs — the bride and groom are separated before the ceremony, following tradition",
        "The trident is finicky and sometimes has a mind of its own — using it for time travel could have disastrous results",
        "Siren women often need water after exerting energy; burning out from overuse of powers is a real risk",
        "Arctic Kingdom hosts a week-long festival during prime Northern Lights viewing season in fall",
        "Succession is complex — direct relatives have priority; Kya's grandmother wanted to pass the crown to her mother but couldn't due to her untimely passing",
        "Sirens are exceptionally good at discerning truth from lies",
        "Royal Atlantis Inc. is the siren royal family's surface-world business cover — runs exports for many companies",
        "Kya's grandmother runs the business and requires marriage before passing leadership to the next generation",
        "Siren training for new sirens involves learning to swim properly (new sirens 'swim like guppies'), conduct court business, and manage powers",
        "Sirens have a strict first-event hosting culture — Kya's first royal gathering for leaders her age was extremely important to her",
        "Political marriages are common — referred to as 'beneficial family expansions'",
      ],
    },
    socialStructure: "Royal court system with kings, queens, and princesses. The trident is a symbol of royal power. Siren society is traditionally insular — they 'tend to keep to themselves.' Royal advisors (like Dillon) serve the crown. Tailbonding and landbonding are sacred — forcing a bond is treason. Multiple siren kingdoms exist (Atlantic, Arctic, etc.) with formal diplomatic relations. Coronations are significant events — Kya was coronated to her own siren throne. Half the time can be spent on the surface while the other half the kingdom must be managed.",
    culturalNotes: "A siren-turned-vampire is extremely rare and feared because their compulsion abilities become nearly universal. Sebastian uses the expression 'I swear on the trident' as his most serious oath. Siren culture values grace, family bonds, and ocean connection. The Atlantic Kingdom has formal diplomatic protocols. Nudity is not a big deal in siren culture — 'I'm a siren. A lot of the time I don't sleep in clothes.' Siren voices can be used for seduction and influence — they can pour attraction into their voice to compel others. Siren charity auctions are a thing. Sirens can influence people they find attractive. Kya's singing voice is legendary — described as 'better than any siren.'",
    keyCharacters: ["Kya", "Sebastian", "Polly", "West", "Dillon", "Trevor", "Aaron", "Jake", "Kaleb", "Hadrian", "Kyo", "Lira"],
  },
  {
    id: "fairy",
    name: "Fairy / Fae",
    category: "Magical / Supernatural",
    description: "Magical beings with wings, elemental affinities, and complex political structures. Fairies in MFML range from common fae to powerful fairy princes and kings. They have seasonal courts, prophecy-driven quests, and the ability to manipulate natural forces.",
    subtypes: [
      { name: "Light Fairy", description: "Can sense and verify magic. The first light fairy was exceptionally powerful." },
      { name: "Dark Fae", description: "Practitioners of corrupted magic. Their wings turn dark as night from the corruption. Capable of faking bonds and connections." },
      { name: "High Fae", description: "Powerful fae nobility. Kieran and Peter are High Fae. Different from common fairies in power level and lifespan." },
      { name: "Fairy Prince/Princess", description: "Royal fae. Dutch, Holt, and Archie are fairy princes sent to find Sabrina (Claire) based on prophecy." },
      { name: "Fall Fairy", description: "Seasonal court affiliation. Dash is a Fall Fairy Prince." },
      { name: "Spirit Fairy", description: "Can communicate with spirits and manipulate matter. Gale is a Spirit Fairy King." },
    ],
    abilities: [
      "Flight (wings — appearance varies by type)",
      "Elemental magic (varies by seasonal court)",
      "Prophecy fulfillment — many fairy quests are prophecy-driven",
      "Fae expressions carry literal magical weight",
      "Special collars/poisons can weaken fae and suppress their powers",
      "Reincarnation — fairies reincarnate, sometimes into different fairy types",
      "Bond creation — fairy bonds (like the hippo bond) can be unique and unusual",
    ],
    eternalOneSystem: {
      description: "The fairy equivalent of soulmates. 'Your eternal one exists' — someone whose soul has followed you throughout your lives. A popular legend among fae that the royal family knows is true.",
      rules: [
        "Fae only fall in love once in their lives — 'I know that you fae only fall in love once'",
        "Eternal ones recognize each other across reincarnations — memories from past lives surface through touch and proximity",
        "When fairies have lovers from their past, they always find each other — 'they're drawn to each other like magnets'",
        "Dark fae can FAKE eternal one connections with corrupted magic — this is deeply feared",
        "Multiple eternal ones are possible in rare cases — some fae have two or more",
        "Rejecting or betraying an eternal one causes devastating emotional pain",
        "Past lives always affect things for the fae — you can feel connections, see glimpses",
        "Pixies and High Fae experience eternal ones differently — pixies connect through trust and shared secrets",
        "You have to interact with women (touch, proximity) to start remembering your eternal one — 'you can't find her without touching anyone'",
      ],
    },
    faeCourts: {
      description: "Fae society is organized into seasonal courts, each with unique characteristics and magic affinities, similar to the Tinkerbell/Pixie Hollow style.",
      details: [
        "Seasonal fairies (Spring, Summer, Fall, Winter) — like Pixie Hollow Tinkerbell style",
        "High Fae have wings but more powers than common fairies — different power level and lifespan",
        "Fae castles host balls, competitions, and festivities",
        "Fae towns have shops and marketplaces — fae love cheese and carbs",
        "Water fairies have imprinting — a respected bond that if broken, the water fairies would wage war",
        "Fairy children are protected fiercely — unique wings make some targets",
        "Fae knights serve their kings and handle security and combat training",
        "Kings make decisions on the spot — 'you always make decisions on the spot and give us no time to prepare'",
        "Elves exist in the celestial realm alongside fae — the elven society has been explored less",
        "The conduit council has political influence — Thane is the son of the head of the conduit council",
      ],
    },
    socialStructure: "Seasonal courts (Spring, Summer, Fall, Winter) with kings and princes. Prophecies drive much of fae politics. Fairy markets and auctions exist — dark ones sell captured supernaturals including weaker gods. The Fae King (Peter) rules with significant authority. Fae castles host elaborate balls, competitions, and festivities. Fae knights protect the courts. Competitions can be marriage-related (Sabrina's suitor competition). The fae realm and mortal realm are connected — fae who leave always feel drawn to return. Merlin exists in the fae world and forces people to do mundane tasks like making pudding.",
    culturalNotes: "Claire/Sabrina was a fairy princess taken at birth by a prophecier for protection and raised as a human. The fairy birthmark on her collarbone identifies her — and a crown appeared that got stuck to her head, identifying her as royalty. Aphrodite created Sabrina from love magic as a gift for Cupid. Fairy dust is a recreational substance some use. Dark fae can fake eternal one connections. Fae can be captured and sold at black market auctions while collared with suppression poison. When a fairy dies, they can be reincarnated into different fairy types. Archie has a natural 'finder' magic — he can find people, animals, things, or places naturally. Fae tend to rely on magic and strength, but human bodies need combat retraining. The fae take privacy very seriously — betraying a secret told in confidence is devastating. Prince competitions involve multiple rounds lasting about five days.",
    keyCharacters: ["Claire/Sabrina", "Dutch", "Holt", "Archie", "Peter", "Kieran", "Dash", "Gale", "Anders", "Damien", "Raz", "Draco", "Calyx", "Zander", "Arthur", "Cyrus", "Lily", "Aster", "Eero", "Eira"],
  },
  {
    id: "witch",
    name: "Witch",
    category: "Magical / Human-Adjacent",
    description: "Humans with magical abilities, often inherited through bloodlines. Witches in MFML range from Salem descendants (the real ones) to casual practitioners. Their power varies enormously — some are hunted for their souls, others are powerful enough to challenge supernatural beings.",
    subtypes: [
      { name: "Salem Witch Descendant", description: "The true Salem witch bloodline. Only 7 remain. Kira Nightingale is one. Their souls are hunted by Jane (Ezra's mother) for a massive bloodline ritual." },
      { name: "Witch/Werewolf Hybrid", description: "Haley in the werewolf timeline is a witch/werewolf hybrid and reincarnated Atropos." },
      { name: "Corrupted Witch", description: "Witches who practice forbidden magic. Can create curses that affect entire bloodlines and make entire communities forget a person exists." },
    ],
    abilities: [
      "Spellcasting (varies by bloodline and training)",
      "Visions and prophetic sight (some bloodlines)",
      "Soul magic — witch souls can be harvested for powerful rituals",
      "Curse creation and breaking",
      "Blood magic and blood bonds",
      "Potion crafting",
      "Memory manipulation (powerful witches only)",
    ],
    culturalNotes: "Kira arranged her own blood bond with Ezra in a single day — a strategic marriage to gain vampire protection for the remaining Salem witches before Jane could intercept. Witch souls are extremely valuable in the supernatural economy. The Nightingale family has been hunted for centuries.",
    keyCharacters: ["Kira Nightingale", "Haley (witch/werewolf)", "Jane (Ezra's mother)", "Ella Nightingale"],
  },
  {
    id: "greek_god",
    name: "Greek God / Goddess",
    category: "Divine / Olympian",
    description: "The Greek pantheon rules from Mount Olympus with Zeus as King. Gods in MFML are active participants in mortal and supernatural affairs — they host TV shows, assign soulmates, run schools, deal in souls, and directly interfere in the lives of characters. They are not distant deities; they are messy, dramatic, and deeply entangled in every storyline.",
    subtypes: [
      { name: "Olympian (Major)", description: "Zeus, Hera, Poseidon, Hades, Ares, Athena, Apollo, Aphrodite, Hermes — the core Olympian council." },
      { name: "Demigod", description: "Half-god, half-mortal/supernatural. Evelyn (daughter of Ares), Darren (son of Hades/Persephone), Lex (son of Zeus/Hera), Milo (son of Dike/Hades)." },
      { name: "Titan", description: "The older generation. Iapetus (Zeus's uncle) can turn into darkness itself." },
      { name: "Fate Goddess", description: "The three Fates — Lachesis (Measurer/Present), Atropos (Cutter/Future), Clothos (Spinner/Past). Daughters of Zeus and Nyx." },
    ],
    abilities: [
      "Domain-specific powers (fire for Ares, lightning for Zeus, love manipulation for Aphrodite, etc.)",
      "Immortality and near-invulnerability",
      "Soul manipulation and collection",
      "Bond breaking — Zeus can break ANY bond as King of the Gods",
      "Ambrosia — divine food that can affect mortals (Zeus feeds it to women he likes; Hermes uses it to dull Peter's senses)",
      "Shape-shifting and transformation",
      "Immaculate conception — gods can conceive children through 'stimulating conversation' (Athena and Cupid's method)",
      "Prophecy and future sight (Apollo, the Fates)",
      "Soulmate assignment (Aphrodite)",
      "Deity marriage officiation — Aphrodite officiates marriages that literally cannot be divorced",
    ],
    socialStructure: "Zeus rules as King of the Gods from Olympus. Hera is Queen but their marriage is strained. The Olympian council includes major gods who vote on divine matters. Hades rules the Underworld separately. Apollo runs entertainment. Hermes handles messaging and soul ferrying. Aphrodite controls love and marriage. The Fates (Lachesis, Atropos, Clothos) measure, cut, and spin the threads of life. Gods watch mortals for entertainment.",
    culturalNotes: "Zeus was killed by Cruz with a celestial blade through the heart — one of the most universe-altering events in MFML history. Drachmas are the divine currency. Apollo runs reality TV shows from Olympus. Hermes has modernized his role into 'Insta-Hermes' with star ratings. Gods can be killed but it requires extraordinary means (celestial blade, divine weapons). Children of Ares cannot be hurt by their own fire.",
    keyCharacters: ["Zeus", "Hera", "Ares", "Aphrodite", "Apollo", "Athena", "Hermes", "Hades", "Persephone", "Poseidon", "Lachesis", "Atropos"],
  },
  {
    id: "norse_god",
    name: "Norse God / Goddess",
    category: "Divine / Norse",
    description: "The Norse pantheon operates from Asgard/Valhalla under Odin's rule. Norse gods in MFML are more martial and honor-bound than their Greek counterparts. Ragnarok (the end of the world) is a real, prophesied event that Loki attempted to trigger.",
    subtypes: [
      { name: "Aesir", description: "The main Norse gods — Odin, Thor, Frigg." },
      { name: "Vanir", description: "The secondary Norse gods — Freya is Vanir." },
      { name: "Valkyrie", description: "Odin's chosen warriors who ferry souls of the warrior-dead to Valhalla. Svira is a Valkyrie — a former mortal noblewoman chosen by Odin for her combat abilities." },
      { name: "Norn", description: "Norse fate seers. Penelope is a Norn who can see the future but is forbidden from revealing it in ways that would alter events." },
    ],
    abilities: [
      "Domain-specific powers (thunder for Thor, trickery for Loki, wisdom for Odin)",
      "Rune magic (Odin gained the runes by hanging himself from Yggdrasil)",
      "Valkyrie soul-ferrying to Valhalla or Fólkvangr",
      "Uncontrollable rage (Thor and Agmund — rare but devastating)",
      "Shape-shifting (Loki)",
      "Prophecy/future sight (Norns like Penelope, but restricted in what they can reveal)",
      "Ragnarok instigation (Loki and Fenrir)",
    ],
    socialStructure: "Odin rules as All-Father. Frigg is the highest-standing goddess — no goddess surpasses her. Freya is Odin's concubine, creating a love triangle that defines Norse divine politics. Thor is the most approachable god. Norse weddings ONLY happen on Friday (Frigg's Day) with her blessing and perfect weather she ensures. Valkyries serve Odin directly.",
    culturalNotes: "Loki had his mouth sewn shut for outsmarting dwarves. He must be transported with tape over his mouth because his words are literally weapon-level dangerous. The chains of Ivaldi are one of the few things that can restrain him. Fenrir (Loki's wolf son) is destined to devour the sun and kill Odin during Ragnarok. Agmund inherited Thor's rare uncontrollable rage. Norse gods cross over with Greek gods in the MFML universe — they coexist.",
    keyCharacters: ["Odin", "Thor", "Frigg", "Freya", "Loki", "Svira", "Penelope", "Agmund", "Agnar"],
  },
  {
    id: "angel",
    name: "Angel / Fallen Angel",
    category: "Divine / Celestial",
    description: "Celestial beings created by God. Angels in MFML include archangels, fallen angels, and everything in between. Falling from grace transforms them but doesn't necessarily strip their power — Lucifer retained both divine and angelic abilities.",
    subtypes: [
      { name: "Archangel", description: "The highest-ranking angels. Gabriel and Benny are archangels. Gabriel was beheaded by God for helping Lachesis escape heaven." },
      { name: "Fallen Angel", description: "Angels cast out of heaven. Lucifer is the most prominent. Fallen angels can enter heaven briefly but risk burning up or being shot down by Dike's blue fire." },
      { name: "Guardian Angel", description: "Angels assigned to protect specific individuals." },
    ],
    abilities: [
      "Flight (wings — color indicates status; Lucifer has white wings)",
      "Divine and angelic powers combined (for powerful fallen like Lucifer)",
      "Future sight (Gabriel could see the future)",
      "Heaven access (fallen angels can briefly return but risk destruction)",
      "Soul interaction and manipulation",
      "Wing-clipping (removing wings as punishment or power move)",
    ],
    culturalNotes: "Gabriel helped Lachesis escape heaven and was beheaded by God as punishment. Lucifer's white wings mark him as unique among the fallen. Fallen angels in the Underworld/Hell retain enough power to be major players. Wing-clipping is a significant act in the Lucifer/Ford/Lachesis triangle (Fordsplosion route).",
    keyCharacters: ["Lucifer", "Gabriel", "Benny", "Garrett (fallen angel/werewolf/dark elf)"],
  },
  {
    id: "demon",
    name: "Demon",
    category: "Infernal / Supernatural",
    description: "Infernal beings from Hell and related dark dimensions. Demons in MFML range from minor imps to powerful lords. Some are children of divine-infernal unions.",
    subtypes: [
      { name: "Demon Lord", description: "Rulers of Hell's domains. Lucifer is the King of Hell." },
      { name: "Death Reaper (Deity)", description: "Gambit is a Death Reaper deity who deals in souls, manipulates timelines, and speaks in riddles. He won Evelyn's soul through a coin toss bet." },
      { name: "Half-Demon", description: "Asrius (son of Lachesis and Lucifer) is half-divine, half-demonic." },
    ],
    abilities: [
      "Soul manipulation and collection",
      "Timeline manipulation (Gambit)",
      "Telepathic communication (Gambit lives in Evelyn's mind)",
      "Shadow form transformation (Hades)",
      "Infernal fire and dark magic",
      "Riddle-speech and cryptic prophecy (Gambit)",
    ],
    culturalNotes: "Hell has multiple levels or 'circles' — Evelyn accidentally exploded one. Second Heaven and Second Hell are afterlife planes where souls exist without bodies. Ford Steele has connections as a 'prince of Hell.' Gambit's riddle-speech is a signature trait — he never speaks plainly.",
    keyCharacters: ["Lucifer", "Gambit", "Asrius", "Ford (Hell connections)"],
  },
  {
    id: "shifter",
    name: "Shifter (Non-Wolf)",
    category: "Shifter / Supernatural",
    description: "Beings who can transform into animals other than wolves. Various species exist in MFML including tiger shifters, dragon shifters, and others.",
    subtypes: [
      { name: "Tiger Shifter", description: "Liz is a tiger shifter who bounces off walls with energy." },
      { name: "Dragon Shifter", description: "Dragons carry internal fire and possessive instincts. Some were cursed into their forms." },
    ],
    culturalNotes: "Shifters exist alongside werewolves but have different social structures. Some shifter types are rarer than others.",
    keyCharacters: ["Liz (tiger)"],
  },
  {
    id: "water_nymph",
    name: "Water Nymph",
    category: "Aquatic / Supernatural",
    description: "Aquatic magical beings distinct from sirens. In MFML, water nymphs can be captured and collared as prisoners, suggesting they are less politically powerful than sirens.",
    culturalNotes: "Water nymphs have been seen collared in captivity in underground kingdoms like Dimitri's domain.",
    keyCharacters: [],
  },
  {
    id: "arachne",
    name: "Arachne",
    category: "Cursed / Supernatural",
    description: "Spider-human hybrids spawned from the woman Athena cursed. Part spider, part human. Some have multiple eyes. Even ancient vampires and kings find them deeply unsettling.",
    culturalNotes: "The Arachnes exist because of Athena's ancient curse on a mortal woman. They are classified as a supernatural species that even King Emerson's court acknowledges as particularly creepy. Known as 'the black hole' in some supernatural circles.",
    keyCharacters: [],
  },
  {
    id: "ralidanti",
    name: "Ralidanti",
    category: "Shadow / Supernatural",
    description: "Shadow beings who serve the goddess Nyx. Ralidanti can use shadows like extensions of themselves, disappear into darkness, and phase through walls. They are organized into troops and are fundamentally dangerous — most are 'bona fide serial killers' with a twisted appreciation for fear and violence.",
    subtypes: [
      { name: "Shadow Soldier", description: "Standard Ralidanti troops who follow orders, patrol in shadows, and carry out missions including tracking, assassination, and abduction." },
      { name: "Ralidanti Leader", description: "Higher-ranking Ralidanti who command troops. Lance is a prominent Ralidanti leader." },
    ],
    abilities: [
      "Shadow manipulation — can use shadows like extensions of themselves",
      "Phasing through solid objects (walls, barriers)",
      "Shadow travel — can follow targets by moving through darkness",
      "Near-immortality — regenerate if thrown into light, but can be permanently killed by beheading",
      "Enhanced combat abilities",
      "Tracking by scent and shadow observation",
      "Invisibility in shadows — werewolves and humans cannot see Ralidanti houses during the day (they disappear)",
    ],
    weaknesses: [
      "Beheading — the only way to permanently kill a Ralidanti spirit",
      "Daylight limits visibility of their structures",
      "Their houses/bases disappear during the day to non-Ralidanti",
      "Nyx's control — they serve at her will and can be directed or restrained",
    ],
    socialStructure: "Military hierarchy under Nyx's command. They operate as troops with leaders and soldiers. Some families have connections to the Ralidanti — the Khan vampire family hired Ralidanti to protect Evelyn. Ralidanti can be hired as mercenaries despite their dangerous nature.",
    culturalNotes: "Every Ralidanti is a little twisted — 'they had to be since most of them were bona fide serial killers.' They appreciate fear and find 'the look of fear that flashes across a cute woman's face' enjoyable. If a Ralidanti kills a werewolf, Nyx can intervene to stop their soul from bridging to the heavens. The Ralidanti have been at war with Cruz's pack — 'Cruz has been evading my troops and killing them off for a long time.' Ralidanti can be reformed through genuine bonds — mating bonds affect even them. One described being mated as 'very Ralidanti of you' when someone forced a bond. RALIDANTI MARKING (from the played route, lance-mating): a Ralidanti marks a mate by INSTINCT, not by ritual or decision — Lance knew only that 'I don't know why I feel this but I just know... I marked you as mine', and could not explain the mechanism even as it was happening ('Gah, how do I explain this?'). The mark is PERMANENT — 'it's just going to last until you die' — and it physically changes the mate (whiskers appeared on Nicole, visible and not hideable). No Ralidanti in canon knows a procedure for undoing one. IMPORTANT: Ralidanti marking is NOT the werewolf mating bond and does not follow werewolf pack rules; do not describe rituals, renunciations or 'overwriting' as ways to break either bond — no such method is established anywhere in canon.",
    keyCharacters: ["Lance", "Nyx", "Saeren"],
  },
  {
    id: "demigod",
    name: "Demigod / Half-God",
    category: "Divine / Mixed",
    description: "Half-divine, half-mortal or half-supernatural beings. Demigods in MFML inherit partial divine powers from their godly parent but may not fully understand their heritage until later in life. Some demi-gods can be elevated to full godhood through training, death and resurrection, or divine intervention.",
    subtypes: [
      { name: "Greek Demigod", description: "Children of Greek gods. Evelyn (daughter of Ares), Darren (son of Hades/Persephone), Lex (son of Zeus/Hera), Milo (son of Dike/Hades)." },
      { name: "Half-Goddess", description: "Some characters discover their parent was secretly a god/goddess — learning this can unlock dormant powers. 'Your father is a god. And I was a goddess. A half goddess.'" },
      { name: "Elevated God/Goddess", description: "Mortals or supernaturals who have been turned into full gods/goddesses. Norse gods can turn people into gods but it's 'very dangerous giving people the power and immortality of a god.'" },
    ],
    abilities: [
      "Partial divine powers based on parentage — fire for Ares's children, darkness for Hades's children",
      "Can be elevated to full godhood (but may require dying first — 'Why is dying the only way to achieve it?')",
      "Full goddess transformation is immediate when a Norse god decides to turn someone",
      "100 years of training typically required for demigods to master their full potential",
      "Powers can be emotion-driven — Evelyn's fire powers respond to her emotional state",
      "Eyes may flash gold or change color when powers activate",
    ],
    culturalNotes: "Learning your divine parentage is often a shock — mothers may keep it secret for decades to protect their children. If a demigod's identity is discovered 'there's a good chance we'd both be dead.' The supernatural school on Olympus helps train demigods. Greek and Norse demigods exist but their divine systems have different rules. Becoming a goddess means living forever while mortal loved ones die around you — 'she'd live forever while everyone else died around her.'",
    keyCharacters: ["Evelyn", "Darren", "Lex", "Milo", "Cole (son of Poseidon)"],
  },
  {
    id: "pixie",
    name: "Pixie",
    category: "Magical / Fae-Adjacent",
    description: "Small magical beings related to fairies but distinct. Pixies in MFML have their own communities, eternal one traditions, and can be found in various remote locations including the arctic.",
    culturalNotes: "Being sent 'to the arctic with those damned pixies' is considered a punishment assignment. Pixies have their own version of the eternal one legend. Pixie communities are relatively self-contained.",
    keyCharacters: [],
  },
  {
    id: "dragon",
    name: "Dragon / Dragon Shifter",
    category: "Magical / Shifter",
    description: "Powerful magical beings who can take human and dragon form. In the supernatural school, the headmaster is a dragon — 'he strikes fear into the Gods as well.' Dragons carry internal fire and possessive instincts.",
    subtypes: [
      { name: "True Dragon", description: "Born dragons who can shift between human and dragon form. The headmaster of the supernatural academy is a dragon." },
      { name: "Cursed Dragon", description: "Beings cursed into dragon form who develop dragon instincts (possessiveness, internal fire)." },
    ],
    culturalNotes: "Dragons don't have a representative teacher at the supernatural school — they share the headmaster with vampires. The dragon curse creates dragon instincts in the cursed individual — possessiveness and internal fire. The headmaster dragon 'strikes fear into the Gods as well,' suggesting dragons are among the most powerful beings.",
    keyCharacters: [],
  },
  {
    id: "norn",
    name: "Norn (Fate Seer)",
    category: "Divine / Norse",
    description: "Norse equivalents of the Greek Fates. Norns can see the future but are forbidden from revealing it in ways that would alter destiny. They can warn of danger but cannot help prevent it — only help overcome it.",
    abilities: [
      "Future sight (restricted — cannot reveal specifics that would alter events)",
      "Can help ensure good afterlives for those they care about",
      "Warning ability — can alert of approaching tragedy without specifics",
    ],
    culturalNotes: "Penelope is a Norn who is best friends with Svira (Valkyrie). People constantly ask her to tell their futures, which she cannot do. Ragnarok is destined to happen regardless of anyone's knowledge of it — this is a core Norn teaching.",
    keyCharacters: ["Penelope"],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// REALMS & LOCATIONS
// ═══════════════════════════════════════════════════════════════════════════════
export const REALMS = [
  {
    id: "olympus",
    name: "Mount Olympus",
    type: "Divine Realm (Greek)",
    description: "Home of the Greek gods. A bustling divine city in the clouds with schools, marketplaces, baths, libraries, and royal temples. Not a quiet meditation space — it's a full civilization with signage, direction markers, and active social life.",
    features: [
      "Athena's supernatural academy — where gods, demigods, and supernaturals study together",
      "Apollo's production studios — where he films reality TV shows",
      "The Royal Temple",
      "Marketplaces and baths",
      "Libraries",
      "Cloud edges where gods sit and watch the mortal world below",
    ],
    connectedTo: ["The Underworld (via Hermes)", "Mortal Realm", "Valhalla (cross-pantheon interaction)"],
    keyEvents: ["Zeus's forced marriage arrangement with Evelyn", "Cruz's visit to Olympus after the pack attack", "Evelyn's first meeting with Ares", "Apollo's dating shows filmed here"],
  },
  {
    id: "underworld",
    name: "The Underworld",
    type: "Divine Realm (Greek)",
    description: "Hades' domain — a vast kingdom of the dead ruled from a throne made of skulls (Hitler is dead center under his butt). Features elaborate architecture, kitchens, parties, and concubine quarters. Not just a place of punishment — it's a functioning kingdom with its own culture.",
    features: [
      "Hades' skull throne room",
      "The judgment scale (where Hermes ferries souls)",
      "Concubine quarters and harem areas",
      "Party halls for elaborate themed events",
      "Kitchen where Kya taught Hades to make dough",
      "Portals back to the mortal realm",
      "Second Heaven and Second Hell — afterlife planes where souls exist without bodies",
    ],
    connectedTo: ["Mount Olympus", "Mortal Realm", "Second Heaven", "Second Hell"],
    keyEvents: ["Evelyn's time in Hades' harem", "Hades giving Evelyn a piece of his heart", "Lachesis serving as wife/companion", "Evelyn exploding a circle of Hell"],
  },
  {
    id: "valhalla",
    name: "Valhalla / Asgard",
    type: "Divine Realm (Norse)",
    description: "Home of the Norse gods. Features the World Tree Yggdrasil, perfect weather on Fridays (Frigg's doing), and a culture centered on honor, feasts, and martial prowess. Always has perfect weather on Frigg's Day because she 'won't allow anything else.'",
    features: [
      "Yggdrasil — the World Tree where Odin hung himself for the runes",
      "Odin's throne room with Gungnir",
      "Frigg's wedding grounds (Friday ceremonies only)",
      "Fólkvangr — where some warrior souls go instead of Valhalla proper",
      "Living quarters for the divine family",
    ],
    connectedTo: ["Mount Olympus (cross-pantheon)", "Mortal Realm", "Realm of the dead"],
    keyEvents: ["Kya's journey to marry Agmund and Agnar", "Loki's capture and trial", "Fenrir's containment", "Odin's judgment on Loki"],
  },
  {
    id: "cliffs_edge",
    name: "Cliff's Edge",
    type: "Mortal Realm (Werewolf Territory)",
    description: "The werewolf pack territory run by Alpha Cruz Steele. Features the pack house as a central communal hub, surrounding forests for patrols and hunting, and a small town nearby with a dance studio and local businesses. Originally where many characters' families came from.",
    features: [
      "The Pack House — communal living space for the pack",
      "37th floor apartment — where Ryder, Audrey, Alyce, and Allie live",
      "Patrol routes and border checkpoints",
      "Town with dance studio (owned by Skye's mother)",
      "Beautiful nighttime views — clear skies, moonlit fields",
    ],
    connectedTo: ["Nearby vampire territories", "Other werewolf packs nationally"],
    keyEvents: ["Cruz's rise to Alpha", "Bryce's integration into the pack", "Ford's challenge for Alpha position", "Evelyn's vampire attack on the pack house"],
  },
  {
    id: "immortuos",
    name: "The Immortuos",
    type: "Mortal Realm (Vampire Territory)",
    description: "The governing body and territory of powerful vampires. A formal, aristocratic society where ancient vampires manage supernatural politics. Features castle estates and formal courts.",
    features: [
      "Alistair's family castle",
      "Formal courts and political chambers",
      "Blood bank systems (Evelyn's ethical reform)",
      "Diplomatic channels with other supernatural factions",
    ],
    connectedTo: ["Other vampire territories", "Siren kingdoms (diplomatic)", "Werewolf territories (treaty)"],
    keyEvents: ["Sebastian's twenty-year reign as King", "Alistair and Kya's multi-lifetime reconnection", "Time-traveling Kya's arrival"],
  },
  {
    id: "atlantic_kingdom",
    name: "The Atlantic Siren Kingdom",
    type: "Aquatic Realm",
    description: "The underwater siren kingdom ruled by the Miyamoto royal family. A formal court system with diplomatic protocols, royal advisors, and ancient traditions around tailbonding and landbonding.",
    features: [
      "Royal palace",
      "Court with formal diplomatic protocols",
      "Trident of power (royal artifact)",
    ],
    connectedTo: ["Surface mortal realm", "Other siren territories", "Vampire courts (diplomatic)"],
    keyEvents: ["Kya's departure and twenty-year absence", "Sebastian becoming King", "The trident's magical pulse"],
  },
  {
    id: "hell",
    name: "Hell",
    type: "Infernal Realm",
    description: "Ruled by Lucifer as King. Features multiple circles/levels, each with different properties. Distinct from Hades' Underworld — Hell is its own domain with its own politics and power structure.",
    features: [
      "Multiple circles (Evelyn exploded one)",
      "Lucifer's domain and throne",
      "Infernal politics and power hierarchies",
      "Fordsplosion battleground (Lucifer vs Ford territory)",
    ],
    connectedTo: ["The Underworld (Hades' domain — separate but interacting)", "Mortal Realm", "Heaven"],
    keyEvents: ["Fordsplosion — Lucifer vs Ford over Lachesis", "Evelyn's stay in Hell", "Asrius's upbringing", "Lachesis's 'gilded cage' existence"],
  },
  {
    id: "celestial_planes",
    name: "The Celestial Planes",
    type: "Higher Realm",
    description: "The realm of High Fae and Elven royalty from which Princess Layla originates. A place of immense power and beauty, where beings can create anything from celestial energy.",
    connectedTo: ["Mortal Realm"],
    keyEvents: ["Layla's escape from arranged marriage", "Layla's arrival in the mortal realm"],
  },
  {
    id: "fairy_realm",
    name: "The Fairy Realm / Fae Courts",
    type: "Magical Realm",
    description: "The domain of fairies and fae, organized into seasonal courts (Spring, Summer, Fall, Winter). Features marketplaces, royal courts, and ancient forests. Dark fae markets exist where supernatural beings are sold.",
    features: [
      "Seasonal courts (Spring, Summer, Fall, Winter)",
      "Fairy markets (some legal, some black market)",
      "Royal palaces for fairy princes",
      "Dark fae underground — where corrupt magic is practiced",
    ],
    connectedTo: ["Mortal Realm", "Celestial Planes"],
    keyEvents: ["Claire/Sabrina's discovery of her fairy heritage", "The prophecy of the three fairy princes", "Aphrodite's love quest"],
  },
  {
    id: "mirror_dimension",
    name: "The Mirror Dimension / Mirror Plane",
    type: "Pocket Dimension",
    description: "A parallel dimension that traps beings. Brax accidentally sent Ford's mate into the mirror dimension, and extracting someone requires specific knowledge and power.",
    connectedTo: ["Mortal Realm"],
    keyEvents: ["Ford's mate being trapped", "Nicole/Evelyn planning to enter and retrieve her"],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAGIC SYSTEMS & BONDS
// ═══════════════════════════════════════════════════════════════════════════════
export const MAGIC_SYSTEMS = [
  {
    id: "mating_bond",
    name: "Mating Bond / Mating Mark",
    type: "Bond (Werewolf)",
    description: "A permanent bond created when a werewolf bites their mate during intimacy. Allows shared thoughts and emotions. Considered sacred and nearly unbreakable. The first mating is the most significant — 'you can never get that moment back.' Mating marks are visible to other werewolves and declare possession.",
    mechanics: [
      "Created through a bite during intimate moments",
      "Allows telepathic connection and shared emotions",
      "Visible mark on the body — declares possession to all wolves",
      "First mating bond is the most powerful",
      "Can be severed by extremely powerful magic (devastating consequences)",
      "Double marks (two different mates) are extremely unusual and concerning",
    ],
    culturalSignificance: "The mating bond is the most sacred institution in werewolf culture. Having someone's mating mark and being seen by the pack as bonded changes everything — living arrangements, pack hierarchy position, and political alliances. Being unmated as an Alpha creates political vulnerability.",
  },
  {
    id: "tailbond",
    name: "Tailbond",
    type: "Bond (Siren)",
    description: "A deep, intimate bond between sirens (or a siren and another being). More intimate than a mating bond — it connects the very essence of two beings. Forcing a tailbond is considered treason in siren culture.",
    mechanics: [
      "Requires physical contact with tails/siren forms",
      "Creates a deep spiritual and emotional connection",
      "Considered more intimate than most other bond types",
      "Cannot be forced without committing treason",
      "Shares thoughts and sensory experiences at a deeper level than mating bonds",
    ],
    culturalSignificance: "Tailbonding is the siren equivalent of the deepest possible commitment. It's beyond marriage — it's a merging of essences. Sebastian desperately wanted to tailbond with Kya but knew forcing it would be treason.",
  },
  {
    id: "landbond",
    name: "Landbond",
    type: "Bond (Siren-Land Dweller)",
    description: "A bond connecting a siren to a land-dwelling being that transcends lifetimes. Kya and Sebastian/Alistair's landbond spans multiple reincarnations — even death cannot permanently sever it.",
    mechanics: [
      "Connects across species boundaries (siren to non-siren)",
      "Survives death and reincarnation",
      "Creates a pull that draws bonded individuals together across lifetimes",
      "Shares consciousness at deep levels (Aaron can listen to Haley's mind and heartbeat)",
    ],
    culturalSignificance: "A landbond is destiny made manifest. It's considered proof that the universe intends two beings to be together regardless of species, time, or death.",
  },
  {
    id: "blood_bond",
    name: "Blood Bond",
    type: "Bond (Vampire)",
    description: "A vampire marriage contract created through exchange of blood. Binding and difficult to break. Kira Nightingale arranged her own blood bond with Ezra in a single day as a strategic move to gain vampire protection for her family.",
    mechanics: [
      "Created through deliberate blood exchange between vampire and partner",
      "Functions as an unbreakable marriage contract in vampire society",
      "Grants the non-vampire partner protection and status within the vampire family",
      "Can be arranged strategically (as Kira did) or romantically",
      "Carries vampire political implications — marrying into a family means joining their political structure",
    ],
  },
  {
    id: "soulmate_bond",
    name: "Soulmate / Eternal One",
    type: "Bond (Universal)",
    description: "A destined connection between two beings. In MFML, soulmates manifest differently per species: vampires have soulmate marks (visible through bites), werewolves have fated mates, sirens have landbonds, and fairies have eternal ones. Aphrodite assigns soulmates, and Zeus can break ANY soulmate bond as King of the Gods.",
    mechanics: [
      "Soulmate marks appear through vampire bites — a specific mark pattern indicates a true soulmate",
      "Werewolf fated mates feel an immediate, overwhelming pull",
      "Fairy eternal ones recognize each other across reincarnations — memories from past lives surface",
      "Aphrodite can assign and manipulate soulmate connections",
      "Zeus can break any bond — his authority as King supersedes all other divine laws",
      "Dark fae can FAKE eternal one connections with corrupted magic",
      "Soulmate bonds often persist across reincarnations",
    ],
    culturalSignificance: "Finding your soulmate is considered the most significant event in any supernatural being's existence. The type of bond varies by species but the cosmic significance is universal. Cruz describes it: 'Love is like getting to read a really good book and understanding each page and wanting to cherish it.'",
  },
  {
    id: "reincarnation",
    name: "Reincarnation",
    type: "Cosmic System",
    description: "Beings in MFML can be reborn across lifetimes, sometimes retaining memories or personality echoes. Reincarnation works for everyone — good and evil — and doesn't always produce the same species. A fairy prince could reincarnate as a different type of fairy.",
    mechanics: [
      "Works for ALL beings — not just the virtuous",
      "Species can change between lives (fairy → different fairy type)",
      "Memories may or may not carry over — often surface as feelings of familiarity",
      "Soulmate/eternal one bonds persist across incarnations",
      "Atropos reincarnated as Haley (werewolf timeline)",
      "Damien says: 'Reincarnation doesn't only work for good people. Everyone is reincarnated.'",
    ],
  },
  {
    id: "prophecy",
    name: "Prophecy",
    type: "Cosmic System",
    description: "Prophecies drive many major storylines in MFML. The Fates, Norns, and certain gods (Apollo) can see the future. Prophecies often involve choices — fulfilling them wrong can have catastrophic consequences. Aria's choice 'could destroy everything.'",
    mechanics: [
      "Prophecies are real and reliable but interpretation matters",
      "The Fates measure, cut, and spin the threads of life",
      "Apollo sees the future but treats it as entertainment spoilers",
      "Norns see the future but are forbidden from revealing specifics",
      "Choices within prophecies can alter outcomes dramatically",
      "Bad events will happen regardless of foreknowledge — trying to prevent them often makes them worse (Norse teaching)",
    ],
  },
  {
    id: "heat_cycle",
    name: "Heat Cycle / Mating Season",
    type: "Biological System (Werewolf)",
    description: "True mates experience heat cycles — periods of intense physical and emotional need driven by the goddess Diana's design. 'The goddess created wolves to be very potent and typically true mates grow pregnant after the first mating session.'",
    mechanics: [
      "Affects all true mates — the pull is irresistible",
      "The werewolf summit during heat season is dangerous for unmated females — 'the last place I'd take a woman is into a room full of men in heat'",
      "Heat cycles serve a biological purpose — ensuring continuation of werewolf lines",
      "Can be overwhelming for newly turned werewolves who don't understand what's happening",
      "If a werewolf doesn't mate during the cycle, it can cause physical pain or even death — 'if you die because you didn't want to sleep with your soulmate, then I consider it a meaningless death'",
    ],
  },
  {
    id: "god_transformation",
    name: "God/Goddess Transformation",
    type: "Divine System",
    description: "Mortals and supernaturals can be elevated to full godhood through various means. Norse gods can turn people immediately by choice. Greek demigods typically require death and resurrection or 100 years of training. The process is irreversible and fundamentally changes the person.",
    mechanics: [
      "Norse gods can turn someone into a god/goddess immediately — but it's 'very dangerous giving people the power and immortality of a god'",
      "Only those chosen will survive Ragnarok and rebuild the world — 'if we pick the wrong people, it may end up destroying everything'",
      "Greek demigods may need to die to achieve full godhood — 'Why is dying the only way?'",
      "100 years of training is standard for mastering divine powers",
      "Newly made gods/goddesses may not understand their powers — 'I don't know anything about this goddess thing'",
      "Becoming a goddess means living forever — you watch mortal friends age and die",
      "Eye color changes (flashing gold) indicate divine power activation",
      "Gods from different pantheons (Greek, Norse) are viewed with prejudice by each other's followers at the supernatural school",
      "Ambrosia is fed to mortals by Zeus to gain their favor — people don't know they're eating divine food",
    ],
  },
  {
    id: "wolf_goddess_system",
    name: "Diana / Lunar Goddess System",
    type: "Divine System (Werewolf)",
    description: "The goddess Diana (also known as the Lunar Goddess) oversees werewolf destiny. She can communicate with Alpha wolves, influence mate pairings, and use mortal host bodies. She plays a subtle but powerful role in werewolf affairs.",
    mechanics: [
      "Diana can communicate with Alphas in wolf form",
      "She subtly influences mate pairings — 'if the Lunar goddess would have cock blocked them at the last second' she would have",
      "She can use mortal host bodies — 'Diana was pleasantly surprised over the fact that Milo was already familiar with her host body'",
      "She prefers immortal hosts so she doesn't need a new body often",
      "She feels insulted when not properly respected by other divine factions — 'your mother has the audacity to invite my followers and not a single Roman deity to attend the school?'",
      "Diana/Roman deities are distinct from Greek/Norse pantheons but interact with them",
    ],
  },
  {
    id: "nyx_shadow_system",
    name: "Nyx / Shadow Goddess System",
    type: "Divine System (Dark)",
    description: "Nyx is the goddess of darkness/night who commands the Ralidanti shadow warriors. She is the mother of the Fates (with Zeus). She can intervene in the afterlife to prevent souls from reaching heaven.",
    mechanics: [
      "Commands the Ralidanti shadow forces",
      "Mother of the three Fates (Lachesis, Atropos, Clothos) with Zeus",
      "Can see through a 'looking glass' to communicate with mortals — but cannot give direct answers without consequences",
      "If a Ralidanti kills a werewolf, Nyx can stop their soul from bridging to the heavens",
      "Has her own agenda separate from both Greek and Norse pantheons",
      "Her forces can track, abduct, and kill — organized as military troops",
    ],
  },
  {
    id: "siren_compulsion",
    name: "Siren Voice / Compulsion",
    type: "Innate Ability (Siren)",
    description: "Sirens can pour attraction and influence into their voice to compel others. The ability ranges from subtle influence to full mind control depending on the siren's power and training.",
    mechanics: [
      "Siren voice can compel obedience, calm emotions, or create desire",
      "Kya used siren voice to compel someone to 'stop fighting or hurting them' and to make feelings 'go away'",
      "Siren compulsion doesn't work on other sirens — 'your compulsion won't work on me'",
      "Siren-vampires have enhanced compulsion that works on almost everyone including vampires",
      "Memory manipulation via siren voice is possible — Kya compelled someone to 'forget all of your time as a werewolf'",
      "Sirens are also naturally good at detecting lies — an innate sense of truth",
      "Training improves siren voice ability — some need practice",
    ],
  },
  {
    id: "divine_marriage",
    name: "Deity Marriage",
    type: "Divine Institution",
    description: "Marriages officiated by Aphrodite (Greek) or blessed by Frigg (Norse) carry divine weight and CANNOT be dissolved. Aphrodite-officiated marriages are literally undivorceable. Frigg-blessed weddings must occur on Friday with perfect weather she ensures.",
    mechanics: [
      "Aphrodite officiates Greek divine marriages — these cannot be broken by any means",
      "Frigg blesses Norse weddings — ONLY on Fridays, with weather she controls",
      "Zeus can override most bonds but even divine marriages are complex",
      "Norse weddings are large outdoor gatherings by tradition",
      "If Frigg denies blessing, the marriage cannot proceed in Norse tradition",
      "Frigg and Freya oppose each other — if one supports a marriage, the other may resist",
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// CURSES & AFFLICTIONS
// ═══════════════════════════════════════════════════════════════════════════════
export const CURSES = [
  {
    id: "apollos_bloodline_curse",
    name: "Apollo's Bloodline Curse (Dimitri's Line)",
    type: "Divine Curse",
    castBy: "Apollo",
    affectedCharacters: ["Dimitri's bloodline", "Chiara"],
    description: "Apollo cursed Dimitri's entire vampiric bloodline. The curse specifically strips vampiric traits from WOMEN at puberty. This means female dhampirs like Chiara (Dimitri and Evelyn's daughter) will lose their vampire abilities when they reach puberty.",
    mechanics: "Affects the female line only. Vampiric traits (fangs, strength, blood abilities) fade at puberty. The curse is permanent and affects all descendants.",
    storyImpact: "This curse is central to Chiara's character — she is a baby now but will lose her vampiric nature as she grows. It creates a ticking clock for her family.",
  },
  {
    id: "memory_curse",
    name: "The Forgetting Curse",
    type: "Witch Curse",
    castBy: "Unknown powerful witch/dark practitioner",
    description: "A curse that makes everyone forget a specific person exists. One character had their hair permanently changed color as a 'visual reminder' of the curse. At age five, their entire family forgot them, and the curse was strong enough that almost everyone else did too.",
    mechanics: "Erases the cursed person from the memories of everyone they know. Hair color change as permanent visual marker. The cursed person remembers everything but no one remembers them.",
    storyImpact: "This character grew up in a shack provided by a pack who barely interacted with them, afraid to form attachments because 'if I made a real attachment to them, they'd forget about me too.'",
  },
  {
    id: "dragon_curse",
    name: "Dragon Transformation Curse",
    type: "Curse (Unclear Origin)",
    description: "A curse that transforms someone into a dragon or binds them to dragon nature. The cursed individual believes the caster wants them to 'fall in love and then take you away — to get revenge for the loss of her child.'",
    mechanics: "Creates dragon instincts (possessiveness, internal fire) in the cursed individual. May be designed to cause emotional devastation rather than physical harm.",
  },
  {
    id: "lokis_punishment",
    name: "Loki's Mouth-Sewing",
    type: "Divine Punishment (Norse)",
    castBy: "Odin",
    affectedCharacters: ["Loki"],
    description: "Odin had Loki's mouth sewn shut as punishment for outsmarting the dwarves. This is both a historical scar and an ongoing precaution — Loki must be transported with tape over his mouth because his words alone are considered a weapon-level threat.",
    storyImpact: "Defines Loki's character — even chained, even silenced, he's the most dangerous person in any room.",
  },
  {
    id: "evelyns_soul_deal",
    name: "Evelyn's Soul (Gambit's Coin Toss)",
    type: "Soul Contract",
    castBy: "Gambit (Death Reaper)",
    affectedCharacters: ["Evelyn"],
    description: "Gambit won Evelyn's soul through a coin toss bet after her death. He now lives in her mind telepathically, speaks in riddles, and manipulates timelines around her. He sent her back in time and his sister time-traveled to warn others about him.",
    mechanics: "Gambit owns Evelyn's soul and can communicate telepathically from within her mind. He manipulates timelines and speaks only in riddles and rhymes.",
  },
  {
    id: "lucifer_soul_ownership",
    name: "Lucifer's Ownership of Lachesis's Soul",
    type: "Soul Contract",
    affectedCharacters: ["Lachesis"],
    description: "Lucifer owns Lachesis's soul, binding her to his domain. She describes Hell as 'a gilded cage' she stays in partly for their son Asrius. Despite this, their relationship is genuine — 'He'd probably kill whoever my other lover was.'",
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// IMPORTANT ITEMS & ARTIFACTS
// ═══════════════════════════════════════════════════════════════════════════════
export const ARTIFACTS = [
  {
    id: "celestial_blade",
    name: "Celestial Blade",
    type: "Divine Weapon",
    description: "A weapon capable of killing gods — even the King of the Gods. Cruz used a celestial blade to kill Zeus by driving it through his heart. One of the most significant weapons in the MFML universe.",
    currentHolder: "Unknown (Cruz used it)",
    keyEvent: "Cruz killed Zeus with this blade — one of the most universe-altering events in MFML history.",
  },
  {
    id: "gungnir",
    name: "Gungnir",
    type: "Divine Weapon (Norse)",
    description: "Odin's legendary spear. He slams it on the ground to punctuate divine decrees. A symbol of absolute Norse authority.",
    currentHolder: "Odin",
  },
  {
    id: "chains_of_ivaldi",
    name: "Chains of Ivaldi",
    type: "Divine Restraint (Norse)",
    description: "One of the only restraints capable of holding Loki. Svira used them to capture the Trickster God. Created by the dwarven smiths of Ivaldi.",
    keyEvent: "Svira captured Loki using these chains, leading to his imprisonment and trial before Odin.",
  },
  {
    id: "siren_trident",
    name: "The Siren Trident",
    type: "Royal Artifact (Siren)",
    description: "Symbol and source of royal siren power. Can emit large pulses of magic that are detectable across realms. Belongs to the Atlantic Siren royal family.",
    currentHolder: "Atlantic Siren Kingdom",
    keyEvent: "Alistair sensed a 'dangerously large pulse of magic' from the trident and called Kya to investigate.",
  },
  {
    id: "ambrosia",
    name: "Ambrosia",
    type: "Divine Substance",
    description: "Food of the gods with multiple uses in MFML: Zeus feeds it to women he fancies, Hermes uses it to dull Peter's senses so he doesn't discover his divine heritage, and it can sustain immortality. A versatile and potentially dangerous substance.",
    effects: [
      "Sustains divine immortality",
      "Can dull mortal/supernatural senses (Hermes → Peter)",
      "Used as a seduction tool by Zeus",
      "Can temporarily grant divine-adjacent effects to mortals",
    ],
  },
  {
    id: "drachma",
    name: "Drachma",
    type: "Divine Currency",
    description: "The currency of the Greek divine world. Hermes charges drachmas for every Insta-Hermes service. Used in divine transactions, bets, and payments across Olympus and the Underworld.",
    culturalNote: "Hermes charges extra for overtime, rush delivery, and emotional labor. All in drachmas.",
  },
  {
    id: "fae_collar",
    name: "Fae Suppression Collar",
    type: "Restraint Device",
    description: "Special collars containing fae-specific poison that weakens fairies and suppresses their powers without killing them. Used in black market fae auctions to keep captured fae compliant.",
    culturalNote: "Peter bought Kieran at an auction where she was collared. The collar's poison makes fae 'mostly useless' but doesn't kill them.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// ALTERNATE UNIVERSES & ROUTES
// ═══════════════════════════════════════════════════════════════════════════════
export const ALTERNATE_ROUTES = [
  {
    id: "fordsplosion",
    name: "The Fordsplosion",
    type: "Major Route / Love Triangle",
    characters: ["Ford Steele", "Lucifer", "Lachesis"],
    description: "One of the most intense storylines in MFML history. A love triangle between Ford Steele (werewolf/Hell prince), Lucifer (King of Hell), and Lachesis (Fate goddess). Involves wing-clipping, psychological conditioning, and two impossibly powerful men tearing reality apart over one woman.",
    keyElements: ["Wing-clipping as power move", "Conditioning and manipulation", "Lucifer vs Ford territory war in Hell", "Lachesis trapped between two consuming loves"],
  },
  {
    id: "yandere_cruz",
    name: "Yandere Cruz",
    type: "Dark AU",
    characters: ["Cruz"],
    description: "An alternate universe where Cruz becomes manipulative and possessive, using comfort and logic to coerce rather than force. His charm becomes a weapon. This is explicitly an AU — not his default personality.",
  },
  {
    id: "evelyn_routes",
    name: "Evelyn's Multiple Routes",
    type: "Route System",
    characters: ["Evelyn"],
    description: "Evelyn has the most alternate routes of any character, reflecting her central role in the MFML universe.",
    routes: [
      "Rowan Route (alpha mate)",
      "Lucius Route (affair)",
      "Dimitri Route (underground kingdom)",
      "Ace Route (toxic relationship)",
      "Hiro Route (school/Olympus)",
      "Raz Route (celestial)",
      "Ethan Route (lost love — former fiancé)",
      "Jumin Route (healthy relationship)",
    ],
  },
  {
    id: "apollos_shows",
    name: "Apollo's Reality Shows",
    type: "Recurring Setting",
    characters: ["Apollo", "Various contestants"],
    description: "Apollo runs multiple reality TV shows from Olympus. 'Fate in Love' is a bachelorette-style show. 'Lightborn' is a horrifying survival competition where creatures hunt vampires. He accepts script submissions and arranges elaborate competitions between suitors.",
    shows: [
      { name: "Fate in Love", format: "Bachelorette-style dating show with goddess contestants and supernatural suitors" },
      { name: "Lightborn", format: "Survival competition where creatures hunt vampires for sport" },
      { name: "Various Competition Shows", format: "Talent and combat competitions with real supernatural consequences" },
    ],
  },
  {
    id: "supernatural_school",
    name: "Supernatural Academy (Olympus)",
    type: "Setting / Route",
    characters: ["Athena", "Cupid", "Lex", "Milo", "Vaughn", "Various students"],
    description: "Athena runs a school on Olympus where supernatural beings study together. Features freshmen transition mentors (like Vaughn), mixed species classrooms (werewolves, vampires, sirens, gods), and all the drama of a supernatural boarding school.",
  },
  {
    id: "time_travel",
    name: "Time Travel Routes",
    type: "Mechanic",
    description: "Multiple characters have traveled through time: Gambit sent Evelyn back in time, Gambit's sister time-traveled to warn about him, Kya appears in the Immortuos timeline with knowledge she shouldn't have (from the future). Time travel is real but risky — 'only the gods' were supposed to be able to do it.",
  },
  {
    id: "fae_celestial_realm",
    name: "Fae Celestial Realm Routes",
    type: "Setting / Route Collection",
    characters: ["Draco", "Calyx", "Merlin", "Various fairy princes and princesses"],
    description: "Stories set in the celestial fae realm feature fairy castles, fae festivities, competitions for marriage, and the complex dynamics of seasonal courts. Multiple princes often compete for the same person. Merlin forces characters to do mundane tasks like making pudding.",
    keyElements: [
      "Fae castles with elaborate balls and competitions",
      "Marriage competitions where suitors compete over 5+ days",
      "Seasonal court politics (Spring, Summer, Fall, Winter)",
      "Water fairy imprinting — a sacred bond that cannot be broken without risking war",
      "Eternal one discovery — touching and proximity triggers past life memories",
      "The distinction between fae, high fae, pixies, and elves",
      "Dark fae underground markets where supernaturals are sold",
    ],
  },
  {
    id: "norse_school_route",
    name: "Supernatural School / Norse Routes",
    type: "Setting / AU Collection",
    characters: ["Evelyn", "Kya", "Darren", "Ari", "Hiro", "Various students"],
    description: "Stories set at Athena's supernatural academy where demigods and supernatural beings attend school together. Features Greek-Norse prejudice, sorority drama, faculty intrigue, and characters discovering their divine heritage. Kya became a Norse goddess after falling for Professor Ari (Norse god of love/sex), which fractured her friendship with Greek-aligned Evelyn.",
    keyElements: [
      "Cross-pantheon romance causing friendship breakdowns",
      "Discovering divine parentage at school",
      "Greek vs Norse student cliques and prejudice",
      "Sorority social dynamics",
      "Professor-student romances (Ari and Kya)",
      "Dragon headmaster who terrifies even gods",
      "Darren in the Underworld route — falling in love with Evelyn and offering her to live with him",
    ],
  },
  {
    id: "vampire_kingdom_routes",
    name: "Vampire Kingdom Political Routes",
    type: "Setting / Route Collection",
    characters: ["Kya", "Val", "Sebastian", "Alistair", "Evelyn", "Various vampire royals"],
    description: "Stories centered on vampire political intrigue — kings vying for power, blood bonds as strategic marriages, underground criminal enterprises, and the complex elder system. These routes often involve Kya navigating vampire society as a siren-turned-vampire, with Val as her mentor and Sebastian as her complicated ally.",
    keyElements: [
      "Val as book keeper/elder who controls historical narrative",
      "Multiple soulmates revealed simultaneously (Kya with Sam, James, Raz, and Sebastian)",
      "Strategic marriages and blood bonds for political advantage",
      "Vampire private TV networks broadcasting political news",
      "Underground vampire crime — illegal slavery, smuggling, assassinations",
      "The Commonwealth — overseas vampire territory with harsher rules",
      "Elder politics — nearly impossible to become elder under 1,000 years old",
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAJOR LORE EVENTS (TIMELINE)
// ═══════════════════════════════════════════════════════════════════════════════
export const LORE_EVENTS = [
  {
    id: "zeus_death",
    name: "The Death of Zeus",
    era: "Modern Era",
    description: "Cruz killed Zeus by driving a celestial blade through his heart. This is one of the most universe-altering events in MFML history — the King of the Gods, who could break any bond and command any divine being, was killed by a mortal werewolf. Hera was involved in the lead-up, considering killing Zeus herself because he had become tyrannical.",
    consequences: ["Power vacuum in Olympian hierarchy", "Freedom from Zeus's forced marriages and bond-breaking", "Shift in divine politics"],
  },
  {
    id: "evelyn_disappearance",
    name: "Evelyn's 25-Year Disappearance",
    era: "Recent History",
    description: "Evelyn disappeared for 25 years, living as 'Nicole' in New Jersey with no memories of her supernatural life. Her disappearance devastated everyone who knew her — Alistair grieved alongside Ezra, Kya lost her best friend, and the vampire political landscape shifted dramatically.",
    consequences: ["Sebastian ruled as Siren King in Kya's absence (related)", "Vampire power structures reorganized", "Bryce believed she was dead and spiraled", "When she returned, she had to rebuild all relationships from scratch"],
  },
  {
    id: "evelyn_becomes_queen",
    name: "Evelyn Kills Geo / Becomes Vampire Queen",
    era: "Recent History",
    description: "After believing Bryce was dead, Evelyn became ruthless and killed Geo, the vampire king, to seize the vampire kingdom. She ruled as queen until her connection with Cruz drew her back to the werewolf world.",
    consequences: ["Evelyn became the most powerful vampire ruler", "Vampire politics completely reshaped", "Her ruthlessness became legendary — a stark contrast to her soft true self"],
  },
  {
    id: "war_against_zeus",
    name: "Evelyn's War Against Zeus",
    era: "Modern Era",
    description: "Evelyn planned and executed a divine war against Zeus. Her strategy involved turning multiple gods to her side: convincing Apollo that supporting her would bring ratings, using Poseidon's connection to Demeter as leverage, and exploiting Hera's resentment. She signed contracts, gathered allies, and challenged the King of the Gods directly.",
    keyAllies: ["Apollo (signed a contract for ratings)", "Poseidon and Demeter (leveraged their relationship)", "Hera (wanted freedom from Zeus)", "Ares (her father, God of War)"],
  },
  {
    id: "sabrina_creation",
    name: "Aphrodite Creates Sabrina",
    era: "Ancient",
    description: "Aphrodite literally CREATED Sabrina as a being — crafted from love magic with help from Demeter and the Fates — as a gift for her son Cupid. Sabrina was then taken at birth by a prophecier for protection and raised as human 'Claire.'",
    consequences: ["Claire/Sabrina's entire identity crisis", "The prophecy of the three fairy princes", "Cupid's eventual relationship with his mother's gift"],
  },
  {
    id: "lokis_capture",
    name: "Loki's Capture and Trial",
    era: "Norse Timeline",
    description: "Svira captured Loki using the chains of Ivaldi after he attempted to trigger Ragnarok. Kya helped in the capture. Loki was brought before Odin for judgment. His sons Agmund and Agnar had conflicting feelings — Agnar didn't want to kill him, Agmund would follow Odin's wishes despite not wanting to hurt his brother.",
    consequences: ["Loki imprisoned with tape over his mouth", "Kya proved herself to the Norse gods", "Odin's decree on Loki's fate", "Frigg demanded death for Loki murdering her son Balder"],
  },
  {
    id: "gabriel_beheading",
    name: "Gabriel Beheaded by God",
    era: "Ancient / Celestial",
    description: "The archangel Gabriel helped Lachesis escape from heaven. As punishment, God beheaded him. Gabriel had the ability to see the future and was a teasing, irreverent figure who operated in grey areas between heaven and hell.",
    consequences: ["Loss of a major celestial player", "Lachesis's freedom from heaven", "Demonstration of God's absolute authority"],
  },
  {
    id: "fenrir_capture",
    name: "The Capture of Fenrir",
    era: "Norse Timeline",
    description: "Fenrir (Loki's wolf son) was chained up to prevent him from triggering Ragnarok. The chains barely hold him — his strength is enormous. A sword was stabbed through his muzzle as part of the restraint. Kya helped chain him during one of the most intense encounters in the Norse storyline.",
    consequences: ["Ragnarok delayed but not prevented", "Kya proved herself to the Norse divine family", "Agmund and Agnar demonstrated their loyalty despite conflicted feelings about their father Loki"],
  },
  {
    id: "evelyn_kills_geo",
    name: "Evelyn Kills Geo / Seizes Vampire Kingdom",
    era: "Recent History",
    description: "After believing Bryce was dead, Evelyn became ruthless and killed Geo (a vampire king) to seize his kingdom. She ruled as queen with an iron fist until her connection with Cruz drew her back to the werewolf world. This established her reputation as someone who could be both gentle and absolutely lethal.",
    consequences: ["Vampire political landscape completely reshaped", "Evelyn's ruthlessness became legendary", "Established her pattern of having multiple 'lives' across different supernatural societies"],
  },
  {
    id: "kya_coronation",
    name: "Kya's Coronation to Siren Throne",
    era: "Recent History",
    description: "Kya was coronated to her own siren throne after her uncle was compelled to stop fighting and hurting them. Her grandmother (who originally wanted to pass the crown to Kya's deceased mother) was proud. Kya splits her time — half on the surface, half managing the kingdom, with Sebastian left in charge during surface time.",
    consequences: ["Atlantic Kingdom stabilized under new leadership", "Kya navigates dual life between surface and ocean", "Sebastian serves as regent during Kya's surface time", "Sierra is in line to eventually take the crown"],
  },
  {
    id: "supernatural_school_founding",
    name: "Founding of Athena's Supernatural Academy",
    era: "Ongoing",
    description: "Athena established a school on Olympus where supernatural beings study together. Each major race has a representative teacher. The headmaster is a dragon feared even by gods. Cross-species friendships and rivalries define the experience, with Greek-Norse prejudice being a major social tension.",
    consequences: ["Mixed-species education creates cross-faction alliances", "Greek-Norse prejudice plays out among students", "Demigods discover their heritage through school experiences", "Multiple romantic storylines emerge from school settings"],
  },
  {
    id: "evelyn_hades_heart",
    name: "Hades Gives Evelyn a Piece of His Heart",
    era: "Underworld Timeline",
    description: "During Evelyn's time in the Underworld, Hades gave her a literal piece of his heart. This created a deep divine connection between them. As a result, Evelyn could feel what Hades felt and vice versa — 'since she was given Hades's heart, she felt touched by his words.'",
    consequences: ["Permanent divine connection between Evelyn and Hades", "Evelyn gained Underworld-adjacent abilities", "Created complex emotional ties that persist across Evelyn's many routes"],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// CULTURAL SYSTEMS & RULES
// ═══════════════════════════════════════════════════════════════════════════════
export const CULTURAL_RULES = [
  {
    id: "werewolf_vampire_treaty",
    name: "Werewolf-Vampire Treaty",
    description: "An agreement between werewolves and vampires to 'agree to disagree and stay the hell out of each other's way.' This treaty prevents open warfare but doesn't create friendship. Cruz taking in Bryce (a vampire) violated the spirit of this treaty and was deeply controversial.",
  },
  {
    id: "national_werewolf_meet",
    name: "National Werewolf Annual Meet",
    description: "All werewolf packs gather annually to discuss supernatural creature activity and share updates. Only rogues are excluded (though some exceptions exist). This is how packs stay informed about vampire movements, fairy politics, and divine interventions.",
  },
  {
    id: "siren_tailbond_treason",
    name: "Tailbond Treason Law",
    description: "Forcing a tailbond on a siren without consent is treason in siren culture. The punishment is severe. This prevented Sebastian from simply bonding with Kya against her will despite his desperation.",
  },
  {
    id: "friday_wedding_law",
    name: "Norse Friday Wedding Law",
    description: "Norse weddings can ONLY occur on Friday (Frigg's Day) with Frigg's explicit blessing. She ensures perfect weather for the ceremony. If she denies the blessing, the wedding cannot proceed. This is absolute and non-negotiable in Norse divine law.",
  },
  {
    id: "blood_donation_system",
    name: "Ethical Blood Systems (Evelyn's Reform)",
    description: "Evelyn established ethical blood donation systems where supernatural creatures willingly donate blood and human blood comes from blood banks. This was a major reform — previously, vampires drank 'straight from the tap.' Evelyn worked hard to ensure 'nobody died to get blood in our area.'",
  },
  {
    id: "insta_hermes",
    name: "Insta-Hermes Customer Service System",
    description: "Hermes modernized his messenger god role into a divine customer service app with video calls, drachma payments, mandatory star ratings, and overtime charges. His HQ is in New York. He screams at employees off-screen during customer calls. Giving him fewer than five stars results in passive-aggressive guilt-tripping.",
  },
  {
    id: "supernatural_school_rules",
    name: "Supernatural Academy Rules & Culture",
    description: "Athena's school on Olympus is a mixed-species educational institution below Olympus's cloud level. Each supernatural race has a representative teacher. The headmaster is a dragon who 'strikes fear into the Gods as well.' Students are organized by species but interact freely. The school uses a coded 'major' system to hide supernatural identities.",
    details: [
      "Each major supernatural race has a representative teacher (werewolf, siren, fairy, Greek god, Norse god, etc.)",
      "Vampires and dragons don't have separate representatives — they share the headmaster",
      "Norse representative is a head teacher, just below the headmaster as assistant",
      "'Majors' are code names for supernatural species — switching majors means switching species allegiance, which is extremely difficult",
      "If a major 'likes you' (e.g., accidentally mating with a werewolf), you can be 'adopted' into that species group",
      "Supernatural-only classes exist — listed as 'full' if a human tries to register",
      "The school has dorms, common areas, and sports facilities",
      "Students form cross-species friendships and rivalries — Greek and Norse students often clash",
      "Freshmen transition mentors help new students (Vaughn is one)",
      "Sorority-style social groups exist",
      "Prejudice between pantheons is real — Greeks and Norse often look down on each other",
      "Elite parties serve as supernatural social scenes — getting in requires connections",
      "Species like dwarves, elves, and various other supernaturals attend alongside the major species",
      "Turning a student (vampire turning, werewolf bite, etc.) can happen on campus — the headmaster may oversee or allow it if the family donates enough",
      "Memory wiping is used on students who discover too many secrets — the headmaster would 'wipe her memory and send her on her way'",
      "Wealthy supernatural families can get their children special treatment through donations — some can 'get away with murder on campus'",
      "The vampire-werewolf treaty led to the school's founding as neutral ground for all species",
    ],
  },
  {
    id: "vampire_werewolf_war",
    name: "The Vampire-Werewolf War of 1983",
    description: "A devastating war between vampires and werewolves that nearly wiped out both species. Started when a vampire girl who was a werewolf's mate was killed by an angry werewolf, sparking escalating conflicts.",
    details: [
      "Started in 1983 over the killing of a vampire mate",
      "Nearly wiped both supernatural species out entirely",
      "The Immortuos after the war was 'made up of half of its members and most of them are young'",
      "Ended with a formal treaty with stipulations including vampires feeding from blood bags or volunteer blood slaves",
      "The treaty established 'agree to disagree and stay the hell out of each other's way'",
      "In some AUs the war never happened and werewolves are much more powerful",
      "An Alpha (Rowan) fighting an Elder (James) through the night at the Immortuos council chambers was a trigger for potential new conflict",
    ],
  },
  {
    id: "ralidanti_operations",
    name: "Ralidanti Military Operations",
    description: "The Ralidanti operate as shadow military forces with specific rules of engagement, chain of command under Nyx, and interaction protocols with other supernatural species.",
    details: [
      "Ralidanti houses/bases are invisible during daytime to non-Ralidanti",
      "They can be hired as protection detail — the Khan vampire family hired them to protect Evelyn",
      "Recon missions involve solo operatives observing pack movements, relationships, and weaknesses",
      "Operatives are instructed to follow targets to ensure safety, not engage in combat (though some can't resist)",
      "If an operative's past connection to a target is discovered, they're pulled from the mission and may be sent to the 'arctic with those damned pixies' as punishment",
      "Ralidanti cannot be trusted — 'werewolves and humans couldn't see Ralidanti houses during the day'",
      "They can propose alliances — offering to help trap enemies rather than kill them",
    ],
  },
  {
    id: "divine_prejudice",
    name: "Cross-Pantheon Prejudice & Politics",
    description: "Greek and Norse gods coexist but there is significant prejudice between the pantheons. This extends to their followers, demigods, and the supernatural school.",
    details: [
      "Greek gods view Norse gods as 'a bunch of stupid assholes who only care about fighting with swords until the world ends'",
      "Norse gods view Greeks as manipulative and dramatic",
      "At the supernatural school, Greek and Norse students often avoid each other",
      "Cross-pantheon romance (like Kya becoming a Norse goddess while friends with Greek-aligned Evelyn) causes friendship breakdowns",
      "Roman deities (Diana) feel insulted when not included alongside Greek and Norse",
      "Hades specifically told other gods 'be warned not to touch any Norse whether they come down here or not'",
      "The Norse benediction 'Blessaður!' is used when departing dramatically",
      "Some characters navigate both pantheons — Kya married into Norse while having Greek-aligned friends",
    ],
  },
  {
    id: "brothel_safe_haven",
    name: "Supernatural Brothels & Safe Havens",
    description: "Certain establishments in the MFML universe serve as safe havens disguised as brothels or entertainment venues. These provide shelter for supernatural beings who need protection.",
    details: [
      "Workers are supernatural beings who choose to be there",
      "Deaths of workers are kept secret to maintain the illusion of safety — 'they didn't know that she was dead or else it would destroy the illusion of the brothel being a safe haven'",
      "The establishments have their own hierarchy and rules",
      "New arrivals can be offered shelter — 'different can be invigorating and freeing'",
      "These places serve as neutral ground between different supernatural factions",
    ],
  },
  {
    id: "vampire_immortality_perspective",
    name: "Vampire Perspective on Mortality",
    description: "Vampires have a unique and melancholic relationship with mortality that shapes their social bonds and isolation.",
    details: [
      "Anyone who can't live forever is 'mortal' to a vampire — other supernaturals who can die of old age are considered mortal",
      "Older vampires teach their children not to befriend non-vampires because other species will die and leave them alone",
      "This creates deep isolation — vampires who form bonds with non-vampires carry centuries of grief",
      "Some vampires (like Evelyn) wish they were born as another species — 'I wish I was born a werewolf. Life would have been so much easier.'",
      "Vampire book keepers record history in secret family tomes — only family members can see the contents",
      "The loneliness of immortality drives many vampire political and social structures",
    ],
  },
  {
    id: "werewolf_full_moon_containment",
    name: "Full Moon Containment & Wild Wolf Protocol",
    description: "Werewolves who haven't gained control over their wolf must be contained during the full moon. This is managed by a formal system within the pack.",
    details: [
      "Holding centers are comfortable rooms, not cages — designed to keep the wolf contained humanely",
      "Walls are laden with special materials that subdue the wolf, preventing thrashing and destruction",
      "Rangers are responsible for locking doors from the outside — only rangers or pack leaders can release wolves",
      "Once the full moon night passes, containment is no longer needed until the next month",
      "This is a monthly routine until the wolf gains enough control through training",
      "Inner wolves are wild by nature — no one ever gains total control, only gets close with training",
      "Pack warriors train specifically to handle dangerous situations — the head of the army trains warrior wolves",
    ],
  },
  {
    id: "fairy_magic_training",
    name: "Fairy Magic Education & Royal Powers",
    description: "Fairies learn a structured set of skills from an early age, and royal fae have unique abilities that set them apart.",
    details: [
      "All fairies are taught: how to put wings away, how to fly, what happens during bonding, and how to use general magic",
      "Royal persuasion magic is exclusive to the royal bloodline — other fae cannot teach it, only recognize it",
      "Royal fae can give other fae 'heavy suggestions' — not full compulsion but a strong pull that requires willpower to resist",
      "This persuasion does not work on other species — only affects fae",
      "Light fairies can sense and verify magic by walking into a room — extremely rare and powerful ability",
      "Spirit fairies can communicate with spirits and manipulate matter",
      "Dark fae practitioners have wings that turn dark from corruption",
      "Deities are always welcome to enter the fairy realm — it's considered an open invitation",
      "General fairy magic can be taught by any fairy, but species-specific powers must be discovered by the individual",
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// DIVINE GOVERNANCE — Heaven, Hell, and God's Rules
// ═══════════════════════════════════════════════════════════════════════════════
export const DIVINE_GOVERNANCE = [
  {
    id: "gods_heaven_rules",
    name: "God's Governance of Heaven",
    description: "God (the Abrahamic God in MFML) rules heaven with absolute authority, but is not truly all-knowing or all-powerful as he claims. His rule is maintained through fear, punishment, and control over the Fates.",
    rules: [
      "God is NOT all-knowing — characters can eavesdrop and keep secrets from him",
      "Angels are forbidden from having romantic relationships with mortals, witches, or humans until the afterlife",
      "The Fates (Lachesis, Atropos, Clothos) are daughters of Zeus and Nyx but serve in heaven under God's authority",
      "God chose Lachesis as his intended queen/mistress — she was expected to obey and marry him",
      "Breaking God's rules results in severe punishment: banishment, imprisonment, or the 'second death' (permanent soul destruction)",
      "Gabriel was beheaded for helping Lachesis escape heaven — a demonstration of God's cruelty",
      "God punishes criticism harshly — he doesn't reflect on mistakes, only punishes those who call him out",
      "The Plains of the Righteous is where hero angels craft paths for future angels — humans who are mighty, kind, and brave",
      "Souls can be forced to heaven via contract — the vampire contract forces vampire souls to heaven as punishment (Hades agreed because he enjoys others' suffering)",
      "Leaving heaven requires training that takes at least 100 years — but you can contact people on Earth during that time",
      "God has attempted to train/break disobedient fates through increasingly harsh methods",
    ],
  },
  {
    id: "hell_governance",
    name: "Hell & Lucifer's Domain",
    description: "Hell is ruled by Lucifer (a fallen angel) and operates as a punishment realm with its own political structure. Lucifer runs Hell based on feedback from demons and personally handles tasks when no one volunteers.",
    rules: [
      "Lucifer is the King of Hell — a fallen angel, not a demon by birth",
      "Hell has a feedback-based governance — if no demon volunteers for a task, Lucifer does it himself",
      "Murder generally gets you a 'straight ticket to heaven' unless you were exceptionally evil",
      "Souls can be bargained for — Gambit won Evelyn's soul through a coin toss, Lucifer owns souls through deals",
      "Lucifer made a deal with Atropos: he trains her to rule Hell while he takes over Heaven",
      "Hell has sophisticated punishment chambers with technology for inducing hallucinations and psychological torment",
      "Demon society avoids 'needlessly murdering another demon' — some sins are avoided even in Hell",
      "The demon king can arrange marriages for political purposes — heirs are necessary for succession",
      "Fallen angels can enter heaven briefly but risk burning up or being shot down by Dike's blue fire",
      "Lucifer values honesty and rule-breaking — he teaches that 'rules are meant to be broken' if they're bad rules",
    ],
  },
  {
    id: "celestial_realm_succession",
    name: "Celestial Realm Succession Crisis",
    description: "The Celestial Realm (fae/angelic higher plane) has a complex succession crisis driven by sibling rivalry, with multiple princes vying for the throne while their father remains indecisive.",
    details: [
      "The king is too indecisive to choose a successor — either waiting for Raz to return or unable to pick between children",
      "Raz (Crown Prince) left the Celestial Realm to live on Earth, refusing to rule — but Kieran wants him back",
      "Calyx is the 'nice one' but still an instigator in the succession war — puts Raz on his radar when competition heats up",
      "Draco staged a coup that Kieran and Raz must reverse — Kieran orders allies to help as both a request and royal command",
      "Kieran hates the mortal realm and stays in the Celestial Realm — Raz only returns because Kieran wants to be with family",
      "The siblings are all 'stubborn as hell' — Raz demands Kieran take over as ruler if he helps kill Draco",
      "The throne is seen as everything — 'They were worthless if they didn't have the throne'",
      "Kieran uses her princess authority to command others — pulling rank as needed",
      "The children believe their father would never agree to abdicate or choose — creating a power vacuum",
    ],
  },
  {
    id: "soul_mechanics",
    name: "Soul Mechanics & Afterlife Rules",
    description: "Souls in MFML follow specific rules about where they go after death, how they can be traded, and what determines their fate.",
    details: [
      "Vampire souls are normally damned — the afterlife contract forces them to heaven as punishment",
      "Souls in the afterlife appear at whatever age the person preferred — most choose to appear young",
      "Murder usually results in going to heaven (counterintuitively) unless the person was 'really bad'",
      "God can intervene in soul placement — moving souls between heaven and hell",
      "The Fates can influence soul paths and life trajectories — they craft the paths of future angels",
      "Reincarnation is possible — souls can be reborn with dormant memories that can be unlocked",
      "Soul deals are binding — shaking on a deal with Lucifer seals the contract magically",
      "Gambit (Death Reaper deity) deals in souls through games and bets — he won Evelyn's soul through a coin toss",
      "Demi-gods who die may end up in unexpected afterlife locations based on their mixed heritage — Evelyn found herself in 'a light place' despite being vampire",
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// KEY RELATIONSHIPS — Canonical pairings and dynamics
// ═══════════════════════════════════════════════════════════════════════════════
export const KEY_RELATIONSHIPS = [
  {
    id: "kya_sebastian",
    names: ["Kya", "Sebastian"],
    bondType: "Tailbond + Royal Marriage",
    description: "Kya and Sebastian are the King and Queen of the Atlantic Siren Kingdom. Their tailbond is deep and consuming — they're practically made for each other. Sebastian became King after Kya disappeared but always held her place. Their royal wedding was a grand affair with traditional siren customs.",
    dynamics: [
      "Sebastian is protective and supportive — always wants Kya to rely on him",
      "Kya is strong-willed but struggles with the weight of being royalty",
      "Dillon serves as their royal advisor and is personally invested in both of them",
      "Their grandmother approved of and supported the union",
      "The tailbond makes them deeply connected — they savor time together before busy periods",
    ],
  },
  {
    id: "evelyn_multi",
    names: ["Evelyn"],
    bondType: "Multiple Bonds (Various)",
    description: "Evelyn is central to nearly every major storyline. Her relationships span species and realms — she has bonds with vampires, werewolves, and gods. Her complicated love life is a driving force of MFML's drama.",
    dynamics: [
      "Evelyn and Dimitri — complex power dynamic; he's controlling, she's rebellious. He trained her 'like a dog' at times.",
      "Evelyn and Bryce — former unofficial relationship; she respected him as King but they pushed each other away. He was cold and tyrannical but softened around her.",
      "Evelyn and Rex — deep trust; she described him as the one sane person she could rely on",
      "Evelyn and Ares — father-daughter relationship; he wanted to train her as a goddess but she refused to stay 100 years in heaven",
      "Evelyn discovered she might not be a full pureblood vampire — her soul went to 'a light place' when she died",
      "Evelyn and Val — he promised to train her into the best vampire Queen, teaching her about stars and vampire culture",
      "Evelyn established the supernatural school treaty — her biggest political achievement",
    ],
  },
  {
    id: "lachesis_relationships",
    names: ["Lachesis"],
    bondType: "Multiple (Angel, Demon, God)",
    description: "Lachesis has the most tragic romantic history in MFML — entangled with God, Gabriel, Lucifer, and Ford across multiple lives and realms.",
    dynamics: [
      "God wanted Lachesis as his queen — she was forced into the arrangement",
      "Gabriel helped Lachesis escape heaven and was beheaded for it — she tried to take the fall for him",
      "Lucifer and Lachesis/Atropos share a deep bond — she carries their child Asrius (half-divine, half-demonic)",
      "Ford was her love in another life — he turned monstrous, killed everyone, then killed himself in a moment of clarity to protect her and their children",
      "She carries deep guilt over Ford — 'My love turned him into a monster'",
      "She screams at God openly — one of the few beings willing to criticize him to his face",
      "Her relationships with angels Cruz (born angel she admired) and others add more complexity",
    ],
  },
  {
    id: "james_soulmate",
    names: ["James"],
    bondType: "Soulmate Bond (Siren-Werewolf)",
    description: "James is a siren king who discovers his soulmate is a werewolf — a cross-species bond that shouldn't exist but does. He initially tried to push her away before accepting the bond.",
    dynamics: [
      "Initially resistant — 'He's been such an ass to you and now look. You're soulmates.'",
      "His brother Leo finds the irony hilarious — 'probably because you're the only woman who can put up with his bull shit'",
      "James was willing to turn his soulmate into a partial siren and be turned into a partial werewolf",
      "He accepts the need for an heir as political duty, not just romance",
      "Has deep respect for the Fates — 'If the fates didn't want this meeting to happen, they would have made certain that you were gone'",
    ],
  },
  {
    id: "raz_kieran",
    names: ["Raz", "Kieran"],
    bondType: "Lovers + Political Alliance",
    description: "Razwell and Kieran are lovers from the Celestial Realm — Raz left the realm to live on Earth but would give up everything for Kieran. Their dynamic is passionate and combative.",
    dynamics: [
      "Raz left the Celestial Realm to live on Earth — prefers the mortal realm over ruling",
      "Raz only ever returns to the Celestial Realm because Kieran wants to be with their family",
      "Kieran is the type to use her royal status to force cooperation — ordering others as both request and command",
      "Raz demands Kieran take over as ruler if he helps with the coup against Draco",
      "Both are stubborn — their arguments are intense but rooted in deep love",
      "Kieran accuses Raz of always putting himself first; Raz accuses Kieran of the same",
    ],
  },
  {
    id: "lucifer_atropos",
    names: ["Lucifer", "Atropos"],
    bondType: "Deal-Based Partnership Turned Romance",
    description: "Lucifer and Atropos started as a political deal — she rules Hell while he conquers Heaven — but their dynamic evolved into genuine connection. They are 'opposites' who complement each other.",
    dynamics: [
      "Their deal: Lucifer trains Atropos to take over Hell; she helps him take Heaven. Sealed with a handshake.",
      "Lucifer tries to corrupt the goodie-two-shoes Atropos — 'You were born from the darkness, you were meant to be bad'",
      "Atropos's 'worst sin' was stealing candy — Lucifer finds her boring human morality amusing",
      "He teaches her that 'rules are meant to be broken' and tries to draw out her darker side",
      "They share deep conversations about God's hypocrisy — God punishes sinners yet commits his own sins",
      "Their child Asrius is half-divine, half-demonic — a being of unprecedented power",
    ],
  },
];

// Export everything as a single encyclopedia object for easy access
// ═══════════════════════════════════════════════════════════════════════════════
// LOCATIONS — the granular canonical places of the RP (Haley, 2026-08-24:
// "there are a lot of places in the rp that must be in the dictionary and
// fully described and referenced by the characters that would know that
// place"). REALMS above are the macro-worlds; these are the rooms, houses,
// venues and grounds scenes actually happen in. `knownBy` lists the app
// character ids who canonically know the place — buildKnownPlaces() feeds
// each character exactly their own places, room-by-room, into every prompt.
// Corpus-extracted with evidence; most-common = canon, variants in au_notes.
// knownBy ids that are app-created characters (character_overrides rows
// with no base entry). Validated against the live roster at generation
// time; tests use this to prove every knownBy resolves to a real character.
export const LOCATION_OVERRIDE_IDS = ["adrius", "alaric", "amy", "ashton", "emma", "kuroo", "lonny", "mae", "nyx", "oath", "teagan", "tucker", "valerian"];

export const LOCATIONS = [
  {
    "id": "cliffs-edge-valley",
    "name": "Cliff's Edge (town and valley)",
    "world": "Cliff's Edge",
    "description": "The small human-facing valley town of Cliff's Edge, Tennessee, that sits below the pack's mountain. On-page it is tiny and close-knit: a single hotel with wifi, a supermarket, a gas station, a small shopping center of mom-and-pop stores, and an unusually large dollar store the pack secretly owns. A main street holds 'pretty much everything,' and everything in town sits close together. Many townspeople are employed by the pack's company (Cruz/Steele Industries), which funds town infrastructure; several routes add a general store, a dance studio, a grocery store on the parkway, an ice cream shop called Mad Dog's, and a roller skating rink with a party room in back. The pack keeps a no-going-into-town-alone rule for its own members.",
    "features": [
      "Single hotel with wifi; supermarket; gas station; mom-and-pop shopping center",
      "Main street 'where pretty much everything was'",
      "Steele General Store and a dance studio (route variants)",
      "Grocery store on the parkway; Mad Dog's ice cream; roller skating rink with a back meeting room used for parties",
      "Town infrastructure supported by the pack's company; many townsfolk are pack employees",
      "Rented tourist cottage/cabin near town used by out-of-town visitors"
    ],
    "knownBy": [
      "cruz",
      "nolan",
      "bryce",
      "ryder",
      "alyce_char",
      "nicole_human",
      "haley_human",
      "eva",
      "shane",
      "roger"
    ],
    "evidence": [
      "\"The valley of Cliff's Edge, Tennessee was fairly small. The single hotel in the area had wifi, there were a few local state parks, there was a supermarket, a gas station, a small shopping center filled with mom and pop type stores, and a very large dollar store.\" (cruz-nicole-dollar-store)",
      "\"a lot of the townspeople are employed by us.\" (a-tricky-micah-cruz-route)"
    ],
    "au_notes": "Town details vary slightly per route but the name Cliff's Edge, Tennessee is constant. The task brief's expected 'Gatlinburg' references do not appear in any cluster channel. Two school-setting channels and the childhood-era channel were excluded from sourcing; only neutral town geography is catalogued."
  },
  {
    "id": "cliffs-edge-pack-house",
    "name": "The Pack House",
    "world": "Cliff's Edge",
    "description": "The pack's enormous shared home, hours up a mountain at the end of a long private driveway — 'secluded but still connected to the outside world for modern luxuries.' The front door is never locked. Inside: tall white ceilings and big open rooms; a large communal kitchen where mini-families eat where they like (breakfast and lunch are grab-and-go, dinner is communal, Sundays are special; a whistle from the kitchen announces meals); a dining room/mess hall; blue double doors joining kitchen and living room; a crowded meeting room where the whole pack gathers; a stage-equipped assembly space; an armory of celestial-steel weapons behind sliding walls, restricted to the alpha and top guards; a hidden basement safe room opened by tilting a book on a shelf; family apartments plus extra hallways (like the quiet 'hall E') added as the pack grows; and a huge attic hangout with couch, TV, VHS tapes and a window overlooking the mountains and town. The alpha's quarters (bedroom, bathroom, small office full of books and papers, nearby guest suite) occupy a private third-floor wing; alpha and beta rooms are soundproofed. The backyard has a deck, gardens, and a yard used for weekly training tournaments and games in and out of wolf form.",
    "features": [
      "Front door always unlocked; tall white ceilings, big open rooms",
      "Communal kitchen with island; whistle-called dinners (pizza night); grab-and-go breakfast/lunch; Sunday dinners",
      "Third-floor alpha wing: bedroom, bathroom, small office, guest suite ('I like to keep an eye on my guests')",
      "Soundproof alpha and beta rooms; hall E apartments; family private apartments",
      "Armory: sliding walls of celestial-steel knives, switchblades and tranquilizer devices; alpha and top guards only",
      "Hidden basement safe room behind a bookshelf; wolf scent masks guests hidden there",
      "Huge attic hangout: couch, TV, VHS tapes, paintings, window over the mountains and town",
      "Meeting room for full-pack assemblies; stage for announcements and challenges",
      "Backyard deck, gardens, and training yard for weekly tournaments",
      "Warmest shower water on the first floor, best water pressure on the second",
      "A bell is rung as a safety precaution to warn that humans may be approaching",
      "Roughly 30 members present at a time with about 20 more on patrol (early route); births in the house every couple of months"
    ],
    "knownBy": [
      "cruz",
      "ryder",
      "nolan",
      "bryce",
      "audrey",
      "alyce_char",
      "allie",
      "trix",
      "evelyn",
      "nicole_human",
      "amy",
      "eva",
      "shane",
      "luca"
    ],
    "evidence": [
      "\"The Waters Pack House was huge—tall white ceilings and big, open rooms.\" (pack_house)",
      "\"He tilts a book back on a shelf and a door pops open in the shelf itself. 'Come on. Into the safe room.'\" (indifference)"
    ],
    "au_notes": "The family surname is Waters in the earliest branch (pack_house/cruz-x-human continuity) and Steele in most later routes; the house itself keeps the same bones across AUs (mountain seclusion, third-floor alpha wing, communal kitchen). Routes diverge sharply in plot after these scenes; only the physical layout is catalogued here."
  },
  {
    "id": "cliffs-edge-clinic",
    "name": "Ryder's Clinic",
    "world": "Cliff's Edge",
    "description": "The pack's medical clinic, run by Cruz's brother Ryder ('Dr. Steele'), located in an out-building beside the main pack house — some routes place it in back of the house or by the poolside. It is stocked from the pack's supply shipments (including witchhazel, used because vampire cuts can poison werewolves), and holds gurneys, hospital beds with nurse call buttons, and a treatment table. A werewolf is always on duty, and Ryder himself sleeps at the clinic in case anyone needs help. Ryder doubles as a veterinarian, since he must treat the pack in both human and wolf form. A spare side room once used for clinic storage, with a small assistant's apartment above it, is later granted to Eva for her odd-jobs business.",
    "features": [
      "Out-building beside the main pack house (placement varies slightly by route)",
      "Witchhazel treatments for vampire-inflicted wounds",
      "Gurneys, hospital beds, bedside nurse call button; always staffed",
      "Ryder sleeps in the clinic; also serves as pack veterinarian",
      "Attached spare room with side door plus small upstairs assistant apartment (later Eva's salon/odd-job space)",
      "Route variants add an apprentice doctor (Magnus) and intern (Frank)"
    ],
    "knownBy": [
      "ryder",
      "cruz",
      "evelyn",
      "nicole_human",
      "bryce",
      "luca",
      "eva",
      "amy"
    ],
    "evidence": [
      "\"Ryder was standing inside his clinic--located in an out building besides the main pack house\" (pack_medic)",
      "\"There's always a werewolf in the clinic if you need anything and a button on the side of the bed to call in a nurse.\" (indifference)"
    ],
    "au_notes": "Consistent across nearly every Cliff's Edge route; in the summit continuity of brax-soulmate a Ryder is instead alpha of Mountain's Peak Pack in Nevada."
  },
  {
    "id": "cliffs-edge-greenhouse",
    "name": "The Greenhouse",
    "world": "Cliff's Edge",
    "description": "A glass greenhouse attached to the porch on the back of the pack house, to the right as one faces the grounds ('The pack house is straight ahead and the green house is to the right'). It is covered in plants from wall to floor, with an island counter kept clear in the center for preparing vegetables, glass ceiling and walls, and a garden bench. A tucked-away corner at the far end serves as an unofficial hangout where a small group of packmates goes to let off steam away from the pups and elder werewolves — Ace's usual spot.",
    "features": [
      "Attached to the back porch of the pack house",
      "Wall-to-floor plants; central island counter for vegetable prep; glass ceiling",
      "Garden bench used for quiet conversations",
      "Hidden back-corner de-stress hangout kept away from pups and elders"
    ],
    "knownBy": [
      "ace",
      "alyce_char",
      "evelyn",
      "cruz",
      "nicole_human",
      "eva"
    ],
    "evidence": [
      "\"The pack house is straight ahead and the green house is to the right.\" (indifference)",
      "\"This is our little secret corner in the greenhouse to let off steam.\" (indifference)"
    ],
    "au_notes": "Rowan's northwest pack keeps separate 'greenhouses' of its own (northwest-pack-lodge); the two should not be merged."
  },
  {
    "id": "cliffs-edge-pack-restaurants",
    "name": "The Three Pack Restaurants",
    "world": "Cliff's Edge",
    "description": "Three restaurants operated on pack grounds for pack members, in addition to the main house's dining room: a nice little clubhouse up the cliffside, a cafe and bar close to the poolside, and a nicer restaurant out in the meadow catered specifically to potential couples and mates. Eva's father Roger manages the menus for all three and works shifts in the meadow restaurant.",
    "features": [
      "Clubhouse up the cliffside",
      "Cafe and bar near the poolside",
      "Meadow restaurant for courting pairs",
      "Menus managed by Roger; supplements the main house dining room"
    ],
    "knownBy": [
      "roger",
      "eva",
      "cruz",
      "ryder",
      "shane",
      "trix"
    ],
    "evidence": [
      "\"The pack had three restaurants on site for their pack members plus the main houses' dining room. The pack had a nice little clubhouse up the cliffside, a cafe and bar close to the poolside, and a nice restaurant in the meadow specifically catered to potential couples or mates.\" (a-tricky-micah-cruz-route)"
    ],
    "au_notes": "Established in the a-tricky-micah-cruz-route continuity; other routes reference only the communal kitchen and dining room."
  },
  {
    "id": "cliffs-edge-cliffside",
    "name": "The Cliffside Ceremony Spot",
    "world": "Cliff's Edge",
    "description": "A ceremonial overlook down at the cliffside that surveys a good part of the forest. The pack holds its yearly coming-of-age rite there: pups ready to take on pack jobs approach 'the decider,' a bowl given to the pack by the goddess Diana, and read the role the water shows them (advanced jobs like warrior, protector, medic, midwife, builder, matchmaker and psychologist; omega jobs like feeder, cleaner, gardener and maintenance). New adult pack members are also expected to consult the decider. Alphas are determined by blood rather than by the bowl.",
    "features": [
      "Overlooks a large stretch of the forest",
      "Site of the annual role-assignment ceremony",
      "'The decider' — a divine bowl from Diana that reveals each wolf's pack role",
      "Roles listed on-page: warrior, protector, medic, midwife, builder, matchmaker, psychologist; omega jobs: breeder, feeder, cleaner, gardener, maintenance"
    ],
    "knownBy": [
      "cruz",
      "evelyn",
      "ryder",
      "allie",
      "alyce_char",
      "audrey",
      "bryce",
      "trix"
    ],
    "evidence": [
      "\"We have a little ceremony down at the cliffside. It overlooks a good part of the forest.\" (1-real-cruz)",
      "\"It's a bowl that Diana gave us in order to determine which werewolf does what job.\" (1-real-cruz)"
    ],
    "au_notes": "Detailed in 1-real-cruz; the same route later diverges into two parts (an OOC note marks a 'Bad End... Reset' split), but the ceremony site itself is stable canon."
  },
  {
    "id": "cliffs-edge-territory",
    "name": "Cliff's Edge Pack Territory",
    "world": "Cliff's Edge",
    "description": "The pack's mountain territory around the pack house: dense forest laced with patrol routes and a guarded border, valleys below the mountain, a summit run trail where the forest thickens toward the top, and woods that in one route hold a waterfall spot. The pack patrols nightly because Cliff's Edge sits closest to the origin point of the Ralidanti, shadow-warriors who materialize only at night and 'always disappear and reappear in the same spot' near the southern border; celestial-steel weapons can drive them off. Pack bonfire nights are held on the grounds with log seating. In snow-season routes the pack plays and trains in deep snow; a treaty governs contact with vampires beyond the borders. A dollar store sits down in the valley below.",
    "features": [
      "Patrolled borders (increased to pairs when threats rise)",
      "Southern border is the recurring Ralidanti incursion point",
      "Summit run trail up the mountain; forest densest near the top",
      "Waterfall spot in the woods (route variant)",
      "Bonfire nights with log seating",
      "Valleys below the mountain; dollar store down in the valley",
      "Treaty line with vampire territory"
    ],
    "knownBy": [
      "cruz",
      "nolan",
      "bryce",
      "ryder",
      "red",
      "leon",
      "evelyn",
      "nicole_human",
      "amy",
      "shane",
      "eva"
    ],
    "evidence": [
      "\"It was hours away in the mountains. Secluded but still connected to the outside world for modern luxuries.\" (pack_house)",
      "\"Our pack gets the worst of the lot. Cliff's edge is the closest to the origin of the species.\" (cruz-x-nicole)"
    ],
    "au_notes": "The Ralidanti war frames most Cliff's Edge routes; in the Evelyn-era branch the threat is instead southern packs and vampire war. The 'other side of Cliff's Edge' is teased as holding more locations but is never shown on-page."
  },
  {
    "id": "cruz-cabin",
    "name": "Cruz's Cabin in the Woods",
    "world": "Cliff's Edge",
    "description": "Cruz's small personal cabin in the woods near the edge of pack territory. Originally a single all-in-one room ('Everything in the cabin was in the same room') with a cot and a doorway to a small restroom, it was later expanded so the original space became the living room. In one continuity Cruz lists it as a private rental on 'SpaceBNB' and calls it 'my personal cabin'; in another it serves as a retreat for packmates needing distance from the main house; in a late branch a falling tree destroys it and the insurance money funds the new guest building.",
    "features": [
      "Single-room origin later expanded (living room plus bedroom)",
      "Cot and small restroom",
      "Near the patrol perimeter of pack lands",
      "Sometimes rented out to visitors as a vacation cabin"
    ],
    "knownBy": [
      "cruz",
      "evelyn",
      "nicole_human",
      "bryce",
      "alyce_char"
    ],
    "evidence": [
      "\"Everything in the cabin was in the same room.\" (pack-cabin)",
      "\"the cabin's living room which used to be the main part of the cabin before they expanded it\" (pack-cabin)"
    ],
    "au_notes": "Appears across several routes with consistent placement but different uses (personal retreat, rental, refuge); destroyed by a fallen tree in the late 1-real-cruz timeline."
  },
  {
    "id": "cliffs-edge-guest-building",
    "name": "The Guest Building",
    "world": "Cliff's Edge",
    "description": "A newer guest residence on pack lands, built with the insurance money after a tree collapsed Cruz's cabin: a two-floor, beige-sided complex of units that feels almost like a hotel, with an elevator and numbered keyed rooms (room 210 is shown). Each unit is a clean little apartment with a small kitchenette and bathroom, plus a small guest table. By this era the pack also earns income from changing cabins it operates in the national parks.",
    "features": [
      "Two floors, beige siding, hotel-like keyed units",
      "Elevator access; numbered rooms",
      "Units contain kitchenette, bathroom, bed and small table",
      "Funded by insurance after the old cabin's destruction"
    ],
    "knownBy": [
      "cruz",
      "bryce",
      "evelyn",
      "ryder"
    ],
    "evidence": [
      "\"a two floor, beige-sided complex with units inside. It felt almost like a hotel.\" (1-real-cruz)",
      "\"we started making money off of our changing cabins in the national parks.\" (1-real-cruz)"
    ],
    "au_notes": "Exists only in the twenty-years-later stretch of the 1-real-cruz timeline."
  },
  {
    "id": "cliffs-edge-state-park",
    "name": "The State Park and Changing Cabins",
    "world": "Cliff's Edge",
    "description": "Local state parks ring the valley, with marked trails and a signed trailhead where visitors park. The nearest state park is where new wolves are historically taken for their first shift, and the pack maintains 'changing cabins' there — newcomers are escorted to one of the state park changing cabins around noon for their transformation. In the later timeline these park cabins become a pack revenue source. The park trails connect down toward the big lake.",
    "features": [
      "Multiple local state parks around the valley",
      "Signed trailhead and parking area",
      "Pack-run changing cabins for first shifts",
      "Trail connection to the lake"
    ],
    "knownBy": [
      "cruz",
      "evelyn",
      "bryce",
      "lindon",
      "nicole_human",
      "haley_human"
    ],
    "evidence": [
      "\"You're going to be sent to one of the state park changing cabins close to noon\" (indifference)",
      "\"The car's down in the nearest state park\" (1-real-cruz)"
    ],
    "au_notes": "The specific park is unnamed on-page; different routes use it for first shifts, meetings and departures."
  },
  {
    "id": "cliffs-edge-dollar-store",
    "name": "The Dollar Store",
    "world": "Cliff's Edge",
    "description": "A very large dollar store in the valley — big enough that visitors mistake it for a second supermarket — secretly owned by the pack as a way to spend and make money off themselves and reinvest everything back into the pack. The pack rides a bus down for group shopping trips, with the alpha holding the door and counting heads. It stocks groceries, snacks, crafting supplies and toys; its parking lot is a local landmark and meeting point.",
    "features": [
      "Pack-owned; profits reinvested into the pack",
      "Unusually large stock: snacks, crafts, household goods, toys",
      "Group pack shopping trips by bus",
      "Large parking lot"
    ],
    "knownBy": [
      "cruz",
      "nolan",
      "alyce_char",
      "nicole_human",
      "haley_human",
      "bryce"
    ],
    "evidence": [
      "\"The pack owned the dollar store in the Valley. It was another way they could spend and make money off of themselves to invest everything back into the pack.\" (cruz-nicole-dollar-store)"
    ],
    "au_notes": "Central to the cruz-nicole-dollar-store meet-cute route; referenced in other routes as the valley's dollar store."
  },
  {
    "id": "annies-diner",
    "name": "Annie's",
    "world": "Cliff's Edge",
    "description": "The one diner in town that resembles a nicer restaurant; everyone in Cliff's Edge loves it and it is known for its hospitality. Beyond that single description it is named but not further detailed on-page.",
    "features": [
      "The town's only diner-style restaurant",
      "Reputation for hospitality"
    ],
    "knownBy": [
      "cruz"
    ],
    "evidence": [
      "\"Annie's was the one diner in town that resembled a nicer restaurant. Everyone loved it there. It was known for its hospitality.\" (cruz-x-human)"
    ],
    "au_notes": "Named in a Cliff's Edge town AU; no interior scenes occur on-page. This is the only diner found anywhere in the cluster's channels."
  },
  {
    "id": "cliffs-edge-lake",
    "name": "The Lake",
    "world": "Cliff's Edge",
    "description": "A huge lake in the woods near Cruz's cabin, a short walk down from the cabin with kayaks kept in back for guests. It is the pack's standby recreation spot — kayaking on calm days, swimming, and trout fishing trips ('go with the guys down to the lake to fish for some trout'). Route branches also stage quiet lakeside talks on the soft grass at the water's edge.",
    "features": [
      "Kayaks stored behind Cruz's cabin; life jackets kept at the waterline",
      "Trout fishing",
      "Soft grass bank used for rest and conversation",
      "Tips easily — kayakers are warned to keep steady"
    ],
    "knownBy": [
      "cruz",
      "nicole_human",
      "haley_human",
      "nolan",
      "roger",
      "eva"
    ],
    "evidence": [
      "\"He quickly jogs them over to the lake. It was huge.\" (cruz-nicole-dollar-store)",
      "\"I was going to go with the guys down to the lake to fish for some trout\" (a-tricky-micah-cruz-route)"
    ],
    "au_notes": "A separate lake also exists beside Alaric's Bayou Veil pack house in brax-soulmate; they are different bodies of water."
  },
  {
    "id": "cliffs-edge-university",
    "name": "The Pack-Run University",
    "world": "Cliff's Edge",
    "description": "A university near the pack lands that the pack runs, with a beautiful campus. Its practical function on-page is as cover: the pack offers jobs or scholarships there (teaching assistant posts, an assistant-to-the-dean role, a sociology department is named) so that new adult members have a plausible human-world reason to move to Cliff's Edge.",
    "features": [
      "Pack-operated; scenic campus",
      "Cover employment for newcomers (teaching assistant, dean's assistant)",
      "Sociology department named on-page"
    ],
    "knownBy": [
      "cruz",
      "nicole_human"
    ],
    "evidence": [
      "\"We have a school nearby. It's pack run. A university. We usually offer jobs or scholarships to mates as a way to give them a decent excuse to move here.\" (cruz-x-nicole)"
    ],
    "au_notes": "Described only in the cruz-x-nicole continuity; no campus scenes occur on-page."
  },
  {
    "id": "lances-shadow-house",
    "name": "Lance's Ralidanti House",
    "world": "Cliff's Edge",
    "description": "A house granted to the Ralidanti Lance, reached across a dark field beyond Cliff's Edge pack lands and lit from outside by what look like stars made of hardened shadow. It exists only at night and its location changes; inside it appears like any ordinary grand home. The entry hall is large, with a double rounded staircase rising from both ends of the room and two long hallways running beneath the stairs; there is a stocked kitchen with a refrigerator, and bedrooms upstairs. Every Ralidanti receives a house like it, courtesy of the goddess Nyx, as part of her bargain to give her warriors 'as close a simulation to life as possible in exchange for their loyalty.'",
    "features": [
      "Exists only at night; location shifts",
      "Exterior lit by 'stars made entirely out of hardened shadow'",
      "Large entry hall; double rounded staircase; two hallways beneath the stairs",
      "Kitchen with modern appliances; upstairs bedrooms",
      "One such house is granted to each Ralidanti by Nyx"
    ],
    "knownBy": [
      "lance",
      "nicole_human"
    ],
    "evidence": [
      "\"When you come inside, the space will look like any other—but it only exists at night and our home's location changes.\" (cruz-x-nicole)",
      "\"A double, rounded staircase started at both ends of the room and there was a large entry hall. There were two long hallways under the stairs.\" (cruz-x-nicole)"
    ],
    "au_notes": "Appears only in the cruz-x-nicole route; catalogued architecturally only."
  },
  {
    "id": "little-beach-summer-estate",
    "name": "The Summer Estate at Little Beach",
    "world": "Cliff's Edge",
    "description": "The pack's summer estate on Little Beach, used only a few weeks each year — or as an evacuation refuge when the mountain house is unsafe. Little Beach itself has a boardwalk, an aquarium and a little 4D theater; the estate sits at the very end of the boardwalk, hidden from view behind a big gate and wall. Vines climb the inside of the wall, a spare key is kept under a garden gnome inside the gate, and the beach house holds a comfortable living room. Decades later the next generation still uses it as a getaway and shops for essentials on the boardwalk.",
    "features": [
      "At the end of Little Beach's boardwalk, hidden behind a big gate",
      "Wall with climbing vines; spare key under the garden gnome",
      "Boardwalk, aquarium, and 4D theater nearby",
      "Used a few weeks each summer; doubles as an evacuation site"
    ],
    "knownBy": [
      "evelyn",
      "nolan",
      "luca",
      "bryce",
      "ryder",
      "asmodeus",
      "thorne"
    ],
    "evidence": [
      "\"Yes... we have one on Little Beach, have you heard of it? It has a boardwalk.. aquarium.. 4d little theater.\" (pack_house)",
      "\"Our estate is at the end of the boardwalk and hidden from view by a big gate.\" (pack_house)"
    ],
    "au_notes": "Belongs to the Waters-era continuity of the pack; revisited a generation later in pack-beachhouse."
  },
  {
    "id": "six-guys-burger",
    "name": "Six Guys",
    "world": "Cliff's Edge",
    "description": "An old burger place on the Little Beach boardwalk with a counter and white-and-red checkered walls, teased on-page as feeling 'so 2010' but serving genuinely good burgers. One character claims it is run by vampires, which lets mixed supernatural parties order food they can actually eat. Named and lightly described in a single scene; no further canon exists on-page.",
    "features": [
      "White and red checkered walls; counter service",
      "Old-fashioned decor; well-regarded burgers",
      "Said (by one character) to be vampire-run"
    ],
    "knownBy": [
      "asmodeus",
      "thorne"
    ],
    "evidence": [
      "\"the two found themselves in the middle of an *old* burger place called Six Guys.\" (pack-beachhouse)"
    ],
    "au_notes": "Appears once, in the next-generation pack-beachhouse scene."
  },
  {
    "id": "northwest-pack-lodge",
    "name": "The Northwest Pack Lodge",
    "world": "Werewolf world (shared)",
    "description": "Alpha Rowan's pack seat: a tall lodge in the mountains of the northwest Olympic Circle just north of Seattle, Washington, surrounded by snow, wind and forest. The lodge is bigger than any ordinary pack house — 'some say we have a secret kingdom in the woods.' Its open-concept entrance connects to a meeting room; a living room with TV and games anchors daily life; a billiards room fills the basement, with a separate game room holding a poker table; a hallway of guest and family rooms runs deep into the building; the kitchen keeps a liquor cabinet stocked with Nellie's homemade Moonshine, one of the only liquors strong enough to affect werewolves. Rowan's office sits just off the kitchen (sleek metal desk, light blue walls, and a wall collection of magic knives), and his private study holds old books, a loveseat and its own bathroom. The alpha's quarters end a hall behind double doors: an ever-burning fireplace with a TV above it, an en-suite bathroom, a seating area, and a balcony shortcut to the ground. Barracks beneath the lodge store serious weaponry, including flamethrowers. Because the cold is too harsh to go outside unshifted, the pack built many outbuildings: a billiards-and-games building, an indoor swimming pool and hot tub center, an indoor tennis court, a big red barn kept warm all winter, stables with horses (Lucius's white horse August), and an indoor riding rink. The pack also keeps greenhouses, and a local village delivers food like sushi.",
    "features": [
      "Open-concept entrance joined to the lodge's meeting room",
      "Basement billiards room; separate poker/game room",
      "Kitchen with liquor cabinet; Nellie's Moonshine brew",
      "Rowan's office off the kitchen with a wall of magic knives (including the outlawed ritual 'demon knife')",
      "Private study with books, loveseat and bathroom",
      "Alpha quarters: double doors, always-lit fireplace, TV, en-suite, balcony",
      "Underground barracks with flamethrowers and guns",
      "Outbuildings: games building, indoor pool and hot tubs, indoor tennis court, warm red barn, stables and riding rink",
      "Pack greenhouses; sushi delivered from the local village",
      "Rogue wolves haunt the surrounding forest; 'the rituals' lore — rogues seeking immortality via a forged blade and a bargain with Hades"
    ],
    "knownBy": [
      "rowan",
      "nellie",
      "lucius",
      "evelyn"
    ],
    "evidence": [
      "\"a tall lodge in the middle of the mountains of the northwest Olympic Circle, just north of Seattle, Washington. There was snow everywhere.\" (northwest-pack-lodge)",
      "\"One of them was a billiards and games building, an indoor swimming pool and hot tub center, they even had an indoor tennis court and a big, red barn that they kept warm all winter long.\" (northwest-pack-lodge)"
    ],
    "au_notes": "Belongs to the Evelyn continuity (she arrives as a vampire negotiator for the Allied Vampires; Cruz's pack is called 'the north east pack' in the companion pack-house channel, and commonwealth vampire lands lie near the lodge). In the separate Cruz–Nicole/summit continuities Rowan is instead a rival west-coast alpha who organizes the alpha summits. Later branches of this route diverge sharply; only the lodge's physical layout is catalogued."
  },
  {
    "id": "the-wilderness",
    "name": "The Wilderness",
    "world": "Werewolf world (shared)",
    "description": "A rogue settlement a few hundred miles from the northwest lodge, in the middle states: a small community of rogue wolves who refuse to be led by one leader — no alpha, deliberately fluid. It is reputed nearly impenetrable and dismissed by pack wolves as 'dirty and insignificant,' though those who know it say that reputation is wrong; joining it is called 'social werewolf suicide' by pack society. Lucius's mother came from there. It is named and characterized but never shown on-page.",
    "features": [
      "No alpha; leaderless, fluid rogue community",
      "Nearly impenetrable to outsiders",
      "Located in the middle states, far from the coasts",
      "Members may come and go, but newcomers are advised to lie low for years"
    ],
    "knownBy": [
      "lucius",
      "bryce",
      "evelyn"
    ],
    "evidence": [
      "\"The settlement is called the wilderness. It's a small, small pack of rogues who refuse to be lead by one leader. They don't have an alpha, and they want to be fluid.\" (northwest-pack-lodge)",
      "\"You could go to The Wilderness, but that would be like social werewolf suicide.\" (1-real-cruz)"
    ],
    "au_notes": "Referenced consistently across two separate continuities (northwest-pack-lodge and 1-real-cruz), making it stable shared canon; never depicted directly."
  },
  {
    "id": "werewolf-summit-venue",
    "name": "The Alpha Summit Venue",
    "world": "Werewolf world (shared)",
    "description": "The recurring venue for the alpha summit, the annual week-long gathering of pack alphas, their betas, and invited supernatural dignitaries. Attendees are housed in named shared cabins ('the Eagle cabin' is assigned on-page), with schedules and rooming arranged meticulously by Alpha Rowan, who holds the elected 'lead alpha seat' and hires a chef for the week. The grounds include a welcome hall with adjacent activity rooms (a cooking class runs in 'the room next to the welcome hall'), a cafeteria/dining hall for communal meals, meeting rooms for the formal sessions, open field space, and an on-site clinic staffed for the duration. Newly mated pairs are granted a cabin of their own. Its geographic location is never stated on-page.",
    "features": [
      "Named attendee cabins (Eagle cabin shown); mates receive private cabins",
      "Welcome hall with adjoining activity rooms; scheduled morning and afternoon sessions",
      "Cafeteria/dining hall with hired chef",
      "Formal meeting rooms; open field",
      "On-site clinic during the summit",
      "Presided over by lead alpha Rowan; attended across routes by Cruz, Ryder, Teagan, Maxen, Alaric, Brax and Vaughn"
    ],
    "knownBy": [
      "rowan",
      "teagan",
      "kya_sg1",
      "dillon",
      "cruz",
      "ryder",
      "brax",
      "vaughn",
      "nicole_human",
      "alaric"
    ],
    "evidence": [
      "\"I was supposed to be in the Eagle cabin. How about you?\" (kya-werewolf-summit)",
      "\"It says that it's in the room next to the welcome hall.\" (brax-soulmate)"
    ],
    "au_notes": "Two summit continuities exist (kya-werewolf-summit and brax-soulmate) with consistent structure. Note for the encyclopedia team: the task brief's expected 'Nashville basement apartment' and 'Lonny & Mae's diner' locations do not appear anywhere in this cluster's corpus channels (vaughneo-and-juliet contains no Nashville/diner content and is a non-werewolf school-setting AU excluded from sourcing); no such places are named on-page and none were catalogued."
  },
  {
    "id": "storm-peak-town",
    "name": "Storm Peak",
    "world": "Storm Peak",
    "description": "A hidden town tucked along the rugged Northern California coast, where pine-lined cliffs meet crashing waves. It appears on no map; most outsiders never find it, and those who do leave with no memory of it — exactly as the Storm Peak Pack prefers. The pack that shelters here is not the oldest but is the largest, a community built on trust and loyalty rather than bloodline, having survived internal schisms and rogue attacks. It is led by young Alpha Teagan Moreau, who inherited the role at nineteen after his older brother died in a border skirmish with rogue wolves. Pack members hold ordinary town jobs (Lena works as a mechanic in town), and the pack fights nightly Ralidanti incursions like their inland cousins.",
    "features": [
      "Northern California coast; pine-lined cliffs and ocean",
      "Absent from all maps; outsiders lose their memories of it",
      "Home of the Storm Peak Pack — the largest pack, community-built rather than bloodline-built",
      "Led by Alpha Teagan Moreau; beta Dax Navarro; strategist Mace; healer Zaid",
      "A hybrid seer, Adeline, lives at the outskirts of the territory under pack protection",
      "Ralidanti raids strike the borders at night"
    ],
    "knownBy": [
      "teagan",
      "rory"
    ],
    "evidence": [
      "\"Tucked along the rugged Northern California coast, where pine-lined cliffs meet crashing waves, lies Storm Peak, a hidden town that doesn't exist on any map. Most outsiders never stumble across it—and those who do tend to leave with no memory of it at all.\" (storm-peak)",
      "\"We *do* have a rivalry going on with Storm Peak Pack, though. It belongs to Alpha Teagan in California.\" (brax-soulmate)"
    ],
    "au_notes": "The brax-soulmate summit continuity independently confirms Storm Peak as Teagan's California pack, tying the two routes together."
  },
  {
    "id": "storm-peak-pack-house",
    "name": "Storm Peak Pack House",
    "world": "Storm Peak",
    "description": "The Storm Peak Pack's communal house. Halls with high gilded windows and polished wood floors connect a busy dining hall (tray service, held tables, communal breakfasts), the pack clinic run by Doctor Zaid — with apprentices, a healer-training program, and a soundproof back room used for private conversations — and a small planted garden outside the dining hall where members can eat among the flowers. The Alpha's private wing sits at the far end of the house, more secluded than the rest, behind a heavy carved door: inside is Teagan's office with a mahogany desk, and a bookshelf that opens by pressing certain books into a secret passage. The motion-lit passage, built generations ago against invasions and known only to the alpha family and a select few, exits into the woods at the edge of the territory where an unremarkable escape car is kept. The pack also maintains a research division.",
    "features": [
      "Dining hall with communal meals and held tables",
      "Clinic with soundproof back room; Zaid as head doctor; healer apprenticeships",
      "Small garden for outdoor meals beside the dining hall",
      "Alpha's secluded private wing behind a heavy carved door; office with mahogany desk",
      "Bookshelf-hidden secret passage with motion-sensor lights exiting to the territory's edge and a throwaway car",
      "High gilded windows and polished wood floors",
      "Pack research division"
    ],
    "knownBy": [
      "teagan",
      "rory"
    ],
    "evidence": [
      "\"The Alpha's private wing was at the far end, more secluded than the rest, with its own heavy, carved door.\" (storm-peak)",
      "\"Teagan walks over to a bookshelf and presses down on a few books, opening up a secret door.\" (storm-peak)"
    ],
    "au_notes": "This is the storm-peak channel's version of the pack's home; the kya-werewolf-summit continuity shows Teagan's pack living instead in a coastal gated community (separate entry). The storm-peak route also introduces 'Magia' lore — guardians of Diana whose powers wake on mating — as this pack's route-specific mythology."
  },
  {
    "id": "storm-peak-gated-community",
    "name": "Storm Peak Pack Grounds (Coastal Community)",
    "world": "Storm Peak",
    "description": "In the summit continuity, Teagan's pack is too large for one house and instead occupies a private gated community beside the ocean: houses spread through a large forested property, centered on a mega-mansion whose entire upper wing forms the alpha's quarters. The manicured grounds are the domain of successive Lunas, each of whom adds her mark — flower-lined paths, a butterfly-etched fountain, a tennis court, a pond. Golf carts run the paths past a boutique dress shop, a restaurant, a pool, and a golf course that climbs to a large stone gathering center on a hilltop, where a white wooden gazebo draped in flowers and fabric hosts the pack's mating ceremonies. A beach lies directly beside the community. Teagan's soundproof office sits in the mansion.",
    "features": [
      "Private gated community of houses beside the ocean with a large forest",
      "Central mega-mansion; alpha quarters occupy an entire wing",
      "Luna-designed grounds: flower-lined paths, fountains, tennis court, pond",
      "Boutique, restaurant, pool, golf carts and golf course",
      "Hilltop stone gathering center with white wooden ceremony gazebo",
      "Adjacent beach; weddings held on the sand",
      "Soundproof alpha office"
    ],
    "knownBy": [
      "teagan",
      "kya_sg1",
      "emma"
    ],
    "evidence": [
      "\"they were all in a private gated community with houses spread throughout and a large forest in the grounds as well.\" (kya-werewolf-summit)",
      "\"a large gathering center made of stone at the end of a golf course at the top of a hill.\" (kya-werewolf-summit)"
    ],
    "au_notes": "Route variant of Teagan's pack home from kya-werewolf-summit; the pack is unnamed on-page there but matches Storm Peak's Teagan-led, oceanside California identity confirmed in brax-soulmate. The most common depiction (dedicated storm-peak channel) is the single pack house; this community version is notated as the AU variant."
  },
  {
    "id": "storm-peak-recreational-building",
    "name": "The Recreational Building and Hidden Basement",
    "world": "Storm Peak",
    "description": "The pack's recreational building on Teagan's grounds, offering indoor activities alongside the outdoor tennis and horseback riding. A side door opens onto a darkened hallway that descends, growing colder, into a hidden basement — normally locked and posted with a do-not-enter sign, its existence covered by a story that dark mages are housed there to contain a banished mage from a portal-realm called the Antila. In truth the basement is a divine prison: the forgotten god Oath, a demigod son of Diana who once helped found the werewolf council, is chained to the wall with golden cuffs inside golden bars built to hold a god, steeped in dark magic no one has explained. The basement door is sealed by a god-forged lock of overlapping circles, squares, triangles and scattered letters; only matching god-made keys open it. He was captured generations ago and only the alpha and the cell's caretakers know he is there.",
    "features": [
      "Indoor recreation upstairs; tennis and riding held outside nearby",
      "Side-door hallway descending into a cold, dark basement",
      "Golden bars and golden wall-cuffs built to imprison a god",
      "God-forged symbol lock; only two god-made keys exist in the pack",
      "Cover story: dark mages containing the Antila portal mage",
      "Prisoner: Oath, demigod son of Diana, co-founder of the werewolf council"
    ],
    "knownBy": [
      "teagan",
      "kya_sg1",
      "oath"
    ],
    "evidence": [
      "\"she could stop by some of the outdoor activities like tennis or horseback riding, or she could even explore inside the recreational building.\" (kya-werewolf-summit)",
      "\"chained to a wall with golden cuffs as well as golden bars surrounding him. It was clearly meant to hold a god in it\" (kya-werewolf-summit)"
    ],
    "au_notes": "Appears in the kya-werewolf-summit continuity of Teagan's pack; the origin of Oath's imprisonment ('someone in this pack three or four generations ago') is left unresolved on-page."
  },
  {
    "id": "storm-peak-changing-bunker",
    "name": "The Changing Bunker",
    "world": "Storm Peak",
    "description": "A fortified changing area and emergency shelter on Teagan's pack grounds. New wolves are locked in for the night of their first shift for everyone's safety — first transformations are exhausting and control can slip — and the same structure serves as the pack's safety bunker in an attack, designed comfortably for both short and long containments. Inside are rows of small lockable apartments like plain hotel rooms, with furnishings stored separately in a locked storage room until needed. No one can get in or out without a push-button release or one of the pack's two god-made keys, which are kept by the alpha and the bunker's caretakers — the same key pattern that seals the recreational building's basement.",
    "features": [
      "Locks down overnight for first shifts",
      "Doubles as the pack's emergency bunker",
      "Small lockable apartment rooms; supplies kept in a locked storage room",
      "Entry only by internal button or god-made key (two exist: alpha's and caretakers')"
    ],
    "knownBy": [
      "teagan",
      "kya_sg1"
    ],
    "evidence": [
      "\"It would lock them in for a night for safety. It was also used as a safety bunker for emergencies.\" (kya-werewolf-summit)",
      "\"The separate apartments also lock.\" (kya-werewolf-summit)"
    ],
    "au_notes": "From the kya-werewolf-summit continuity; the storm-peak channel's version of the pack does not show an equivalent structure on-page."
  },
  {
    "id": "teagans-treehouse",
    "name": "Teagan's Treehouse",
    "world": "Storm Peak",
    "description": "Teagan's private retreat a few states away from his pack — nothing like the word 'treehouse' suggests. It is a luxurious house in the sky: wooden boards integrating it into the forest, large windows looking out over the woods, a cozy interior, and an infinity pool at the edge of the deck gazing into the trees. The area is popular with alphas, several of Teagan's alpha friends bought neighboring treehouse properties after staying in his, and the surrounding woods are remote enough for wolves to run freely. It functions as the pressure valve for alphas needing to escape the weight of their packs.",
    "features": [
      "Luxury elevated house with large forest-view windows",
      "Infinity pool on the deck edge",
      "Remote woods safe for shifted runs",
      "Neighboring treehouses owned by fellow alphas; a known alpha get-away region"
    ],
    "knownBy": [
      "teagan"
    ],
    "evidence": [
      "\"It felt more like a house up in the sky, with large windows looking out at the woods and an infinity pool at the edge of the deck.\" (storm-peak)",
      "\"I do have a treehouse a few states away. It's popular with wolves especially alphas to own one there.\" (storm-peak)"
    ],
    "au_notes": "The treehouse region's state is unstated; Teagan's periodic disappearances to 'a trusted group of fellow Alphas across state lines' in the same route likely reference this circle."
  },
  {
    "id": "bayou-veil-pack",
    "name": "Bayou Veil Pack Lands",
    "world": "Werewolf world (shared)",
    "description": "The pack led by Alpha Alaric in the brax-soulmate summit continuity, named on-page only as Bayou Veil Pack. Its grounds are shown sparsely: a pack house with a communal dinner table, an office with a locked drawer of pack documents, forest around the house, and a quiet lake within walking distance with soft grass at the water's edge where the alpha takes breaks. Beyond these glimpses the territory is named but never described in detail on-page.",
    "features": [
      "Pack house with communal dinners",
      "Alpha's office with locked document drawer",
      "Nearby lake with grassy bank",
      "Rivalry with Storm Peak Pack; attends Rowan's alpha summits"
    ],
    "knownBy": [
      "alaric",
      "nicole_human"
    ],
    "evidence": [
      "\"As the Luna of Bayou Veil Pack, yes... We *do* have a rivalry going on with Storm Peak Pack, though.\" (brax-soulmate)",
      "\"When they reached the soft grass by the water's edge, Alaric sank down onto the ground\" (brax-soulmate)"
    ],
    "au_notes": "Exists only in the brax-soulmate continuity; the route's interpersonal premise is not part of this location record. A luxury hotel-spa the pair visits (private suite, steam room, two saunas, shared restaurant) is off pack lands and otherwise unlocated."
  },
  {
    "id": "mountains-peak-pack",
    "name": "Mountain's Peak Pack",
    "world": "Werewolf world (shared)",
    "description": "Named, never described on-page. A werewolf pack in Nevada led by an Alpha Ryder in the brax-soulmate summit continuity; he is characterized as a hard-edged summit attendee, but no scene takes place on his pack's lands and nothing of the territory itself is shown.",
    "features": [
      "Located in Nevada",
      "Led by Alpha Ryder (summit-continuity version of the character)"
    ],
    "knownBy": [
      "ryder"
    ],
    "evidence": [
      "\"He's the alpha of Mountain's Peak Pack in Nevada. He's kind of a hard ass if he's in a bad mood.\" (brax-soulmate)"
    ],
    "au_notes": "In Cliff's Edge continuities Ryder is instead the pack doctor and Cruz's brother; this Nevada alpha version exists only in brax-soulmate."
  },
  {
    "id": "nashville-basement-apartment",
    "name": "The Nashville Basement Apartment",
    "world": "Nashville",
    "description": "A large rented basement apartment in Nashville shared by the 'would-be Alphas' — wolves with Alpha blood who left or cannot return to their original packs: Jack, Teagan, and Tucker, with Ford Steele among them until he moved out. It is the secret second home where the Alpha title comes off: Teagan slips across state lines every few months to decompress here, hiding the trips from his pack as routine business. The apartment sits beneath Lonny and Mae's diner, and the residents consider themselves the building's fiercely loyal 'freeloaders.'",
    "features": [
      "Rented basement beneath Lonny and Mae's diner",
      "Residents: Jack, Teagan, Tucker (Ford Steele formerly)",
      "The one place visiting alphas are nobody's Alpha",
      "House law: disrespect Lonny or Mae and the whole basement alliance is 'on your ass like butter on toast'"
    ],
    "knownBy": [
      "teagan",
      "tucker",
      "lonny",
      "mae",
      "ford"
    ],
    "evidence": [
      "Authored app canon (Nicole, 2026-08-21) — character rows for teagan, tucker, lonny, mae; not in the Discord corpus on-page"
    ],
    "au_notes": "App-era canon added by Nicole with the Nashville wolves; the pre-app Discord corpus never shows the apartment on-page."
  },
  {
    "id": "lonny-and-maes-diner",
    "name": "Lonny & Mae's Diner",
    "world": "Nashville",
    "description": "The upstairs restaurant run by Lonny and Mae, an omega couple regarded as the sweetest, most welcoming people the misfit Alphas have ever met. The diner is a safe haven that 'doesn't hold people to ridiculous standards or expectations' — a peaceful, non-judgmental refuge for the neighborhood's outcasts, where Lonny greets guests in a warm southern drawl ('Welcome to m'place, youngin'') and Mae holds gentle motherly authority over her patrons ('You all be good now. You hear?'). Ford Steele refuses to freeload, slamming hundreds of dollars on the table for his meals; the basement Alphas below protect the couple absolutely.",
    "features": [
      "Upstairs from the basement apartment; the misfit Alphas' anchor",
      "Lonny: southern-drawl host and father figure; celebratory meals on the house for good news",
      "Mae: soft-spoken motherly authority; famous cooking",
      "House peace rule: no rowdy pack politics or drama inside",
      "Ford pays in cash stacks rather than accept free meals"
    ],
    "knownBy": [
      "lonny",
      "mae",
      "teagan",
      "tucker",
      "ford"
    ],
    "evidence": [
      "Authored app canon (Nicole, 2026-08-21) — character rows for lonny, mae, tucker, teagan; not in the Discord corpus on-page"
    ],
    "au_notes": "App-era canon added by Nicole; evidence quotes live in the characters' own authored rows."
  },
  {
    "id": "atlantis-kingdom",
    "name": "Atlantis (the Atlantic Siren Kingdom)",
    "world": "Siren world",
    "description": "The underwater kingdom of the Atlantic Ocean sirens, seated off the shore below the boardwalk town where its royal family also operates surface businesses. Ruled by the Miyamoto dynasty — the elderly Queen (grandmother) as Head Siren, with the crown passing to the heir around age twenty. Society is peaceful and law-abiding: accusations go to public trials, wrongdoers are imprisoned rather than routinely executed, and secrecy from humans is the cardinal rule. The kingdom's city holds ordinary siren homes with front doors, a castle at its heart, and civic venues. Its economy trades Atlantic coral to the Pacific for kelp, the staple food. Citizens reach the surface through the family aquarium and gated beach access points.",
    "features": [
      "Miyamoto royal family; heirs state intent to take or abstain from the throne by age 20",
      "Kelp imported from the Pacific in exchange for coral (kelp is the siren staple food)",
      "Public trials; the Great Clam serves as an impartial arbiter at royal trials",
      "Residential siren houses with doors in the kingdom's main city",
      "Air-filled carriage shuttles drawn by dolphins, seahorses, or sharks link the kingdom to the surface",
      "Magically built phones allow calls from the ocean to the surface",
      "Guards, advisors (the Fiati family serves the crown for generations), and a small standing army"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "dillon",
      "kyo",
      "kuroo",
      "evelyn",
      "emma",
      "haley_human",
      "skipper"
    ],
    "evidence": [
      "\"I've been Princess Kya Miyamoto of Atlantis my whole life.\" (kaleb-kya-finally)",
      "\"Your mother was born to take over the throne of Atlantis, but she abdicated the throne.\" (human-not-human)"
    ],
    "au_notes": "Kingdom naming varies by branch: the AU_SIREN channel siren-places calls it \"the Atlantic's main kingdom of Adlaran,\" and one early 2020 route (aquarium) once calls the family's realm \"Landris.\" Atlantis is the most common and current name. In the human-not-human branch the twins' mother abdicated and the twins were raised on the surface."
  },
  {
    "id": "atlantis-royal-castle",
    "name": "The Royal Castle of Atlantis",
    "world": "Siren world",
    "description": "The Miyamoto palace at the heart of the kingdom. Its throne room is where the Queen holds audiences and confers with family and advisors. The grounds include a coral garden of soft, colorful coral — Kya's favorite place — plus a main hall used for diplomacy lessons, a dining hall, an enormous kitchen full of staff, a formal meeting room where the royal advisor works, a common room, guest quarters watched by stationed guards, a ballroom for announcements and parties, a treasure room, a dance hall for galas, dressing rooms, storage closets, and dungeons below. Bedrooms are typically air-bubble rooms — air-filled chambers where sirens regain legs to sleep — with seashell beds, light-rock headboards, coral ceilings, and the smell of the ocean throughout. A water bell rings at the changing of each hour.",
    "features": [
      "Throne room where the Queen confers and receives visitors",
      "Castle coral garden with soft coral, Kya's favorite spot on the grounds",
      "Air-bubble bedrooms throughout (sirens sleep with legs); clothes hang inside them",
      "Seashell beds with headboards made of light rocks",
      "Advisory meeting room; the advisor Dillon/Dillan keeps papers and schedules there",
      "Treasure room, dance hall for galas, ballroom, enormous staffed kitchen, dining hall",
      "Guest quarters with guards stationed at the doors; dungeons; storage closets; security tapes",
      "Dressing rooms for royals; servants coordinated under the steward Topps",
      "A water bell rings in the castle at each new hour"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "dillon",
      "kyo",
      "kuroo",
      "evelyn",
      "emma",
      "haley_human",
      "polly",
      "topps",
      "madeline",
      "tanya",
      "blair",
      "drisella"
    ],
    "evidence": [
      "\"Here is the castle's coral garden. It's my favorite place here.\" (siren-places)",
      "\"their treasure room, dance hall where they hosted beautiful galas, various rooms, an enormous kitchen full of staff\" (aquarium-seb)"
    ],
    "au_notes": "The same palace appears under the Adlaran name in the AU_SIREN branch (siren-places), where additional details include a second air-bubble room made by the grandmother and an empty office off the halls. In fantastical-ezra (2025) the throne room is still in active use: \"Dillon is with grandmother in the throne room.\""
  },
  {
    "id": "royal-aquarium",
    "name": "The Royal Aquarium",
    "world": "Siren world",
    "description": "The family-owned aquarium on the boardwalk, both a beloved public attraction and the kingdom's chief surface cover — its enclosures open directly to the ocean and its lower passages lead down toward the sirens' home. All animals are paid volunteers who come in from the sea and leave when they wish; Skipper the dolphin is its show-stopping star. The building holds a dolphin tank with glass viewing sides, a feeding platform, and a restricted upper section, plus shark pools, walk-through water tunnels, a break room, an office, locker rooms, staff-only shortcuts between exhibits, and a keyed siren changing room marked authorized personnel only. Sirens on staff carry human identification and insurance, and the family also hires vetted humans who love marine life.",
    "features": [
      "Enclosures always open to the ocean — no animal held in captivity; animals are paid volunteers",
      "Skipper the dolphin: lazy, spoiled by Kya, an excellent judge of character",
      "Dolphin exhibit with glass-sided tank, feeding platform, stairs, and restricted top section",
      "Walk-through water tunnels and shark feeding stations",
      "Keyed siren changing room labeled authorized personnel only (cover story: confidential records)",
      "Break room, office, locker rooms, side door with laundry basket, staff-only shortcuts",
      "A water gate / fake exhibit and a metal climbing pole connect the building to the open ocean",
      "Hosts benefit concerts and events for the animals; sirens working there hold surface IDs and insurance"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "skipper",
      "emma",
      "dillon",
      "evelyn",
      "kyo",
      "ezra",
      "lira"
    ],
    "evidence": [
      "\"We own one of the aquariums on the boardwalk and it's a front for where we actually live\" (beach-and-boardwalk)",
      "\"We always keep the enclosures at our aquarium open to the ocean. No one is held in captivity.\" (beach-and-boardwalk)"
    ],
    "au_notes": "In the 2020 aquarium route, five large siren families live beneath the aquarium under the royal family's care and tanks connect to an underwater sea manor; in the 2025 aquarium-seb era it operates as a straightforward family business where human interns are hired."
  },
  {
    "id": "boardwalk-and-beach",
    "name": "The Beach & Boardwalk",
    "world": "Siren world",
    "description": "The seaside strip above the kingdom: a boardwalk of shops, food stands, and attractions — including the family aquarium and a Six Guys burger restaurant — running along a public beach. Streets are crowded in summer. The beach below is where sirens come ashore: they stash clothes near the waterline, dry off to hold human form, and slip back into the surf after dark. A dock with steps at its end serves as a quiet entry point to the water. Sirens lounge openly on the boardwalk among humans, and stretches of nearby sand are privately owned or gated for siren use. Kya grew up working, performing, and postering for aquarium benefits along this walk.",
    "features": [
      "Six Guys burger restaurant near the family aquarium; an ice cream shop; plentiful snack shops",
      "The dock: steps at the end where new sirens first enter the water",
      "Clothes-stashing spots on the sand for coming ashore",
      "Siren-only gated beach sections exist (entered by keypad code on the Pacific coast)",
      "Benefit posters and community events for the aquarium's animals",
      "Very crowded in summer; the surf shack and the cove lie close by"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "evelyn",
      "ezra",
      "emma",
      "polly",
      "raye",
      "skipper"
    ],
    "evidence": [
      "\"Kya was walking out on the boardwalk near the aquarium. She was putting up posters for an upcoming event that they were running. A benefit for the animals.\" (beach-and-boardwalk)",
      "\"The streets were very crowded since it was summer and everyone was enjoying the boardwalk.\" (siren-places)"
    ],
    "au_notes": ""
  },
  {
    "id": "six-guys",
    "name": "Six Guys",
    "world": "Siren world",
    "description": "A burger restaurant chain and the friend group's favorite eating spot. The best-known location stands on the boardwalk right near the family aquarium, and there is at least one similar location on the boardwalk near the Pacific prince's home territory overseas. It serves classic burgers and fries; Kya has favored it since her first visit with Evelyn, and it is a standard destination for casual meals up on the surface.",
    "features": [
      "Boardwalk location near the royal aquarium",
      "A second, similar location on the Pacific-side boardwalk",
      "Burgers and shareable fries; casual seating",
      "Long-running favorite of Kya and Evelyn"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "evelyn",
      "aaron"
    ],
    "evidence": [
      "\"Six Guys is my favorite. This one is right near my family's aquarium.\" (misc)",
      "\"I haven't been to this one, but there is one similar on the boardwalk by me.\" (misc)"
    ],
    "au_notes": ""
  },
  {
    "id": "the-cove",
    "name": "The Cove",
    "world": "Siren world",
    "description": "A hidden sea cave close to the aquarium, reached by a rocky, slippery path along a secluded part of the beach. Inside, crystal-blue water fills the cavern floor, moss softens some of the rocks while others stay clear enough to sit on, scattered plants grow in the crevices, and blue crystals line the walls. It is a popular siren hangout — notably free of litter — and local legend says whispers ride the wind there, voices of those long gone or wishes made long ago. The cove is tide-locked: when the tide rises, the entrance is blocked and the chamber nearly fills with water, so it is unsafe to visit alone.",
    "features": [
      "Entrance blocked at high tide; the cave nearly floods",
      "Crystal-blue interior pool with mossy and bare sitting rocks",
      "Blue crystals embedded in the cavern walls",
      "Legend of whispers carried on the wind — attributed by humans to sirens",
      "Popular, well-kept siren hangout spot near the aquarium"
    ],
    "knownBy": [
      "sebastian_siren",
      "emma"
    ],
    "evidence": [
      "\"When the tide rises, it blocks the entrance and almost fills the place.\" (aquarium-seb)",
      "\"There were even blue crystals surrounding the walls.\" (aquarium-seb)"
    ],
    "au_notes": ""
  },
  {
    "id": "hidden-reef",
    "name": "The Hidden Reef",
    "world": "Siren world",
    "description": "A concealed pocket of shoreline behind a row of bushes on the beach, screened from view on its other sides by rocks. Sebastian stumbled on it retrieving a lost volleyball, and word spread through the fish because he could not keep the discovery to himself. Its crystal-blue water hosts a thriving private reef: schools of multicolored fish, sea turtles, and manta rays glide near the surface, and dolphins occasionally drop by. It functions as a secret gathering spot for sea creatures and a prime snorkeling site, deliberately kept off the maps of ordinary beachgoers.",
    "features": [
      "Entrance hidden behind bushes; rocks block sightlines from the open beach",
      "Schools of multicolored fish, sea turtles, and manta rays near the surface",
      "Discovered by Sebastian while chasing a lost volleyball",
      "Now a known gathering spot among local sea creatures"
    ],
    "knownBy": [
      "sebastian_siren",
      "emma"
    ],
    "evidence": [
      "\"It was like their own coral reef, hidden away from the rest of the world.\" (aquarium-seb)",
      "\"He escorts her past a row of bushes and into a seemingly hidden part of the beach.\" (aquarium-seb)"
    ],
    "au_notes": ""
  },
  {
    "id": "glowing-grotto",
    "name": "The Glowing Grotto",
    "world": "Siren world",
    "description": "Kya's special place: an underwater grotto a few minutes' swim from the beach that opens into a large coral reef lit by fish glowing with luminescence. Turtles and dolphins swim through it, and Skipper is a regular. Kya brings only her most trusted companions here to meet her marine friends. She has also described a private version of the spot as a little club in the ocean that other sirens do not visit — a place claimed by the fish themselves, where her dolphin friends congregate.",
    "features": [
      "Grotto entrance opening into a wide coral reef",
      "Bioluminescent fish light the water",
      "Sea turtles and dolphins in residence; Skipper visits often",
      "Kya's personal refuge, shared only with trusted friends"
    ],
    "knownBy": [
      "kya_sg1",
      "skipper",
      "alistair"
    ],
    "evidence": [
      "\"the siren pulls him into a grotto in the water that opened up into a big coral reef. The reef was lit up with fish that glowed with luminescence in the water\" (beach-and-boardwalk)",
      "\"There's this special little club I know in the ocean where none of the other sirens go. It's a fish thing.\" (surf-shack)"
    ],
    "au_notes": ""
  },
  {
    "id": "surf-shack",
    "name": "The Surf Shack",
    "world": "Siren world",
    "description": "A casual sit-down eatery by the beach and boardwalk, a short walk from the aquarium. It has booths — including quiet corner booths suited to private conversations — and windows that look out on the ocean. Locals and students use it as a meeting spot to talk over food away from the noise of the boardwalk; conversations held discreetly there have covered everything from siren colony politics to inter-ocean relations.",
    "features": [
      "Booth seating with quiet corners for private talks",
      "Windows overlooking the ocean",
      "Short walk from the boardwalk and aquarium"
    ],
    "knownBy": [
      "kya_sg1",
      "raye"
    ],
    "evidence": [
      "\"Raye and Kya walked to the surf shack--not saying much of anything until they got there. They were sat at a booth in a quiet corner of the room\" (surf-shack)"
    ],
    "au_notes": ""
  },
  {
    "id": "siren-spa-resort",
    "name": "The Siren Spa & Resort",
    "world": "Siren world",
    "description": "A specialized resort run by sirens who left their colonies to live independent lives. Its pools are filled with genuine ocean water so sirens can stay hydrated and transform safely far from the sea — such resorts exist even in the hottest inland places, and the same independent sirens also build apartments adapted for siren life. The resort has a lobby with a front desk, public pools, and private rooms; Room 815 opens onto a large private ocean-water pool at least twenty feet deep, with a bathroom to the left and a surface bedroom to the right. Submerged at the bottom of the deep pool is a second, underwater bedroom furnished for guests in siren form. Spare swimwear is provided for non-siren companions.",
    "features": [
      "Run by colony-unaffiliated sirens; discreet, members welcome from any ocean",
      "Ocean-water pools for hydration and safe transformation",
      "Room 815: private ocean-water pool (about 20 feet deep), surface bedroom and bathroom",
      "A furnished bedroom submerged underwater at the bottom of the deep pool",
      "Connecting rooms available; spare swim clothes for non-siren guests",
      "Part of a wider network including siren-adapted apartments in dry regions"
    ],
    "knownBy": [
      "kya_sg1",
      "raye"
    ],
    "evidence": [
      "\"It's run by some sirens who aren't associated with any colonies.\" (siren-spa)",
      "\"the first thing in front of them was a large pool filled with ocean water. To the left was a bathroom and to the right was a bedroom.\" (siren-spa)"
    ],
    "au_notes": "Introduced through Raye, whose vampire family manages supernatural relations; Kya's grandmother had never told her such resorts existed."
  },
  {
    "id": "miyamoto-siren-hotels",
    "name": "The Miyamoto Siren Hotels (Europe)",
    "world": "Siren world",
    "description": "A network of specialty hotels owned by Kya's family across Europe, staffed entirely by members of the siren network. Staff meet arriving sirens from the sea, help them dry off, and dress them in appropriate human clothes; suitcases and covers are provided so guests pass as ordinary travelers. The Dublin flagship keeps a royal suite permanently empty for the family: it has a direct pipe out to the ocean, access to a deep siren pool, and below the surface rooms a full siren apartment — an underwater sitting room connected to an air-bubble bedroom, like the suites of the palace at home. The hotel also has a lobby, front desk, complimentary room service, and a business center.",
    "features": [
      "Family-owned specialty hotels throughout Europe; staff are all part of the siren network",
      "Arrival service: drying off, human clothes, and cover luggage for sirens coming in from the sea",
      "Dublin royal suite: direct ocean pipe, deep siren pool, underwater sitting room, air-bubble bedroom",
      "Royal suite reserved for the Miyamoto family; the Queen has stayed there once",
      "Business center and complimentary room service",
      "Serves as the reporting point for lagoon-dwelling sirens in the region"
    ],
    "knownBy": [
      "kya_sg1",
      "kaleb",
      "evelyn"
    ],
    "evidence": [
      "\"my family does own specialty hotels all over Europe\" (kaleb-kya-finally)",
      "\"It had a direct pipe out to the ocean and access to a deep siren pool if she needed some time underwater.\" (kaleb-kya-finally)"
    ],
    "au_notes": ""
  },
  {
    "id": "siren-hotel-lbi",
    "name": "The Siren Hotel (LBI)",
    "world": "Siren world",
    "description": "An exclusive, members-only hotel near the Long Beach Island boardwalk, literally named the Siren Hotel — a name that lets sirens speak of it openly. It is affiliated with the Pacific royal family: its royal suite is reserved for Prince Aaron, who can grant free stays to guests. Human companions of sirens may be lodged there in comfort while their friends adjust to ocean life.",
    "features": [
      "Members-only and very exclusive; openly named 'Siren Hotel'",
      "Royal suite reserved for Prince Aaron",
      "Near the LBI boardwalk and beach",
      "Comfortable lodging for human companions of sirens"
    ],
    "knownBy": [
      "aaron",
      "haley_human"
    ],
    "evidence": [
      "\"It was legitimately named siren hotel and it was known to be member only and very exclusive.\" (aaron-haley)",
      "\"You can stay at the siren hotel nearby if you two would like free of charge.\" (aaron-haley)"
    ],
    "au_notes": "Appears in the aaron-haley branch, in which Haley and Nicole take their annual beach trip to LBI."
  },
  {
    "id": "wysteria",
    "name": "Wysteria",
    "world": "Siren world",
    "description": "King Dimitri's kingdom in Europe and the destination of the great overseas trip. Grand in every particular, it is built in tiers: the Upper City, impressive in its own right, is still smaller than Inner Wysteria. Its territory holds vampire-owned restaurants that also cater to humans, luxury stores selling lavish jewelry, and, in the surrounding mountains, hills, and glens, the famous waterfalls and lagoons that draw sirens from across the oceans. Travelers reach it by road from Ireland — Kya's route began at the family's Dublin siren hotel and moved toward Wysteria within the week.",
    "features": [
      "Upper City of Wysteria — grand, but smaller than Inner Wysteria",
      "Everything built by Dimitri is described as grand",
      "Vampire-owned restaurants in the territory that also cater to humans",
      "Luxury stores with lavish jewelry",
      "Waterfalls and lagoons in the surrounding mountains and glens",
      "About a week's journey from Dublin on the trip itinerary"
    ],
    "knownBy": [
      "kya_sg1",
      "kaleb",
      "evelyn",
      "dimitri"
    ],
    "evidence": [
      "\"The upper city of Wysteria, though not as large as Inner Wysteria, was just as grand as anything else Dimitri built.\" (kaleb-kya-finally)",
      "\"I've studied the maps before and I know that there are beautiful waterfalls and lagoons in Wysteria. I'd love to see them all.\" (kaleb-kya-finally)"
    ],
    "au_notes": "Evelyn ordinarily avoids traveling to Wysteria; she flew in only for a major family occasion."
  },
  {
    "id": "wysteria-lagoons",
    "name": "The Lagoons & Waterfalls of Wysteria",
    "world": "Siren world",
    "description": "Freshwater lagoons beneath waterfalls in Wysteria's mountains, sacred and useful ground for sirens. Magical plants for siren potions grow there and cannot be found in the open ocean. Kind water nymphs live in the lagoons; they grant special gifts and wishes in exchange for siren scales, which they brew into potions to rejuvenate their sick — a siren can spare only a few scales, needing about two days between pulls. Under a full moon, a lagoon lets a visitor experience a memory of their past or future outside their own consciousness. Some sirens choose to live in lagoons: they follow the law of whichever ocean the lagoon borders and report through the nearest siren hotel.",
    "features": [
      "Water nymphs: peaceful hosts who trade wishes and gifts for siren scales",
      "Scales are brewed into rejuvenation potions for sick nymphs; ~2 days between safe pulls",
      "Full-moon visions: past or future memories experienced outside one's consciousness",
      "Magical plants for siren potions, unavailable in the ocean",
      "Lagoon-dwelling sirens follow the bordering ocean's rule and report via siren hotels"
    ],
    "knownBy": [
      "kya_sg1",
      "kaleb"
    ],
    "evidence": [
      "\"In lagoons you can actually experience one of your future or past memories outside of your consciousness every time the moon is full.\" (kaleb-kya-finally)",
      "\"They use our scales to make potions to rejuvenate their sick but they're peaceful people and so are we so we make an exchange.\" (kaleb-kya-finally)"
    ],
    "au_notes": ""
  },
  {
    "id": "pacific-kingdom",
    "name": "The Pacific Siren Kingdom",
    "world": "Siren world",
    "description": "The rival-turned-allied siren kingdom of the Pacific Ocean, off the California coast. Its royal family — King George and his Queen, crown prince Aaron, and the younger prince Andrew — rules from a large underwater castle that looks surprisingly human-built and gorgeous, part of a wider royal compound. The Pacific is the ocean's kelp superpower: kelp is its biggest export, traded to the Atlantic for coral. Its justice runs harsher than the Atlantic's — nasty sirens are imprisoned or executed immediately — though its people are kept free, happy, and well tended. The shore above includes a private, siren-only section of beach secured by a coded gate reader, and the family also keeps land homes and runs a cruise and shipping business.",
    "features": [
      "Large underwater castle in the Pacific with surprisingly human-styled architecture",
      "Royal compound; King George and the Queen rule, Aaron is heir, Andrew is the younger prince",
      "Kelp: the Pacific's biggest export, traded for Atlantic coral",
      "Swift justice: dangerous sirens imprisoned or executed immediately",
      "Siren-only private shore section with keypad-coded gate",
      "Magically built ocean phones; family land homes on the coast"
    ],
    "knownBy": [
      "aaron",
      "haley_human"
    ],
    "evidence": [
      "\"I live in my castle in the Pacific Ocean\" (aaron-haley)",
      "\"Kelp has been a staple of the siren diet for a long time. It's our main source of food and the Pacific ocean's biggest export. We trade coral for their kelp\" (aquarium-seb)"
    ],
    "au_notes": "Atlantic-Pacific relations vary by branch: tense in early routes, allied via arranged unification in misc, and warmly allied by the 2023-2025 era. In the weresirens AU the Pacific throne is held by siren king James, who also keeps an immaculate surface residence — \"we'll set ourselves up at my surface residence. Then tomorrow, I can bring you down into the ocean and we can explore the kingdom together.\""
  },
  {
    "id": "pacific-cruise-ships",
    "name": "The Pacific Royal Cruise Ships",
    "world": "Siren world",
    "description": "The cruise line and shipping fleet run by the Pacific royal family's company. To humans they are ordinary luxury liners; to sirens each ship is a convenient moving hotel. A secret compartment at the bottom of the hull lets siren crew and guests exit directly into the ocean mid-voyage, and the company runs employee-only sailings — siren-only in practice — that are calmer and far less crowded than public cruises. The company also handles general shipping and transportation, and recruits surface celebrities to advertise its cruise lines.",
    "features": [
      "Secret bottom-of-hull compartment for exiting into the ocean",
      "Employee-only (siren-only) sailings, quieter than public trips",
      "Functions as a moving hotel keeping siren staff near the water",
      "Part of a broader royal shipping and transportation business"
    ],
    "knownBy": [
      "aaron",
      "haley_human"
    ],
    "evidence": [
      "\"When they needed to go into the ocean, they could exit through the secret compartment at the bottom of the ship. It was a convenient moving hotel for them.\" (aaron-haley)"
    ],
    "au_notes": ""
  },
  {
    "id": "super-siren-cruise-ship",
    "name": "The Super Siren Cruise Ship",
    "world": "Siren world",
    "description": "The vessel Atlantis chartered for its friends-week diplomatic cruise in the post-reveal era, hosted by Princess Kya and Prince Sebastian with advisor Dillon coordinating. The ship was decked in ten thousand flower arrangements, gold embellishments, and nods to the crown of Atlantis from bow to stern. Guests were greeted in a receiving line at the main entrance with welcome baskets holding event schedules, menus, and itineraries. The main deck hosted a concert violin group with butlers circulating appetizers; downstairs, the bottom deck held ocean-water pools where siren guests waited in comfort. Vampire guests were provided for by paid blood donors — healed with siren magic between extractions — and chefs of blood-infused and hybrid cuisine. Once at sea, the outside world could not interrupt the event.",
    "features": [
      "10,000 flower arrangements, gold embellishments, crown-of-Atlantis motifs throughout",
      "Main entrance receiving line with welcome baskets (schedules, menus, experiences)",
      "Main deck: violin ensemble, butlers with trays of normal and blood-infused appetizers",
      "Bottom deck: ocean-water pools for siren guests",
      "Paid, magically healed blood donors and specialty chefs aboard for vampire guests",
      "Guest list restricted to Evelyn's and Kaleb's trusted friends; attendants assigned per guest"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "dillon",
      "evelyn",
      "kaleb"
    ],
    "evidence": [
      "\"gold embellishments, and nods to the crown of Atlantis all over the ship\" (super-siren-cruise-ship)",
      "\"They're waiting downstairs in the ocean water pools.\" (super-siren-cruise-ship)"
    ],
    "au_notes": "Set after 'the reveal,' when supernaturals became known to humans; Kya introduces herself openly as \"Princess Kya of Atlantis.\""
  },
  {
    "id": "undersea-wedding-venue",
    "name": "The Undersea Wedding Venue",
    "world": "Siren world",
    "description": "The kingdom's ceremonial venue beneath the sea, where royal weddings are traditionally held on display for everyone to see, even when arranged at short notice. It has formal doors and an aisle down which participants swim, with a priest officiating and crowds of citizens in attendance. The venue's regular bookings — including, canonically, a dolphin wedding — are willingly ceded when the royal family requires the space. Ceremonies there feature siren traditions such as the fisherman's knot, tied to symbolize a bond that grows stronger under pressure.",
    "features": [
      "Royal ceremonies traditionally open for all citizens to watch",
      "Formal aisle and doors; officiating priest",
      "Bookings (such as a dolphin wedding) ceded for royal use",
      "Fisherman's knot tradition — knot-tying is taught to all young sirens as evasive training"
    ],
    "knownBy": [],
    "evidence": [
      "\"Siren royal weddings tended to be on display for everyone to see, even when it was last minute. The undersea wedding venue had no issue clearing out their plans\" (siren-places)"
    ],
    "au_notes": "Described in the AU_SIREN branch (siren-places), where the kingdom is called Adlaran."
  },
  {
    "id": "underwater-sea-manor",
    "name": "The Underwater Sea Manor",
    "world": "Siren world",
    "description": "In the earliest 2020 branch, the sirens' home is not a grand palace but an underwater sea manor below the aquarium, where a handful of large siren families live together under the royal family's care, led at the time by the twins' uncle as acting head. Residents pass between the manor and the surface through the aquarium's tanks and water gate. The manor sits down in the deeper water off the shore; swimming down to it at night, the ocean appears bright to siren eyes even under a dark sky, with little fish and seahorses greeting travelers on the descent.",
    "features": [
      "Home to five large siren families under the royal family's care",
      "Reached through the aquarium's water gate to the ocean",
      "Family quarters and communal living beneath the sea",
      "Kya and Lira return there together after surface shifts"
    ],
    "knownBy": [
      "kya_sg1",
      "sebastian_siren",
      "kyo",
      "lira"
    ],
    "evidence": [
      "\"There were five large siren families living at the aquarium under her family's care.\" (aquarium)",
      "\"They swim through and travel down to the underwater sea manor they lived in with their families.\" (aquarium)"
    ],
    "au_notes": "Early 2020 vampire-era branch only; in this route the realm is once referred to as Landris, and later branches replace the manor with the full castle of Atlantis."
  },
  {
    "id": "naga-kingdom",
    "name": "The Naga Kingdom",
    "world": "Siren world",
    "description": "A kingdom named but never described: a small number of Nagas — an ocean-dwelling, snake-like people whose bodies and even skin color change fully in the water — hold their own kingdom near the Atlantic sirens' territory. Its royal family includes Dr. Midori Naga, a surface-practicing physician. Nothing else about the kingdom itself is shown in the corpus.",
    "features": [
      "Located near the Atlantic siren kingdom",
      "Populated by Nagas: snake-tailed ocean dwellers who transform fully in water",
      "Home kingdom of the royal physician Dr. Midori Naga"
    ],
    "knownBy": [
      "midori_naga"
    ],
    "evidence": [
      "\"Her grandmother did mention that there was a small number of Nagas that had their own kingdom near to theirs.\" (siren-places)",
      "\"Naga's were another species that tended to live in the ocean. They were snakelike with their tails\" (siren-places)"
    ],
    "au_notes": "Mentioned only in the AU_SIREN branch (siren-places)."
  },
  {
    "id": "numerian-tribe",
    "name": "The Numerian Tribe",
    "world": "Siren world",
    "description": "A place and people named but never described: the Numerian tribe is a neighboring group with whom the Atlantic crown schedules diplomatic meetings — important enough that the royal advisor juggles the princess's lesson schedule to avoid offending them. Nothing else about the tribe or its territory is shown in the corpus.",
    "features": [
      "Neighboring tribe on the Atlantic crown's diplomatic calendar"
    ],
    "knownBy": [],
    "evidence": [
      "\"If we push back Kya's coronation lesson, then we can add in meeting with the Numerian tribe so they are not upset with how long it will take.\" (siren-places)"
    ],
    "au_notes": "Mentioned only in the AU_SIREN branch (siren-places)."
  },
  {
    "id": "ezra-private-beach",
    "name": "Ezra's Private Beach",
    "world": "Siren world",
    "description": "A gorgeous little cove of privately owned beach not far from Ezra's establishment, purchased by him years ago. Access is strictly by his permission — he is the only one allowed there by default, and others must ask expressly. The cove's clear water and quiet sand make it a favored retreat; Kya had swum along this stretch of coast before and admired it long before learning who owned it. Sirens in the water can sense anyone else approaching through the waves, making it one of the most discreet swimming spots on the coast.",
    "features": [
      "Privately owned; entry only with Ezra's express permission",
      "Quiet cove suitable for open siren swimming",
      "Approaching swimmers can be sensed through the water",
      "Near Ezra's establishment on the coast"
    ],
    "knownBy": [
      "ezra",
      "kya_sg1"
    ],
    "evidence": [
      "\"Ta-da. My own personal beach. Are you impressed?\" (beach-and-boardwalk)",
      "\"I've been along here before and always thought it was a gorgeous little cove.\" (beach-and-boardwalk)"
    ],
    "au_notes": ""
  },
  {
    "id": "von-dabb-castle",
    "name": "The Von Dabb Castle",
    "world": "Vampire world",
    "description": "Seat of King Bryce Von Dabb, 'the Demon King,' ruler of the vampire Commonwealth. Guards line up in two straight rows outside against constant assassination attempts, and the two great front doors open by the telekinesis Kings develop over their reign. Inside are kitchens, a grand living room, servants' quarters, a dining room nearly as grand as the counsellor chambers where guards present willing human blood sacrifices, a forbidden brothel wing, a ten-room harem wing for the Queen, and dungeons whose head-guard office sits at their entrance beneath a wall of camera monitors covering every room except the King's quarters and bathrooms. The counsellor chambers seat thousands — stands for subjects, a royals' box, and two thrones — where the Commonwealth's slave-trade, auction-house, and black-market business is decreed. Balls follow every sporting day at the outdoor arena; royal weddings are held in the gardens at night.",
    "features": [
      "Counsellor chambers: 'a large room filled with stands for subjects and two thrones at one end of the room'; a scepter slam silences thousands",
      "Main entry with two semi-circular staircases, pianist and cellist at their feet; blood-bonding ceremonies held at the top with a golden cup and Latin incantation",
      "Brothel wing Evelyn was 'not permitted to visit'",
      "Harem wing 'with 10 rooms' set up for the Queen's consorts (one wall bears a hole Evelyn punched)",
      "Head guard's office 'near the dungeon's entrance' with camera monitors of the whole castle except the King's quarters and bathrooms; cells hold prisoners for the Auction (captured faeries, witches, warlocks)",
      "Dining room where only fresh blood is drunk — 'They do not have connections to blood banks here and it is frowned upon'",
      "Outdoor arena for mandatory 'sporting day' games and challenges of strength; a ball is held after every sport event",
      "Gardens visible from the King's quarters; wedding aisle set up through the main hall's back doors",
      "Tutoring room with table where the Queen-to-be is schooled in Commonwealth law and etiquette"
    ],
    "knownBy": [
      "bryce",
      "evelyn",
      "frau",
      "kya_sg1"
    ],
    "evidence": [
      "\"Everyone met in the castle's counsellor chambers for meetings--a large room filled with stands for subjects and two thrones at one end of the room.\" (the-von-dabb-castle)",
      "\"guards start lining up outside in two straight lines to barricade their way to the door in welcome\" (the-von-dabb-castle)"
    ],
    "au_notes": "Primary source is the arranged-marriage Bryce route ('Happy End') in which Evelyn Han becomes his Queen and Frau Von Abendroth dies after a failed poisoning. A later route states that after Bryce's death the Commonwealth passed to Rio, then to Geo (meeting-room)."
  },
  {
    "id": "bryces-quarters",
    "name": "Bryce's Quarters",
    "world": "Vampire world",
    "description": "King Bryce's private chambers on the upper floor of the Von Dabb castle — the one room in the castle with no surveillance. A grand king-sized bed dominates the room, with a closet of dresses the King selects himself, an attached restroom, and a table set before windows overlooking the castle gardens, where Bryce sits alone at night watching the moon; he sometimes imagines a woman's figure dancing in the moonlight below. He shuts the lights off by telekinesis. Evelyn lives here as his intended Queen, readied each morning by servants; the strict governess Frau Von Abendroth and later the red-haired Celine oversee her dressing for court occasions such as the blood-bond ceremony.",
    "features": [
      "Windows over the gardens: Bryce 'sat at a table situated in front of the windows overlooking the castle's gardens'",
      "The only room (with the bathrooms) excluded from the head guard's camera wall",
      "King-sized bed, closet, restroom; servants' makeup and hair station set up for the Queen-to-be",
      "Celine: lady-servant who prepares Evelyn when Frau is unavailable; her husband died by the King's hand after an attempt on his life"
    ],
    "knownBy": [
      "bryce",
      "evelyn",
      "frau",
      "celine"
    ],
    "evidence": [
      "\"King Bryce was sitting in the corner of his room, sat at a table situated in front of the windows overlooking the castle's gardens.\" (bryces-quarters)",
      "\"The man uses telekinesis to shut off the lights and he slips into bed.\" (bryces-quarters)"
    ],
    "au_notes": "Belongs to the same arranged-marriage Bryce route as the-von-dabb-castle."
  },
  {
    "id": "hall-of-portraits",
    "name": "The Hall of Portraits",
    "world": "Vampire world",
    "description": "A secret room down a long corridor in a shadowy corner of the Von Dabb castle, hidden behind an unmarked door. Its long walls carry thousands of painted portraits of women with sleeping faces — Bryce's victims, painted by his own hand since he was old enough to understand what he had become. In the middle of the main wall hangs a larger portrait of the former Queen, his mother, with kind eyes, long dark hair and a crown; beneath her is a smaller portrait of his father, King Reginald. A table covered in candles stands at the room's center, which Bryce lights when he enters. The floor is bare rock. Before Evelyn, the only living soul ever admitted was Bryce's mute maid Rebekah, whose tongue was removed — and whose roommate is Frau Von Abendroth.",
    "features": [
      "Thousands of portraits of Bryce's victims, self-painted",
      "Central portrait of the former Queen (Bryce's mother); smaller portrait of King Reginald below it",
      "Candle-covered table at the center; hard rock floor",
      "Known only to Bryce, his mute maid Rebekah, and — by eavesdropping — Frau"
    ],
    "knownBy": [
      "bryce",
      "evelyn",
      "frau"
    ],
    "evidence": [
      "\"On the wall there were thousands of painted portraits of women with sleeping faces.\" (hall-of-portraits)",
      "\"All of the portraits on the wall are my...victims. I started painting them when I became old enough to understand what I was doing.\" (hall-of-portraits)"
    ],
    "au_notes": "Same Bryce route; the reveal of this room triggers Frau and Rebekah's plot against the King."
  },
  {
    "id": "the-commonwealth-spa",
    "name": "The Spa",
    "world": "Vampire world",
    "description": "A luxurious day spa inside Bryce's kingdom, staffed for royalty — the whole establishment can be reserved for a royal party so visits go uninterrupted. Guests are met at a front desk (by the vampiress Estella, long black hair and blue eyes, during Evelyn's visit) and handed towels and robes; a locker room lies to the left inside, with massage chairs, facial treatment rooms and hot tubs beyond. A bell on the spa floor summons any additional service: massages, blood sacrifices, facials, nail services, tan treatments, staff hired in from the local brothel, private rooms in the back, and a house lingerie line. It was here that Estella explained the Commonwealth's harem law to Evelyn: members are bound by their marks 'for as long as their marks last.'",
    "features": [
      "Front desk with reservation of the entire spa for royals",
      "Locker room, facial rooms, massage chairs, hot tubs",
      "Service bell on the spa floor; 'special rooms in the back'",
      "Site of the harem-contract law exposition"
    ],
    "knownBy": [
      "evelyn"
    ],
    "evidence": [
      "\"Welcome to the spa, your future majesty... We have the whole day spa reserved for yours and your harem's use today, so you need not worry about interruptions.\" (the-spa)",
      "\"If you require any additional services during your stay you need only ring the bell on the spa floor.\" (the-spa)"
    ],
    "au_notes": "Bryce-route location; in that route Frau books the visit as cover for her move against the King."
  },
  {
    "id": "evelyns-mansion",
    "name": "Evelyn's Mansion",
    "world": "Vampire world",
    "description": "Evelyn's own mansion residence, run by a full household staff who line up and curtsy at the door while butlers hold it open. Its 2016-era staff roster is the definitive one: Marta, 'a young red-headed Swedish girl with a shy disposition'; the governess Frau Von Abendroth, 'shipped in from Germany twenty years earlier to double as an advisor'; Ivy, 'a stubborn brunette from America with a Southern Accent and no filter to speak of'; and quiet young Mia, who barely raises her eyes. The mansion holds an exquisite dining hall, kitchen staff, televisions in many rooms, CCTV, a guest room, and Evelyn's own suite upstairs with closet, dresser, nightstand and a restroom 'equipped for just about any situation.' Business meetings with her partner Kings (Alistair, Ezra, Raye) convene in the living room.",
    "features": [
      "Door greeting ritual: 'Three maids and a governess were lined up by the door and curtsied as she walked by'",
      "Staff: Marta (shy Swede), Frau Von Abendroth (German governess-advisor), Ivy (blunt American), Mia (quiet youngest)",
      "Frau's maxim: 'the earlier you are, the better you look, the better you look, the better you are'",
      "Exquisite dining hall; kitchens; CCTV; guest room; security posts at bedroom doors when needed",
      "Living room used for meetings of Evelyn's allied business partners"
    ],
    "knownBy": [
      "evelyn",
      "frau",
      "marta",
      "mia",
      "kya_sg1",
      "seven",
      "ezra",
      "alistair",
      "cruz"
    ],
    "evidence": [
      "\"Three maids and a governess were lined up by the door and curtsied as she walked by, greeting her individually.\" (evelyns_mansion)",
      "\"Ivy was a stubborn brunette from America with a Southern Accent and no filter to speak of.\" (evelyns_mansion)"
    ],
    "au_notes": "The staffed-household scenes come from a human-era Mystic Messenger-flavored route (heiress Evelyn, Prime Minister plot) and from war-AU scenes; in vampire-era routes Evelyn's residence merges with the Han family mansion."
  },
  {
    "id": "han-family-mansion",
    "name": "The Han Family Mansion",
    "world": "Vampire world",
    "description": "Ancestral home of the Han vampiric family — King Han, his son Jumin, his niece Evelyn (second in line and only female heir), and Evelyn's mother — staffed by maids including the governess Frau and Marta, one of Evelyn's prior maids. The mansion's large entry hall hosts allied-family meetings of hundreds of vampires, presided over from the grand staircase; King Han's office opens off the interior, and the front room downstairs serves for meetings Jumin holds from home, with weekly Saturday-morning sessions. A dining room dispenses blood bags, a changing room can be prepared for turnings, and Evelyn's bedroom lies upstairs. Kings Sune, Turayne, Ettore (Ezra) and Alistair have all stood in its halls; the family motto is 'My blood for the family.'",
    "features": [
      "Entry hall for allied meetings: 'hundreds of people... scattered throughout the large mansion's entry hall'",
      "Grand staircase from which King Han addresses the assembly",
      "The front room downstairs 'where Jumin held all of his meetings from home'; weekly Saturday-morning meetings",
      "King Han's office; dining room with blood bags; prepared turning/changing room; Evelyn's bedroom",
      "Maids Frau and Marta; drive and front doors where guests are announced",
      "Canon fact recorded here: Dimitri runs a black market Evelyn wants shut down, alongside the werewolf fighting rings"
    ],
    "knownBy": [
      "evelyn",
      "kya_sg1",
      "ezra",
      "alistair",
      "seven",
      "frau",
      "marta"
    ],
    "evidence": [
      "\"...burst into the Han's mansion to find... hundreds of people she didn't know scattered throughout the large mansion's entry hall\" (han-family-mansion)",
      "\"they enter the front room downstairs where Jumin held all of his meetings from home\" (evelyn-mansion)"
    ],
    "au_notes": "Appears across many routes (Seven route, war AU, body-swap route). The Han family ranks second among the allied families behind the Turaynes."
  },
  {
    "id": "king-hans-office",
    "name": "King Han's Office",
    "world": "Vampire world",
    "description": "King Han's private office inside the Han family mansion — a plainly adorned room dominated by his desk, where the head of the Han family does paperwork and receives family members one at a time. It is where he informed Evelyn of her arranged marriage to King Bryce of the Von Dabb family, and where, the next morning, Bryce waited to mark her: by tradition the departing bride must be marked before a witnessing King, for until she is marked she still belongs to her family, and taking her earlier 'would be considered abduction of a high ranking family member.' King Han is described as 'the most influential of all of the vampires within the allied families,' and his office is the room where that influence is exercised.",
    "features": [
      "Plainly adorned room with the King's desk",
      "Site of the witnessed marking tradition binding arranged marriages",
      "Where Evelyn later begged readmission to the family after her exile"
    ],
    "knownBy": [
      "evelyn",
      "bryce"
    ],
    "evidence": [
      "\"King Bryce was waiting in King Han's office for the girl to arrive. He looked uninterested in the plainly adorned room and his stare was cold.\" (king-hans-office)",
      "\"He was the most influential of all of the vampires within the allied families and demanded great respect from all of them.\" (king-hans-office)"
    ],
    "au_notes": "The marking scene belongs to the Bryce arranged-marriage route; the office recurs in other routes as the seat of Han family discipline."
  },
  {
    "id": "ezras-brothel",
    "name": "Ezra's Brothel (Nobu's)",
    "world": "Vampire world",
    "description": "Flagship establishment of King Ezra Ettore in the red light district — his business card names 'Nobu's, a popular red light district building in the area' — a plain, unassuming building outside and a protected family home inside. The entrance lounge has couches where the workers relax between appointments; three hallways converge on a main hall with a front counter where bills are settled (credit cards accepted). There is a kitchen stocked with human food, a restaurant, a theatre room with a stage, a curtained 'Valentine's Day Room' for occasions, a small dark meditation room 'reserved for mediation and self-care,' soundproof private rooms with inside locks, and gardens out back kept by Lilly the garden nymph. Ezra maintains 'branches nearby every major vampire family in the area,' each themed for its local crowd. The house rules are absolute: nobody does more than they are comfortable with, everyone may back out, confidentiality is total, and 'We are a family here and we protect each other.'",
    "features": [
      "Entrance lounge with couches; main hall with front counter; three converging hallways",
      "Temporary madame Primrose, a succubus — 'the head mother in a way'; the Ettore Queen traditionally becomes Madame",
      "Kitchen with human food, restaurant, theatre room with stage, Valentine's Day Room, meditation room, soundproof rooms",
      "Gardens tended by Lilly the garden nymph — 'she makes the back look gorgeous'",
      "A specialized turning room Ezra designed: blood-stocked fridge, exterior-locked door opening on his command, walls lined with scent-blocking herbs",
      "A small office displaying a completed demon knife on the wall and a picture of Ezra's mother",
      "A special detention hallway where the Ettore family keeps most of its interrogated prisoners 'other than at Alistair's castle', with an observation cubicle, intercom, and a five-lock door",
      "Rules of the house: consent and comfort limits, refunds/reassignment, total confidentiality of clients and workers, protection of anyone under Ezra's roof; it is also the only allied house safe to enter without knocking",
      "Staff children may train as Ezra's guards — Ace Rowlings, son of the succubus Rosie, is his best"
    ],
    "knownBy": [
      "ezra",
      "kya_sg1",
      "evelyn",
      "ace",
      "rosie",
      "seven"
    ],
    "evidence": [
      "\"My name is Primrose. I'm the temporary madame of the brothel, that means I'm the head mother in a way.\" (brothel)",
      "\"We have branches nearby every major vampire family in the area\" (brothel)"
    ],
    "au_notes": "Consistent across routes; in the Kya-Ezra route it becomes Kya's home as Queen. Ezra grew up here as the bastard son of the former owner (a retired King) and the brothel worker Jane, a witch."
  },
  {
    "id": "the-containment-cells",
    "name": "The Containment Cells",
    "world": "Vampire world",
    "description": "The allied vampires' detention apparatus, split across two facilities. At Alistair's castle, prisoners of rank are held not in the common cells but in a 'containment room' he sets up — furnished like an ordinary bedroom with a bed and even a potted tree so a detainee may not realize it is a cell, its walls laced with herbs that prevent a werewolf from transforming, with guards posted outside the door. Interrogation itself is the Ettore family's jurisdiction: Ezra and his personally trained guards, foremost Ace Rowlings, work the cell blocks, all of them turned vampires 'to give them strength and immortality.' A separate dark cell block holds captured 'virens' — vampire-turned sirens (Ezra's coinage) — who uniformly know nothing and feel compelled to 'go to the border,' an unsolved mystery. Vampire law caps such detention: trespass earns only a warning, and suspicion of spying holds a prisoner about a week at most.",
    "features": [
      "Containment room 'instead of the cells--and it was at least a step up from that', furnished to disguise its purpose",
      "Herb-laced walls that block werewolf transformation",
      "Ezra's viren cell block: dark cells visited one by one for questioning; prisoner Lira, Kya's siren friend, among them",
      "Legal limits: first-offense trespass = warning; suspected spying = about a week's hold",
      "Ace Rowlings, 'one of Ezra's personally trained torture guards', son of the brothel succubus Rosie"
    ],
    "knownBy": [
      "alistair",
      "ezra",
      "ace",
      "kya_sg1",
      "evelyn"
    ],
    "evidence": [
      "\"Alistair set it up for Evelyn to stay in instead of the cells--and it was at least a step up from that.\" (containment-cells)",
      "\"The man was one of Ezra's personally trained torture guards—Ace Rowlings.\" (containment-cells)"
    ],
    "au_notes": "The Evelyn scenes belong to the war AU (Evelyn as turned werewolf); the viren cells belong to the Kya-Ezra route's border mystery arc."
  },
  {
    "id": "mother-ettores-house",
    "name": "Mother Ettore's House",
    "world": "Vampire world",
    "description": "The home of Jane, Ezra's mother — 'quant and cute, resting about 10 minutes away from the brothel.' Inside are a sitting room with chairs, a stove and kettle for tea, and shelves of family albums including one titled 'Ezra baby pics ages 0-3.' Jane looks scarcely older than her son but is a witch of roughly five hundred years who 'could make a whole room full of people bend to her will'; she was once a Queen — 'she still is technically and she doesn't like people to forget it' — and is reputed to have killed a thousand people for love. Warm and doting to guests, she can flood the room with an icy aura if her powers are doubted, and can read truths (including a pregnancy) through a touch. Ezra brought Kya here to introduce his fiancée and to seek Jane's magical counsel on the vanishing sirens.",
    "features": [
      "Ten minutes from the brothel; quaint cottage exterior",
      "Sitting room, stove and kettle, baby photo albums",
      "Jane: witch, former Queen, ~500 years old, senses truths by touch, icy aura when slighted"
    ],
    "knownBy": [
      "ezra",
      "kya_sg1"
    ],
    "evidence": [
      "\"Her house was quant and cute, resting about 10 minutes away from the brothel.\" (mother-ettore-house)",
      "\"she used to be a Queen...she still is technically and she doesn't like people to forget it.\" (mother-ettore-house)"
    ],
    "au_notes": "From the Kya-Ezra engagement arc tied to the siren-disappearance mystery."
  },
  {
    "id": "the-immortuos",
    "name": "The Immortuos",
    "world": "Vampire world",
    "description": "The elite institution where the heirs of the great vampire families are trained — 'We're all vampires training to be the future leaders. We're immortuos.' In the 1982 timeline it operates as a boarding school in London: a campus with an older gothic library, a gated entrance where visitors must be signed in, an admissions office issuing guest passes under official seal, hallways patrolled by staff, and student quarters that are 'more like miniature apartments,' each elite keeping their own. The dining hall doubles as the only sanctioned feeding place and contains a red-velvet-roped VIP table reserved for the headmaster's family. Nearly every student has a relative on staff, since only high vampire families may teach their heirs: Chancellor Boosta patrols the halls, the headmaster is James's father, and part-god Uncle Festus runs admissions. Young Val, Vanessa, Sebastian, and James all trained here.",
    "features": [
      "Older gothic library",
      "Dorm 'miniature apartments' with guest bedrooms",
      "Dining hall with roped-off VIP section for the headmaster's family; feeding allowed 'only in the cafeteria'",
      "Admissions office (Uncle Festus, 'part vampire and part Greek god'), gate sign-in, guest passes",
      "Staffing rule: 'they take family members of high up vampiric families around the world and employ them here'",
      "An elite chef caters the school and owns a nearby vampire-frequented restaurant"
    ],
    "knownBy": [
      "val",
      "vanessa",
      "kya_sg1"
    ],
    "evidence": [
      "\"You're a siren, right? We're all vampires training to be the future leaders. We're immortuos.\" (immortuos)",
      "\"It was more like miniature apartments\" (immortuos)"
    ],
    "au_notes": "Seen in a 1982 time-jump arc (Kya arrives via the trident); the adult Immortuos of the present day are the elder council met in elders-layla and val."
  },
  {
    "id": "elders-meeting-house",
    "name": "The Elders' Meeting House",
    "world": "Vampire world",
    "description": "Where the immortal elder council of the vampires convenes — Val, Vanessa Sinclair, James, Junta, Tim, and Sebastian among them. Guests are greeted at the door (Tim received Alistair and Kya) and brought into the council chambers, where candidates for elderhood are sworn in 'as if they were a human court system,' Val administering the oath with a book in hand; a hallway outside gives the shaken somewhere to sit. The elders keep no fixed seat: their annual summer meeting has been held in Florida ('We are not zombies. They are in Texas.'). Elders are voted in once old enough, while the main families are born to power; Vanessa (~1,300) is the youngest elder and Val (~2,600) among the eldest working members. Every elder carries a knife capable of killing a vampire.",
    "features": [
      "Council chambers where elder candidates are interviewed and sworn in",
      "Door greeting by a hosting elder; hallway outside the chambers",
      "Annual summer meeting at rotating locations (Florida attested)",
      "Elder lore: voted positions, extreme ages, knives that can kill vampires",
      "A private elders' meeting was once interrupted by the Celestial elf princess Layla teleporting in"
    ],
    "knownBy": [
      "val",
      "vanessa",
      "alistair",
      "kya_sg1"
    ],
    "evidence": [
      "\"Tim greeted the two of them at the door with a smile. 'Hello. Alistair, it's nice to see you and this must be your Queen, Kya. Please, come in.'\" (val)",
      "\"You interrupted a **private** meeting of elder vampires, literally appearing into the middle of all o[f us]\" (elders-layla)"
    ],
    "au_notes": "The elder roster is consistent across the Val route and the Layla arc; Junta and James appear as the harshest members."
  },
  {
    "id": "vals-house",
    "name": "Val's House and Private Library",
    "world": "Vampire world",
    "description": "Home of Elder Val, the Bookman, shared with his sister Vanessa — large enough that Kya mistakes its grounds for a castle, with guest rooms ('The bed was large with two nightstands at each end of the bed. There was a white dresser across from it. A very bare bone guest room since the two of them didn't get many important guests'), gardens reached by a dedicated door, guards, hidden knives stashed through the house, and a corner bar with a kettle for blood tea. Its heart is Val's working library of bookkeeper records: 'I have plenty of books written about the basics of each species,' and beyond that the sacred texts, which by law 'only me or my partner are able to see... or be in the same room as them.' Bookkeepers record every meeting verbatim in its original language — 'we know them all, even dead languages' — and lay their books on the table when elders convene.",
    "features": [
      "Shared sibling home of Val and Vanessa; bare-bones guest room; gardens and a garden door",
      "Sacred texts restricted to the Bookman and his partner",
      "Bookkeeper craft: verbatim records, no translation, all languages living and dead",
      "Knives stashed for defense; guards on the house",
      "Corner bar with kettle — blood tea served to hostile guests (bookkeepers Tyrion and Alan)"
    ],
    "knownBy": [
      "val",
      "vanessa",
      "kya_sg1"
    ],
    "evidence": [
      "\"I would take you up on your offer, but I'm not allowed to at this moment. Only me or my partner are able to see most of the sacred texts or be in the same room as them.\" (elders-layla)",
      "\"There's a reason even I have knives stashed away in my own home.\" (val)"
    ],
    "au_notes": "In the Layla arc the siblings' house has 'more than enough rooms'; in the Kya-Val route the same household is the base from which Val defies the overseas bookkeepers Tyrion and Alan."
  },
  {
    "id": "the-vampire-museum",
    "name": "The Vampire Museum",
    "world": "Vampire world",
    "description": "A grand museum of vampire history maintained by the bookkeeper line — 'a very grand looking building but it was located very remotely in the middle of the woods,' its remoteness deliberate since its contents are for supernatural eyes. Val brings favored guests here to walk them through the recorded past of his people; its exhibits include the story of a man who sought immortality and 'did not touch his wife,' among other recorded lives. It functions as the public-facing face of the archive whose deepest layer — the sacred texts — remains locked to all but the Bookman and his partner.",
    "features": [
      "Grand building hidden remotely in the woods",
      "Exhibits drawn from bookkeeper records of vampire history",
      "Used by Val as a courting and teaching ground"
    ],
    "knownBy": [
      "val"
    ],
    "evidence": [
      "\"It was a very grand looking building but it was located very remotely in the middle of the woods.\" (elders-layla)"
    ],
    "au_notes": "Appears in the Layla arc; complements Val's private library."
  },
  {
    "id": "alistairs-castle",
    "name": "Alistair's Castle",
    "world": "Vampire world",
    "description": "The genuine castle where King Alistair Turayne lives alone — a rarity even among vampire royalty: 'Alistair and some of the older Kings like the aesthetics of castles. Most of us don't live in castles.' Guests knock at the castle doors; inside are a couch-furnished hall where the allied Kings meet, a library of many books (to which Alistair added a television for Kya's sake), maids who serve burgers after every long meeting — 'After every meeting we try to eat' — a downstairs kitchen stocked with blood packs, and the shared royal chambers upstairs. For Kya's turning he blocked up every window in the castle and dimmed the lights, keeping a two-way radio at her bedside. His father, the former King Arthur Turayne, stays over after formal occasions. The castle also holds prisoners: the Ettores keep their interrogated captives at the brothel 'other than at Alistair's castle.'",
    "features": [
      "A true castle kept by one King: 'Most of us don't live in castles'",
      "Library with book lists prepared for guests (and a grudging television)",
      "Post-meeting meals served by maids; downstairs kitchen with blood packs",
      "Every window blockable for a newborn vampire's light-sensitive first days; bedside two-way radio",
      "Detention capacity paired with the Ettore brothel's prisoner hallway",
      "Court protocol: 'You are required to earn your place in the court'"
    ],
    "knownBy": [
      "alistair",
      "kya_sg1",
      "ezra",
      "seven",
      "evelyn"
    ],
    "evidence": [
      "\"This was a castle. An actual. Real life. Castle.\" (val)",
      "\"Alistair and some of the older Kings like the aesthetics of castles. Most of us don't live in castles.\" (val)"
    ],
    "au_notes": "Alistair's surname varies by route — 'King Alistair Turayne' (strongest allied family, father Arthur/King Turayne) in most; 'Alistair Cruorem, first in line for the Cruorem family' in the body-swap route (meeting-room)."
  },
  {
    "id": "allied-meeting-room",
    "name": "The Allied Meeting Room",
    "world": "Vampire world",
    "description": "The formal meeting room of the allied vampire families, where each family holds a designated section of seats and the reigning strongest King presides — King Han in most eras, Alistair Turayne after his family's ascent. Votes are taken openly ('80% of people are in favor'), the eldest speaks first when several vampires wish to talk, and dueling auras substitute for raised fists. The rooms are deliberately unfit for secrets: 'The meeting rooms are being watched for safety,' so private words move to homes or the supply closet just off the hall. A smaller kings'-circle room with a couch and a bar 'stocked... full of blood' serves for introductions among the inner circle (Jumin, Alistair, Ezra, Raye, and their partners), and an adjoining chamber hosted the volatile elf negotiations between Prince Zain of the light elves and Cal, prince of the Drow.",
    "features": [
      "Family-sectioned seating; presiding King; formal votes",
      "Speaking tradition: 'when multiple vampires want to speak, the eldest takes priority'",
      "Surveillance of all meeting rooms; supply closet in the hall for asides",
      "Kings'-circle room with couch and blood bar for introductions",
      "Elf negotiation chamber (Zain and Cal); the orcs' meeting hall was once destroyed by fighting elves (named only)",
      "Where Project Alpha — turning captured werewolves into vampire soldiers — was voted through in the war AU"
    ],
    "knownBy": [
      "kya_sg1",
      "ezra",
      "alistair",
      "evelyn",
      "seven"
    ],
    "evidence": [
      "\"It's not a safe place to speak. The meeting rooms are being watched for safety. We can meet up at my house or yours. It's the safest.\" (meeting-room)",
      "\"As tradition states, when multiple vampires want to speak, the eldest takes priority.\" (meeting-room)"
    ],
    "au_notes": "Recurs in nearly every vampire route; the war-AU sessions (Project Alpha, the war votes) and the body-swap-era introductions are its most prominent uses."
  },
  {
    "id": "castle-vallen",
    "name": "The Castle of Family Vallen (Dimitri's Castle)",
    "world": "Vampire world",
    "description": "Seat of King Dimitri Vallen, reached by a long driveway up a big hill — 'At the gates, the top of the castle could just be made out over yonder.' A valet takes cars at the castle doors. The tour given to Evelyn names it plainly: 'This is the Castle of Family Vallen... we are the highest respected underground producers of assassins, bounty hunters, as well as intelligence agents,' a family deep in the black market. Its halls hold a main ballroom, a large circular judgment room where restrained prisoners are brought before the King, the royal couple's chambers, a dark back-of-castle office built for Evelyn's mother, a balcony used for the coronation, and one forbidden double-doored private room of Dimitri's that no tour includes. Elsewhere in his kingdom stand a theater (which staged a play of the King's own orphan-to-king life) and a guarded safe-house apartment building for visiting dignitaries. Dimitri issues vampire-restraining chains to all his guards.",
    "features": [
      "Gated hilltop estate ('the Vallen estate') with valet and long driveway",
      "Family trade: assassins, bounty hunters, intelligence agents; heavy black-market involvement",
      "Main ballroom and central halls shown on the formal tour",
      "Large circular room where prisoners are judged",
      "Dimitri's forbidden private room behind double doors — entry denied until the King allows it",
      "Back-of-castle office of Evelyn's mother; short 'office hours in the castle' before she returns to her residence in town",
      "Castle balcony used for the Queen's coronation; spikes 'underneath my castle' invoked in the King's threats",
      "Kingdom sites: the theater with back alleyway and stage door; the dignitaries' safe house with attendant and special security (guarded by Dimitri's cousin Kai)"
    ],
    "knownBy": [
      "dimitri",
      "evelyn"
    ],
    "evidence": [
      "\"Welcome h—to the Vallen estate... At the gates, the top of the castle could just be made out over yonder.\" (dimitri-castle)",
      "\"This is the Castle of Family Vallen. If you didn't know, we are the highest respected underground producers of assassins, bounty hunters, as well as intelligence agents\" (dimitri-castle)"
    ],
    "au_notes": "The channel also contains a separate human-royalty AU in which Prince Dimitri rules a rival kingdom (office with a long table, carriages, rival kingdom Lugnica; Stella/Melisende arc). In lets-abduct-evie, Dimitri's 'stone castle' hosts Evelyn's mother's castle-based office and a court psychologist, Leon; the Han-route line that 'Dimitri runs a black market' matches the Vallen trade."
  },
  {
    "id": "clarke-kingdom-castle",
    "name": "King Emerson Clarke's Castle",
    "world": "Vampire world",
    "description": "The castle of King Emerson Clarke, an ancient vampire king — roughly 51,000 years old, born a vampire — who dresses like classical British royalty and rules by ironclad custom. His butler Simeon has served ten thousand years; predecessors 'were only here for a few thousand.' A tower holds the guest chambers and spare wardrobes, with the finest fabrics — imported from 'the valosh realm, a secret realm for only the most elite' — kept in the back. The royal suite has an attached walk-in closet and master bathroom; blood tea is a house staple, and a table is laid for the Queen-to-be's human meals. The King's second-in-charge, the advisor Jinko, teaches ruling, law and custom in a study room ('Vampires don't shake hands'), and the scarred maid Gracie attends the Queen. A church stands within the grounds for royal weddings.",
    "features": [
      "Tower guest chambers with spare wardrobes; elite fabrics from the valosh realm",
      "Butler Simeon (ten thousand years of service); staff turnover measured in millennia",
      "Advisor Jinko's lessons in kingdom law and etiquette; 'Vampires don't shake hands'",
      "Royal suite with walk-in closet and master bathroom; maids Gracie and little Angelika",
      "Blood tea; formal dining table; church for the royal wedding",
      "Kingdom customs enforced in public: the King must be deferred to outside private rooms"
    ],
    "knownBy": [
      "gracie",
      "jinko"
    ],
    "evidence": [
      "\"My name is Emerson Clarke. And you are my Queen.\" (haley-finds-out-shes-cool-2)",
      "\"This is the guest chambers where we keep our spare wardrobes... The finer fabrics the king suggested are in the back.\" (haley-finds-out-shes-cool-2)"
    ],
    "au_notes": "From the Haley goddess route: Haley, secretly the goddess Atropos with reality-writing powers, is claimed as Emerson's Queen; the god Moros intrudes on the castle at will. The kingdom is the 'Clarke kingdom' after its King."
  },
  {
    "id": "manor-cormac",
    "name": "The Manor Cormac",
    "world": "Vampire world",
    "description": "Manor-castle of the Irish vampires — 'the Vampires of Cork' — set on wooded grounds in Cork, Ireland, reached by a long winding path, its old door opening with a creak. The family (Rory its cool-voiced leader, the joking Senan, warm Fiadh, confiding Ciarnan, and surly Goll) lives under a rule found nowhere else: 'We have a strict 8AM-7PM stay at the manor rule that we keep to... this is non-negotiable' — daylight is the dangerous time in Cork, for 'creatures that go bump in the day' haunt the county, and family members lured off the grounds 'have disappeared forever, never to return to the manor.' The lost are so many that most of the family will not take partners. Guest rooms are private and comfortable if plainer than allied luxury; the surrounding forest hides watchers, among them the otherworld guide Fiann.",
    "features": [
      "Long winding path through the grounds; creaking manor door; encircling forest",
      "The 8AM-7PM daytime curfew, non-negotiable for residents and guests alike",
      "Generations of unexplained disappearances; 'Our family has had to account for lost members for a long time'",
      "Family: Rory (leader), Senan, Fiadh, Ciarnan, Goll; only one partnered couple in the family",
      "Private guest rooms across the hall from one another"
    ],
    "knownBy": [
      "rory",
      "fiadh",
      "ciarnan",
      "evelyn"
    ],
    "evidence": [
      "\"...night had already fallen on the grounds of The Manor Cormac. As Evelyn walked through the long winding path that led to the manor...\" (the-kingdom-fallon)",
      "\"We have a strict 8AM-7PM stay at the manor rule that we keep to... this is non-negotiable.\" (the-kingdom-fallon)"
    ],
    "au_notes": "The channel is titled 'the-kingdom-fallon' but the text names only Cork, the Manor Cormac, and the otherworld kingdom beyond; the disappearances feed directly into the Kingdom of Lir arc."
  },
  {
    "id": "kingdom-of-lir",
    "name": "The Otherworld Kingdom of Lir (Bitu)",
    "world": "Fae-adjacent",
    "description": "An otherworld realm entered through a cave in the woods near the Manor Cormac: deep inside, 'the tunnel of darkness growing bright,' the walls turn to meshed multicolored crystals before opening on 'a large castle in the middle of the otherworld... shockingly bright and white.' Bowing guards line the way to a long hall ending in large double doors 'decorated with delicate carvings of swans,' which open of their own accord onto King Lir's throne room — his throne beside an empty one of equal height kept for his lost wife Eva, and four smaller thrones for his warrior children. The kingdom is called Bitu, 'the world of eternal living beings': none may kill within it ('They would have only been able to do that in the old world'), time runs slow against the human realm, and leprechaun flesh is served as a delicacy. Lir believes Evelyn to be Eva reborn, and posts magic-wielding guards to keep her.",
    "features": [
      "Crystal-walled cave passage from the mortal woods",
      "Bright white castle; swan-carved double doors that open on their own",
      "Throne room: Lir's throne, the empty equal throne of Eva, four smaller thrones",
      "Bitu's laws: no death within the realm; time dilation against the human world",
      "Leprechaun flesh served at table ('This one was a traitor from the westlands of Bitu')",
      "Fiann, the forest guide who lures the chosen through; royal guards who trade their protective magic to shield the King's beloved"
    ],
    "knownBy": [
      "evelyn"
    ],
    "evidence": [
      "\"It doesn't take the group long to get to a large castle in the middle of the otherworld. It was beautiful—shockingly bright and white.\" (the-kingdom-fallon)",
      "\"...led down a long hall down to large double doors decorated with delicate carvings of swans.\" (the-kingdom-fallon)"
    ],
    "au_notes": "A reincarnation arc drawing on the Children of Lir myth: Evelyn is received as 'Eva,' Lir's dead wife reborn; the Manor Cormac's centuries of disappearances are bound up with this realm."
  },
  {
    "id": "the-keller-camp",
    "name": "The Keller Camp",
    "world": "Vampire world",
    "description": "A hidden settlement of witch-and-wizard folk — the community Frau Von Abendroth comes from, founded by her father — reached by turning down a strange corner through an empty alleyway ending at a stone ivy wall. 'This camp is all about finding your true self and living life without worrying so much about the little stuff.' Its people carry chameleon blood ('We smell like what[ever is around us]'), which is how Frau — Adely Von Abendroth — served vampire houses for decades with no one ever knowing what she was. The camp has a mess hall famed for its cooking and summer celebrations, hillside cabins (rustic outside, modern and comfortable within — some with enchanted windows), gardens where the women gather at night, and a trial tent where a table of family heads and elders, with Madame Odella, oversees rites — including a test that transports the candidate to the cold, thunder-rumbling cavern of Atrius, first of their people, fated King of Mycenae. Outsiders may join only through Madame Odella's rare transformation potion, and members must earn the right to leave camp.",
    "features": [
      "Hidden entrance: an alleyway ending in a stone ivy wall",
      "Mess hall with celebrated cooking and seasonal celebrations",
      "Hillside cabins — Frau's is 'rustic on the outside' but 'a regular two floor home on the inside'; the Keller manor-cabin has enchanted windows and a stately fireplace living room",
      "No servants: chores by hand or magic",
      "Madame Odella's rare transformation potion for outsiders; marriage by mutually signed parchment, often for companionship rather than love",
      "The trial tent and the cavern of Atrius with its pedestals of powers",
      "The Kellers: the information-keeping family whose duty is 'to know the ins and outs of all the information that lingered in the camp'; Collin among them",
      "Frau's line: 'You're a Von Abendroth now. Act like it. We have almost as much pride as the Hans do. We're just much kinder.'"
    ],
    "knownBy": [
      "frau",
      "evelyn"
    ],
    "evidence": [
      "\"My father founded this camp so there is always a home for me.\" (frau-collin)",
      "\"This camp is all about finding your true self and living life without worrying so much about the little stuff.\" (frau-collin)"
    ],
    "au_notes": "From the route in which Frau reveals she is a witch, formally adopts Evelyn as her daughter, and brings her into the camp; the corpus's own term for the community's people is 'gypsy,' with Atrius named as their first."
  },
  {
    "id": "ryotos-mountain",
    "name": "Ryoto's Mountain",
    "world": "Dragon realm",
    "description": "The remote mountain realm of Ryoto, the great dragon — 'a dragon born from the mountain. The magic of the mountain was created by and for and with him. They are equals and connected.' There is 'only one way up and one way down… and that was through Ryoto,' who flies visitors in; leaving the mountain for long weakens him. Its magic transforms any vampire who enters into a human for the duration of their stay. Ryoto administers a village of mountain people who bow to him; his own home is the pagoda, where Aunt/Ms. Tia keeps the kitchen (bacon at dawn, an oven hauled up the mountain, the little local paper 'the mountaineer'), with a fireplace main room, a loveseat, and a throne. Behind the house a hole in the mountainside leads under a waterfall to the seeker, who reads truth from the mountain through a crystal ball. A bar warms the colder face of the mountain on Wednesday nights, dragons gather to talk and wager, and villagers secretly dig an escape tunnel — 'If the mountain dies, they are determined to survive' — which Talbot can show a trusted friend, past 'a couple of communities living in the mountain.'",
    "features": [
      "One way in or out: carried by the great dragon himself",
      "Vampires are rendered human while on his land",
      "The pagoda: Ryoto's residence with kitchen (Ms. Tia), fireplace main room, loveseat, throne",
      "The village of mountain people Ryoto created and administers; 'the mountaineer' local paper",
      "The seeker's cave behind the waterfall, with crystal ball ('I am the seeker and I seek the truth from what the mountain tells me')",
      "The Wednesday-night bar on the cold side of the mountain; the village café where Talbot works",
      "The secret escape tunnel dug inside the mountain, hidden from the great dragon",
      "Ms. Tia's greeting to Evelyn: 'chosen one... May you bring our mountain and great dragon peace and prosperity'"
    ],
    "knownBy": [
      "ryoto",
      "evelyn",
      "ms_tia",
      "talbot"
    ],
    "evidence": [
      "\"He's a dragon *born* from the mountain. The magic of the mountain was created by and for and with him.\" (lets-abduct-evie)",
      "\"There was only one way up and one way down… and that was through Ryoto.\" (lets-abduct-evie)"
    ],
    "au_notes": "From the abduction route: Ryoto steals Evelyn from Dimitri's castle by paid wagon (about an hour's ride) using vampire chains Dimitri issues to his own guards; the route ends with Zeus striking Ryoto down in the pagoda. Evelyn is revealed as a demigoddess (Ares suggested as her father) whose fire cannot burn her."
  },
  {
    "id": "the-facility",
    "name": "The Facility",
    "world": "Vampire world",
    "description": "A vampire punishment site named but never described. Evelyn invokes it as the fate worse than death awaiting a vampire-turned-werewolf caught by her own kind: 'If I'm lucky I'll be killed. If I'm unlucky...well they'll take me to the facility. And you don't want to go there.' Nothing else about its location, appearance, or methods appears in the corpus.",
    "features": [
      "Named but never described; invoked as a fate worse than execution"
    ],
    "knownBy": [
      "evelyn"
    ],
    "evidence": [
      "\"If I'm lucky I'll be killed. If I'm unlucky...well they'll take me to the facility. And you don't want to go there.\" (evelyns_mansion)"
    ],
    "au_notes": "Mentioned once, in the war-AU conversation about Evelyn's turning."
  },
  {
    "id": "the-valosh-realm",
    "name": "The Valosh Realm",
    "world": "Vampire world",
    "description": "A place named but never described: the source of the finest fabrics in King Emerson Clarke's castle — 'It's imported from the valosh realm, a secret realm for only the most elite.' No geography, inhabitants, or access are ever shown; it exists in the corpus only as the provenance of elite vampire luxury goods.",
    "features": [
      "Named but never described; 'a secret realm for only the most elite'"
    ],
    "knownBy": [],
    "evidence": [
      "\"It's imported from the valosh realm, a secret realm for only the most eli[te].\" (haley-finds-out-shes-cool-2)"
    ],
    "au_notes": "Single mention, in the Emerson Clarke route."
  },
  {
    "id": "drewmore-college",
    "name": "Drewmore College",
    "world": "Schools",
    "description": "The flagship supernatural-cover college where Kya and Evelyn's college routes take place. Humans believe it is simply famous and exclusive; in truth it is a school for the supernatural — gods, vampires, sirens, werewolves, Norns, Valkyries, banshees and changelings attend alongside humans. It is astronomically expensive, so mostly high-profile supernaturals enroll, and it is the only school that would accept vampires. The headmaster is a dragon; each head teacher is a representative of a popular supernatural race so students can go to someone of their own kind. Campus geography recurs across routes: the quad, the front lawn, the green (with Agnar's people-watching tree, grown from a seed by Agmund), a huge student center with exposed beams, a cafeteria of species-specific restaurants with pantheon-divided tables, the campus Selkiebucks coffee shop, a theater with a white marble fountain out front (secret Roman entrance), an aquarium lab with animal tanks, equestrian facilities that host an annual derby, a parking lot, a walking trail, and four student residence buildings (the Greek House, The Dreg, The Gardens, and the invitation-only Elite building, home of the school leaders and a rumored secret society — never entered on screen).",
    "features": [
      "Four residence buildings: Greek House, The Dreg, The Gardens, and the invitation-only Elite building with its rumored secret society",
      "The green — main student hangout, overlooked by Agnar's tree (grown by Agmund from a seed)",
      "Cafeteria with different restaurants catering to each supernatural species; Norse table, Greek table, werewolf table",
      "Student center — very tall, large windows, expensive exposed beams; hosts sword fights and parties",
      "Campus Selkiebucks — sea salt caramel selkiecinno made with real seawater for sirens and selkies",
      "White marble fountain by the theater — deity-only secret entrance to the underground Roman city",
      "Aquarium lab, equestrian derby facilities, quad, front lawn, trail, parking lot",
      "Mascot: the honeybadger, picked by the founding president as the most unassuming cover-school mascot",
      "Headmaster is a dragon; head teachers are species representatives (e.g. the Norse representative)",
      "Off-campus hangouts: the surf shack (beach diner) and the blacklight nightclub"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "jon",
      "donal",
      "raye",
      "darren",
      "lex",
      "milo",
      "aibell",
      "sebastian_siren",
      "dillon",
      "polly",
      "agnar",
      "agmund",
      "penelope_valk",
      "svira",
      "may",
      "riku",
      "kiro",
      "hiro",
      "cole",
      "lacie",
      "ben",
      "rowan",
      "stellar",
      "easton",
      "loki"
    ],
    "evidence": [
      "\"Drewmore was one of the most famous colleges for its architecture though it was so exclusive that not many people got in. At least, that's what humans thought.\" (evelyn-college-2)",
      "\"The headmaster is a dragon. He strikes fear into the Gods as well.\" (college-campus)",
      "\"Drewmore had four separate buildings that were dedicated to student residency.\" (dorms)",
      "\"The founding president of Drewmore thought that the honeybadger was the most unassuming, normal mascot they could have at a supernatural cover school\" (evelyn-college-2)",
      "\"It was an interesting college cafeteria, with different restaurants catering to the different supernatural creatures tastes.\" (college-campus)"
    ],
    "au_notes": "The campus is re-used across several timelines with the same geography (dorms/greek-house/college-campus/cafeteria 2020, the darren-professor-starter + kya-college-2/evelyn-college-2 restart, you-have-me-roman 2022, forced-mate-college 2025). Mascot conflict: honeybadger (evelyn-college-2, with founding lore) vs a throwaway 'Go Drewmore Bulldogs!' cheer (you-have-me-roman) — honeybadger is the better-attested canon. The coffee chain is 'Sirenbucks' in the dorms route but 'the campuses resident Selkiebucks' in kya-college-2. forced-mate-college says there are 'three freshman dorms' with an RA office, curfew, and the ocean a short drive away, versus the four residence buildings of the 2020 route."
  },
  {
    "id": "the-gardens-dorms",
    "name": "The Gardens",
    "world": "Schools",
    "description": "Drewmore's best on-campus apartments short of the Greek House — a coed apartment building not separated by floor, nicknamed The Garden(s). Kya and Evelyn share a furnished end-unit apartment at the end of a hallway (secured by bribing the resident hall coordinator), with a kitchen island and breakfast bar, a couch, separate bedrooms, and Evie's blood-stocked mini fridge. Two doors down is room 606, the apartment Jon (a human scholarship student) shares with Raye — freshmen almost never get in, so Jon considers himself lucky. The building has a common room where freshman meet-and-greets and parties are held, and it sits only a minute's walk from the Greek House. Room doors get knocked on constantly: gods, vampires and Loki himself come calling at all hours.",
    "features": [
      "Kya and Evelyn's end-unit apartment — kitchen island, couch, separate bedrooms, blood mini fridge",
      "Room 606 — Jon and Raye's apartment two doors down",
      "Common room used for freshman get-togethers, sword-fight watching and parties",
      "Coed, not separated by floor; the best housing after the Greek House",
      "One minute's walk from the Greek House"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "jon",
      "raye",
      "donal",
      "agnar",
      "agmund",
      "loki",
      "milo",
      "lex"
    ],
    "evidence": [
      "\"The apartments themselves are separate. Ours is nicknamed The Garden. It's arguably the best place to live on campus other than the Greek house.\" (dorms)",
      "\"I'm in room 606.\" Jon answers her, helpfully (darren-professor-starter)",
      "\"The Gardens were only a minute from the Greek House.\" (greek-house)"
    ],
    "au_notes": "In the darren-professor-starter restart the same geography repeats (Jon in 606 next to Raye, at the end of the girls' hallway)."
  },
  {
    "id": "the-dreg",
    "name": "The Dreg",
    "world": "Schools",
    "description": "The traditional Drewmore dormitory, nicknamed The Dreg — where most freshmen live, coed but separated by floor. It is the budget option: 'It's not a bad place to be. It's just budget,' and most scholarship students live there. In the forced-mate-college timeline the freshman dorm has coed floors with private bathrooms per room, an RA office that hands out room assignments (riggable by someone in a higher position), a head-of-building, single rooms, shared entrance-hall doubles, and a top floor where Kya gets isolated by Polly's meddling; Sebastian's ground-floor room shares an entryway and bathroom with a shifter roommate.",
    "features": [
      "Freshman housing, coed separated by floor",
      "Home to most scholarship students",
      "RA office issuing room assignments; a head of building above the RA",
      "Rooms with own bathrooms; singles and shared-entryway doubles (forced-mate-college route)"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "sebastian_siren",
      "polly",
      "dillon"
    ],
    "evidence": [
      "\"That's the traditional Drewmore dormitory. It's nicknamed *The Dreg*. It's where most of the freshmen live.\" (dorms)",
      "\"Drewmore was astronomically expensive. There was a reason usually only high profile supernaturals went here.\" (forced-mate-college)"
    ],
    "au_notes": "forced-mate-college (2025 route) calls it one of 'three freshman dorms' and gives it private bathrooms per floor, versus the 2020 route's 'coed but separated by floor' budget hall — same building concept, route-variant details."
  },
  {
    "id": "greek-house",
    "name": "The Greek House",
    "world": "Schools",
    "description": "Drewmore's twin fraternity-and-sorority house — Alpha Chi Kappa and Delta Sigma Theta — two connected houses with separate entrances and a connector door between the halves. It is secretly run by Greek gods and goddesses, who recruit students into temporary godhood through initiation rituals and power circles. The gods maintain it themselves at the start of each year (landscaping, repainting faded siding, cleaning gutters): 'We like to party but we also take pride in looking good.' Inside are a modern sitting/game room with an AI music bot named Alex (the girls' side is the same layout in different colors), a glass-walled racquet and paddle ball room, a living room with fireplace on the sorority side, and private bedrooms — every sorority goddess gets her own room because roommates eventually have blow-out fights. It throws a welcome party at the start of every semester, where drinks are laced to get specific supernatural species drunk.",
    "features": [
      "Alpha Chi Kappa (fraternity) + Delta Sigma Theta (sorority), connected with separate entrances and a connector door",
      "Modern sitting/game room with the AI bot Alex; mirrored girls' side in different colors",
      "Glass racquet/paddle ball room",
      "Sorority living room with fireplace; every goddess has a private room",
      "Semester welcome parties with species-targeted spiked drinks",
      "Darren's room: artifacts and art on the walls, a guitar, otherwise a normal college room"
    ],
    "knownBy": [
      "evelyn",
      "kya_sg1",
      "darren",
      "lex",
      "milo",
      "lacie",
      "riku",
      "kiro",
      "hiro",
      "cole",
      "ben"
    ],
    "evidence": [
      "\"There's Alpha Chi Kappa and Delta Sigma Theta. The fraternity and sorority house. The houses are connected but they have separate entrances\" (dorms)",
      "\"Everything gets cleaned and spruced up at the beginning of the year. We like to party but we also take pride in looking good.\" (greek-house)"
    ],
    "au_notes": "In evelyn-college-2 the Greek House is reached by 'a long path' from the party and is again the gods' base; in the 2020 route Evelyn briefly lives on the sorority side before eloping."
  },
  {
    "id": "roman-underground-city",
    "name": "The Roman Underground City",
    "world": "Schools",
    "description": "A hidden city of Roman deities beneath Drewmore, entered through the white marbled fountain near the theater's front yard. Only deities can see the fountain's side open into a staircase — and touching the fountain alongside a Roman god instantly makes you a Roman deity ('See the fountain, make contact with the fountain, boom'). Below lies an underground city of coliseums and Roman buildings lit by a magical night sky, loosely modeled on the Roman Mount Olympus. Time runs twice as slow (48-hour days), which the Romans credit for their academic edge. Each resident's dorm-house is conjured from their own magic and vanishes at graduation; there is a shop selling togas and clothing priced in denarii, and the tallest building — which could hold thousands — is dedicated to the Romans' beloved toga orgies (average 10 people, up to 52). The fountain also carries a campus legend: a cursed silver bell/ball that makes whoever removes it fail out. Professors like Darius live above ground in full faculty houses.",
    "features": [
      "White marble fountain entrance by the theater — deity-only staircase; touch it with a Roman and you become one",
      "Underground city of coliseums and Roman buildings under a magical night sky",
      "Time flows 2x slower than the surface (48-hour days)",
      "Self-conjured dorm-houses that disappear on graduation",
      "Toga shop trading in denarii",
      "The tallest building, dedicated to toga orgies",
      "Cursed fountain bell — remove it and you never graduate",
      "Faculty houses on campus for professors (Darius's 4-bed home with white picket fence)"
    ],
    "knownBy": [
      "kya_sg1",
      "stellar",
      "easton"
    ],
    "evidence": [
      "\"They walked down into an underground city of coliseums and roman buildings lit by a magical night sky.\" (you-have-me-roman)",
      "\"It's two times slower than normal time. So every hour up there is two hours down here.\" (you-have-me-roman)",
      "\"Welcome to our secret entrance. It's much cooler than the stupid Greek house.\" (you-have-me-roman)"
    ],
    "au_notes": "This route (2022) also gives Drewmore the mascot cheer 'Go Drewmore Bulldogs!', conflicting with the honeybadger canon of evelyn-college-2."
  },
  {
    "id": "correctionis-inpositis",
    "name": "Correctionis Inpositis",
    "world": "Schools",
    "description": "Dike's experimental supernatural school — a four-year 'test run of a program that will build a better future' where every teacher is a representative of a supernatural race (sirens, vampires, werewolves, gods) sent to improve inter-species relations. Athena serves as Headmistress; Dike, goddess of justice, created and recruited for it, choosing a remote forest location for its werewolf students' accessibility. Kya and Evelyn arrive as teachers, not students. Its enrollment contract waives all liability: 'All and any liability is waived from any participant in case of any incidents of any kind' — a clause that ends up authorizing mass vampire turnings during the Diana crisis. The grounds include an auditorium, a faculty dining hall, a gym for school dances, a staff break room with copy and lamination machines, Athena's classroom, species wings (god wing, vampire wing), a teachers' dormitory with species-customized suites, and indoor plus large outdoor siren pools.",
    "features": [
      "Founded and recruited by Dike; Athena is Headmistress",
      "Remote forest location chosen for werewolf students",
      "Liability-waiver contract signed by all participants",
      "Teacher wings by species: god wing, vampire wing (Dimitri keeps a captivity room for newly turned vampires in his suite)",
      "Teachers' dorm rooms customized per species — siren suites hold an indoor pool with ocean bed plus a surface bed and kitchen",
      "Large outdoor siren pool maintained by the siren teachers",
      "Classes taught include Evasion, Deceptive Swimming, Siren Society, werewolf history, environment and zoology"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "sebastian_siren",
      "aaron",
      "dimitri",
      "bryce",
      "rex",
      "rio",
      "lex",
      "milo",
      "athena",
      "cupid_eros",
      "sabrina_witch",
      "kiro",
      "diana",
      "dike",
      "audrey"
    ],
    "evidence": [
      "\"Welcome, students, to your first year of Correctionis Inpositis.\" (auditorium)",
      "\"Dike picked this location for Correctionis Inpositis for its remoteness and accessibility for werewolf students.\" (school-gym)",
      "\"*All and any liability is waived from any participant in case of any incidents of any kind.*\" (auditorium)"
    ],
    "au_notes": "The dining-hall channel opens 'After the long introduction in the cafeteria', but the auditorium channel shows that welcome speech happening in the auditorium — the writers used 'cafeteria' loosely; the standalone cafeteria channel is actually set at Drewmore, not here."
  },
  {
    "id": "correctionis-auditorium",
    "name": "Correctionis Inpositis Auditorium",
    "world": "Schools",
    "description": "The assembly hall of Dike's school, with a stage and rows of seating. Athena delivers the opening-year welcome speech here as Headmistress while the teachers lean against the back walls; she stays afterward answering student questions. It is later the stage for the Diana possession arc: the moon goddess, wearing Evelyn's body, gathers the ten werewolf students and their professors here and orders a night attack on the school, and Evelyn wakes on its floor with no memory. Its hallway lockers stand just outside.",
    "features": [
      "Stage used for the Headmistress's welcome address",
      "Teachers' standing area along the back walls",
      "Site of Diana's rally of the werewolf students",
      "Lockers in the adjoining hallway"
    ],
    "knownBy": [
      "athena",
      "kya_sg1",
      "evelyn",
      "dimitri",
      "rex",
      "bryce",
      "audrey",
      "milo",
      "lex",
      "diana"
    ],
    "evidence": [
      "\"I am Athena, the Goddess of Wisdom and War, and I will be acting as your Headmistress throughout the next four years\" (auditorium)",
      "\"Tonight...when the moon is at it's highest, we will meet up here and cause the chaos they wanted to see from us.\" (auditorium)"
    ],
    "au_notes": ""
  },
  {
    "id": "correctionis-dining-hall",
    "name": "Correctionis Inpositis Dining Hall",
    "world": "Schools",
    "description": "The faculty dining hall where the teacher meet-and-greet mixer is held on day one, seating the staff by faction — a Norse table, a god table, a werewolf table and open tables between. It is a diplomatic minefield played for comedy: Agnar's paint prank splatters Audrey, Sebastian slips in the puddle, Dimitri nearly brawls with Bryce over an insult to Evelyn, Prince Aaron formally courts Kya over welcome packets, and Milo, son of Hades and Dike, scandalously 'pets' Evelyn under the table. Dining hall napkins and species diplomacy in equal measure.",
    "features": [
      "Faction tables: Norse, god, werewolf, plus open seating",
      "Site of the faculty welcome mixer",
      "Where Aaron and Kya's arranged-marriage courtship begins",
      "Scene of the Dimitri–Bryce standoff defused by Rex"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "sebastian_siren",
      "aaron",
      "dimitri",
      "bryce",
      "rex",
      "milo",
      "lex",
      "agnar",
      "agmund",
      "audrey",
      "rio"
    ],
    "evidence": [
      "\"After the long introduction in the cafeteria, all of the faculty goes to the dining hall where they would get to meet and speak with each other.\" (dining-hall)",
      "\"Athena was still in the auditorium with the students answering questions and Cupid was visiting other tables, so it was just Lex and Milo who were sitting there.\" (dining-hall)"
    ],
    "au_notes": ""
  },
  {
    "id": "correctionis-school-gym",
    "name": "Correctionis Inpositis Gym",
    "world": "Schools",
    "description": "The school gym, which doubles as the venue for student dances — decked with a punch bowl, a popcorn cart the teachers take shifts watching, upbeat EDM and a dance floor where teachers scandalize students by joining in. Kya and Aaron share their first kiss mid-dance here (to student catcalls) the day before their arranged wedding, and Milo and Evelyn spar flirtatiously by the wall. Outside its doors lies the surrounding forest where Rex teaches Evelyn the pull of the moon under the full moon — the mistake that lets Diana in.",
    "features": [
      "Dance-night venue: punch bowl, popcorn cart, dance floor, EDM",
      "Teachers chaperone and (inappropriately) join the dancing",
      "Doors open onto the forest around the school"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "aaron",
      "rex",
      "milo"
    ],
    "evidence": [
      "\"I promised to watch the popcorn cart and the students are calling me over.\" (school-gym)",
      "\"It may be a dance for the students, but they never said teachers couldn't join.\" (school-gym)"
    ],
    "au_notes": ""
  },
  {
    "id": "correctionis-staff-break-room",
    "name": "Correctionis Inpositis Staff Break Room",
    "world": "Schools",
    "description": "The faculty room and break room of Dike's school — home of the copy and lamination machines (used to laminate welcome packets against water-based accidents like Skipper the dolphin), plus break-room chairs where the teachers plot in crisis. Rex and Evelyn hole up here to research Saph, the embodiment of the shadow world, and plan the mass vampire turning; from here the hallways lead to the vampire wing and the god wing of the teacher quarters. Aaron asks Kya on their first date over the laminator.",
    "features": [
      "Copy and lamination machines",
      "Break-room chairs and planning space",
      "Connects to the vampire wing and god wing of the teachers' quarters"
    ],
    "knownBy": [
      "kya_sg1",
      "aaron",
      "evelyn",
      "rex",
      "dimitri"
    ],
    "evidence": [
      "\"He sighs and turns off the copy and lamination machines.\" (staff-break-room)",
      "\"He's with the other gods in the god wing.\" (staff-break-room)"
    ],
    "au_notes": "The dining-hall channel calls the same facility 'the faculty room' / 'staff room' when Aaron offers to laminate Kya's welcome packet there."
  },
  {
    "id": "athena-classroom",
    "name": "Athena's Classroom",
    "world": "Schools",
    "description": "Athena's classroom at Correctionis Inpositis, with a meeting table and a wooden chair at each desk. Between lessons it serves as the gods' council room — Lex, Milo, Athena, Kiro, Cupid and the witch-goddess Sabrina meet here during the Diana crisis. It becomes the site of the route's darkest beat: after Diana (possessing Evelyn) orders Rex to kill her host body, the gods coolly advise a stake or poison, Cupid and Sabrina vow to find her soul on the other side, and Rex snaps a leg off one of the classroom chairs and stakes Evelyn through the heart — 'I promise, my moon, I will find you.'",
    "features": [
      "Gods' meeting table",
      "Wooden chairs at each desk — one becomes the stake that kills Evelyn",
      "Where Diana confronts her son Kiro and abandons Evelyn's body"
    ],
    "knownBy": [
      "athena",
      "lex",
      "milo",
      "kiro",
      "cupid_eros",
      "sabrina_witch",
      "evelyn",
      "rex",
      "kya_sg1",
      "diana"
    ],
    "evidence": [
      "\"Rex saw them hanging out in Athena's classroom having a meeting while he got the beds.\" (staff-break-room)",
      "\"Rex leans over to grab a wooden chair from next to him. There was one at each desk.\" (athena-class)"
    ],
    "au_notes": ""
  },
  {
    "id": "merryweather-prep",
    "name": "Merryweather Preparatory Academy",
    "world": "Schools",
    "description": "The supernatural high school of Kya's senior-year route — its name appears at the top of the class schedules. Kya and Sebastian enroll for one 'human' year alongside Evelyn and Kaleb, staying with the Han family. It looks like a normal prep school (front doors, office for schedules, alphabetical lockers, homeroom, bells, homecoming with a game and a dance the same week) but the student body is anything but: vampires (Ezra, Raye, Tate), a bakeneko cat spirit (Shadow), succubi and sirens share classes. Electives include home economics under the terrifying Mr. War — 'The classroom is my kingdom' — who teaches recipes from every supernatural species, and a notorious necromancy ethics class where students are literally killed and revived (Kya glimpses 'this big shining gate' before being pulled back into her body; complaints eventually thin the roster).",
    "features": [
      "Name printed on schedules: Merryweather Preparatory Academy",
      "Office issuing schedules and locker assignments; lockers in alphabetical order",
      "Mr. War's home economics room — cuisine of every supernatural species",
      "Necromancy ethics class where students die and are revived at a shining gate",
      "Homecoming game and dance; front entrance benches where suitors wait",
      "Limo pickups for the vampire heirs"
    ],
    "knownBy": [
      "kya_sg1",
      "evelyn",
      "sebastian_siren",
      "kaleb",
      "ezra",
      "raye",
      "tate",
      "shadow",
      "primrose"
    ],
    "evidence": [
      "\"At the top of the schedule was the school's name. Merryweather Preparatory Academy.\" (kya-senior-year)",
      "\"I remember this big shining gate and being pulled back into my body.\" (kya-senior-year)"
    ],
    "au_notes": "high-school-ezra-kya (2021) is a separate, earlier high-school AU: an unnamed school Evelyn attended long-term — 'the only school that serves vampire food' — whose decorated auditorium hosts junior prom ('It's like a starry night'); there Kya first meets Ezra. In kya-senior-year Evelyn has instead never attended school before. Treat the two schools as route variants of the supernatural high school."
  },
  {
    "id": "esu",
    "name": "ESU",
    "world": "Schools",
    "description": "A mostly-human state university (2025 route) where Emma, a scholarship-minded biology freshman, collides with Leon — heir to a lion-shifter pride quietly attending classes. The crowded campus quad includes The Grove, with uneven pavement that trips Emma into the meet-cute; there is a science building with lab practicals, long lecture halls, an on-campus cafe where Leon takes her on their first date, a coffee shop just off campus, and a quiet, secluded spot by the campus lake — a small clearing hidden by weeping willows — where Leon first reveals his lion form. The supernatural hides in plain sight here; the pride's fenced sanctuary lies within reach of campus.",
    "features": [
      "The Grove — crowded quad crossing with uneven pavement",
      "Science building (Introduction to Biology lab practicals)",
      "On-campus cafe and an off-campus coffee shop",
      "Secluded lakeside clearing behind weeping willows — shifter reveal spot"
    ],
    "knownBy": [
      "leon",
      "trenton"
    ],
    "evidence": [
      "\"her foot caught on an uneven bit of pavement in The Grove\" (esu)",
      "\"There's a quiet spot by the lake on campus - secluded, but still public enough to ease my paranoia.\" (esu)"
    ],
    "au_notes": "The route continues into Leon's lion-shifter sanctuary compound and the leopard shifters' throne hall, which are not part of the university itself."
  },
  {
    "id": "olympus-deity-academy",
    "name": "The Academy of the Gods (Olympus Training Center)",
    "world": "Greek divine",
    "description": "Olympus's deity training center, 'just like a human school' with set hours, where every new god must train — normally 100 years — and win the approval of all ten (usually eleven) trainer-gods to pass. Demeter calls it 'the academy of the gods' as she leads Evelyn in; Artemis runs a classroom stacked with piles of hand-made magical artifacts that react to each student's energy (whatever responds to you is yours to keep — a fire-flickering sword leaps to the Daughter of Ares). Between classes students use the common room, library, cafeteria and gym, and every twenty-five years the school holds a formal, the most heavily god-warded event in the realm. Athena's teaching post sits empty while she runs Correctionis Inpositis in the mortal world.",
    "features": [
      "Ten to eleven trainer-gods must approve you to graduate (Demeter, Artemis among them)",
      "Artifact classroom — keep any hand-forged artifact that reacts to your energy",
      "Common room, library, cafeteria, gym for students",
      "Formals every twenty-five years, fully sanctioned and safeguarded by the gods",
      "Athena's post vacant while she teaches on the mortal plane"
    ],
    "knownBy": [
      "evelyn",
      "demeter",
      "zerden"
    ],
    "evidence": [
      "\"Our training center is just like a human school. There are set hours and training teachers.\" (mt-olympus)",
      "\"In this room are many magical artifacts meant for you to use and test different aptitudes of magic on. Explore and see if they react to you.\" (mt-olympus)"
    ],
    "au_notes": "Training length is inconsistent between routes: mt-olympus holds to 100 years with time moving FASTER on Earth, while the dorms route claims '100 years of training which is a couple of weeks in Olympus.'"
  },
  {
    "id": "gates-of-olympus",
    "name": "The Gates of Olympus",
    "world": "Greek divine",
    "description": "The soul-processing entrance to Mt. Olympus, worked for thousands of years by a miserable Hercules — 'human warrior made god of strength and the official gatekeeper of Olympus.' Arriving souls queue in line; Hercules judges each with a single question about unfinished business, grudges or revenge schemes, writes the name in his ledger and checks a column (a pair of wings marks those sent down through the clouds to become angels instead of gods). Souls too honest to hold grudges don't qualify for godhood. Hermes drops travelers at the entrance; when Hercules finally rage-quits ('DAD, I QUIT!' — he'd rather run the Olympus Gym), the gates stand open and even Hades slips through. A freestanding elevator connects heaven below to Olympus above, with a six-hour wait that equals six days in the human world.",
    "features": [
      "Hercules's gate post, ledger and wings column",
      "One-question judgment: unfinished business, grudges, or revenge schemes",
      "Souls denied godhood fall through the clouds to heaven as angels",
      "Elevator link between heaven and Olympus (6-hour wait = 6 human days)",
      "Hundreds of steps up to the main pantheon beyond the gates"
    ],
    "knownBy": [
      "hercules",
      "evelyn",
      "hades",
      "persephone",
      "libion",
      "hermes",
      "zeus"
    ],
    "evidence": [
      "\"It was Hercules, human warrior made god of strength and the official gatekeeper of Olympus, and he was processing souls through or...below, down to another place.\" (mt-olympus)",
      "\"Do you have any unfinished business, grudges, or revenge schemes planned?\" (mt-olympus)"
    ],
    "au_notes": "This gate-side judgment is the corpus's on-screen version of the soul-sorting the app's Fates lore attributes to the In Between — Hercules simply decides by his one question."
  },
  {
    "id": "olympus-main-pantheon",
    "name": "The Main Pantheon of Olympus",
    "world": "Greek divine",
    "description": "The residence of the twelve main gods at the heart of Olympus, reached by marble paths that span through the clouds. Each major god keeps quarters expressing their domain: Ares's pantheon is 'a large, cavernous pantheon room made of shining red rock,' hot but welcoming, its ruby walls flickering with small magical fires — with a bedroom prepared for Evelyn on the day she was born (a room that is mostly bed, in true Greek fashion). Aphrodite's quarters are a great shrine of her own floating in the sky. A courtyard fronts the pantheon, and children of the Twelve live at home until they come of age (at least 100 years of training) and earn their own pantheon or residence.",
    "features": [
      "Home of the twelve main gods; marble paths through the clouds",
      "Ares's ruby-rock pantheon with living magical fires",
      "Aphrodite's sky-shrine quarters",
      "Courtyard; bedrooms that are 'mostly bed'",
      "Gods under 100 years live with their divine parent"
    ],
    "knownBy": [
      "ares",
      "evelyn",
      "aphrodite",
      "persephone",
      "demeter",
      "hercules",
      "zeus"
    ],
    "evidence": [
      "\"Evelyn soon found herself inside a large, cavernous pantheon room made of shining red rock.\" (mt-olympus)",
      "\"They hunted down Aphrodite's quarters--which was located in a big shrine of her own in the sky.\" (mt-olympus)"
    ],
    "au_notes": "Persephone's flower-covered bedroom is also here during her strike against Hades; Darren and Hades themselves are barred from Olympus."
  },
  {
    "id": "cupid-hq",
    "name": "Cupid HQ",
    "world": "Greek divine",
    "description": "Cupid's matchmaking megacorp — 'a company that runs on both earth and the god world' — headquartered in a Los Angeles skyrise whose top is visible across the city. Cupid (Eros), self-styled CEO, works from an office with a desk, leather chair and a wall of live video feeds showing 'people... all in situations of love,' from childhood meet-cutes to elderly couples (and everything between; he watches it all). The firm has 'revolutionize[d] the way searching for love works': no more arrows — magic is synced to smartphones via the cupiphone™, loaded with love-themed apps (Night-outfit-o-matic instant wardrobe, teleporting contact lists, GPS, a regular-cellular mode), all client costs billed to the business. His daughter Halo handles break-ups ('destroys connections between people, normally peacefully' — sometimes with a bazooka), causing hellish paperwork when clients die early; his son Hiro learned the family trade. Client matches come in curated folders of three.",
    "features": [
      "LA skyrise HQ, visible across the city",
      "Cupid's office: desk, leather chair, guest chair, wall of live love-feeds",
      "The cupiphone™ and its magical apps; teleport-by-contact",
      "Match folders of three hand-picked suitors",
      "Halo's break-up division; Hiro's future-vision matchmaking",
      "Guarded office floor where Hermes' insta-travel (drachma-based) delivers visitors"
    ],
    "knownBy": [
      "cupid_eros",
      "halo",
      "hiro",
      "sabrina_witch",
      "evelyn"
    ],
    "evidence": [
      "\"We at Cupid HQ have decided to revolutionize the way searching for love works. It is no longer with arrows in old fashioned ways. We have synced up magic to smart phones\" (cupid-hq)",
      "\"Evelyn saw him sitting back in a big leather chair looking at screens on the wall of people doing every act of romance.\" (greek-house)"
    ],
    "au_notes": "In cupid-hq Cupid's children's mother is Athena; in greek-house Eros's office is the same LA screen-room but Hiro's twin is Halo and Hiro courts Evelyn. Cupid also appears as a married teacher (with Sabrina) at Correctionis Inpositis — route variants of the same god."
  },
  {
    "id": "fates-underworld-home",
    "name": "The Fates' Underworld Home",
    "world": "Underworld",
    "description": "The hidden home of the three Fates — Clotho, Lachesis and Atropos, daughters of Nyx — deep in the underworld, where the sisters work the strings of fate away from the other gods. Lachesis admits she rarely leaves it ('I don't leave the underworld often or the other gods start to...bother me'), living vicariously through mortals whose lives she watches; Clothos remains 'watching her sisters from the depths of the underworld' whenever the other two reincarnate to the mortal plane, left to take care of the work alone. The Fates are honored by only a single festival a year. A ledge along Lachesis's daily path overlooks most of the underworld, and its cavern walls witness her whole tortured history with Lance.",
    "features": [
      "The sisters' workplace and refuge in the depths of the underworld",
      "Clothos keeps watch and fixes fate's mistakes from here when her sisters leave",
      "Ledge overlooking most of the underworld along Lachesis and Lance's daily path",
      "Honored by one festival a year and little other worship"
    ],
    "knownBy": [
      "lachesis",
      "atropos",
      "clothos",
      "nyx",
      "lance",
      "moros"
    ],
    "evidence": [
      "\"I don't leave the underworld often or the other gods start to...bother me.\" (lance-origin-route)",
      "\"Clothos, who was watching her sisters from the depths of the underworld\" (lance-origin-route)"
    ],
    "au_notes": "The app's character canon details the cavern's three work stations (Clotho's spindle, Lachesis's measuring, Atropos's cutting) and the Scales-of-Fate paperwork of the In Between; those station specifics come from character-canon backstory — the corpus shows the sisters living and working in the underworld's depths but never stages the spindle/measure/cut stations in scene, and the phrase 'Scales of Fate' never appears in the corpus."
  },
  {
    "id": "the-in-between",
    "name": "The In Between",
    "world": "Otherworld",
    "description": "A sealed between-realm 'perpendicular' to purgatory — a dumping ground for souls that fit nowhere. 'It's a place, an in between place, that some souls get sucked into when there's… no place to put them. God chose who went to the in-between,' and only sufficient angel magic can open a door into it. Behind that door: 'Fields and fields full of snow. Mountain ranges. Vast overcast skies and wind blowing' — the door vanishes once crossed. Monstrous creatures roam it, including a giant winged red-eyed wolf; Ford was imprisoned here until Lachesis and Clothos broke in to retrieve him. Its cold-humidity smell is distinctive even from the threshold.",
    "features": [
      "Perpendicular to purgatory; holds unplaceable souls chosen by God",
      "Endless snowfields, mountain ranges, overcast wind-blown skies",
      "Entry doors conjured by strong angel magic that disappear behind you",
      "Monstrous inhabitants (giant winged wolf with red eyes)",
      "Ford's prison until his rescue"
    ],
    "knownBy": [
      "lachesis",
      "clothos",
      "ford"
    ],
    "evidence": [
      "\"the in-between is perpendicular to it. It's a place, an in between place, that some souls get sucked into when there's… no place to put them.\" (switched-roles-lachesis)",
      "\"There was snow behind it. Fields and fields full of snow. Mountain ranges. Vast overcast skies and wind blowing.\" (switched-roles-lachesis)"
    ],
    "au_notes": "This snowy prison version comes from the switched-roles AU (Fates as angels). The app's Fates canon instead frames the In Between as fate's soul-sorting office with the Scales of Fate and unfinished paperwork; the closest on-screen match to that function is Hercules's one-question soul processing at the Gates of Olympus (mt-olympus)."
  },
  {
    "id": "heaven-city-of-angels",
    "name": "Heaven (City of the Angels)",
    "world": "Heaven",
    "description": "'Similar to earth, but in the clouds, heaven was blindingly beautiful.' New souls are met by peace-keeper angels (Olora) who grant fresh physical bodies; wingless angels must earn their wings through good deeds. A great palace floats in the sky 'where all the former leaders go when they die and are made into an angel' — Cruz waits there with his wolf. At Olora's kiosk sits the glowing book of quests, 'special tasks that the gods send to angels looking to get their wings' or aiming for archangelhood (archangels alone travel freely between heaven and earth). In the palace courtyard stands a freestanding elevator attached to nothing that rides down through the planes — 'It used to be a staircase and before that it was a horse and buggy.' Sin darkens an angel's wings; sin enough 'in thought, word, or deed' and the clouds stop holding you: you fall, trapped on earth forever with only human abilities. Elsewhere in the clouds: God's garden of apple trees, His humble cottage in a stretch of heavenly woods, the Plains of the Righteous where hero angels craft the paths of future angels, the Fountain of More, guest quarters, entry gates with days-long waits — and one absolute law: killing in heaven brings the true, final death.",
    "features": [
      "Palace in the sky for former leaders made angels",
      "Olora's kiosk, the glowing book of quests, deed tokens and the wings wheel",
      "Freestanding era-shifting elevator in the palace courtyard (1 heaven-hour ≈ half a human day)",
      "Wings darken with sin; falling strips angels to human ability, stranded on earth",
      "God's apple garden, His cottage in the heavenly woods, a house by a great body of water",
      "Plains of the Righteous — hero angels chart future paths",
      "Fountain of More; main gate with entry angels and long queues",
      "Killing in heaven = true death, final and absolute"
    ],
    "knownBy": [
      "evelyn",
      "cruz",
      "libion",
      "lachesis",
      "lance",
      "god",
      "ramiel",
      "atropos",
      "moros",
      "clothos",
      "rika"
    ],
    "evidence": [
      "\"Similar to earth, but in the clouds, heaven was blindingly beautiful.\" (heaven)",
      "\"a big palace in the sky, where all the former leaders go when they die and are made into an angel\" (heaven)",
      "\"You have to sin...it's the only way to fall. In thought, word, or deed.\" (heaven)",
      "\"No killing in the heavens. It brings the true death when you die up here. Not just the second death. Here, death is final.\" (oops-its-heaven)"
    ],
    "au_notes": "Three routes share this heaven: Evelyn's angel arc (heaven, 2020), Lance & Lachesis's visits to God (oops-its-heaven, 2022), and the switched-roles AU where the Fates are heaven-born angels (Fountain of More, Plains of the Righteous, togetherness ball). Time ratios differ slightly between routes."
  },
  {
    "id": "fountain-of-eden",
    "name": "The Fountain of Eden",
    "world": "Heaven",
    "description": "A relaxation bath for the angels of the heavens — a big stone-gray fountain bath set in a flowered courtyard. Once beautiful, it withers scene by scene while the archangel Ramiel bathes there in emotional agony: 'one by one each flower and petal and blade of grass was wilting' around him, hell's corruption following him even into paradise. It is where Lachesis finds him, where he finally confesses he has always loved her, and where God's furious arrival ends with Lachesis silenced and chained. A loud bang across the fountain's courtyard announces His coming.",
    "features": [
      "Stone-gray fountain bath for angelic recuperation",
      "Surrounding gardens that wilt in the presence of Ramiel's despair",
      "Courtyard where God confronts Ramiel and Lachesis"
    ],
    "knownBy": [
      "ramiel",
      "lachesis",
      "god"
    ],
    "evidence": [
      "\"The fountain of Eden used to be a beautiful place until Ramiel arrived and everything started to grow ill.\" (oops-its-heaven)",
      "\"the big stone gray fountain bath meant to give relaxation to angels of the heavens\" (oops-its-heaven)"
    ],
    "au_notes": ""
  },
  {
    "id": "mafia-city",
    "name": "The Mafia City (Lance & Lachesis's Human Trial)",
    "world": "Mortal world",
    "description": "The unnamed old-world American city where Lance and Lachesis play house as a 'married' Catholic couple for his redemption trial. Its streets are carved up among secretive crime families — 'They were currently the sixth main mafia family in the city' — with the Contes (devout Catholic gang under boss Angelo; his heirs Gino, Aldo and Donna) holding their neighborhood against the crueler Gaglianos. Lamppost corners host after-dark muscle, nothing opens without a family's backing, and newcomers get background-checked by the mob. Lance buys the couple a walk-up above a storefront with a sectioned-off private back area; Aldo suggests turning the shop into a bakery under Conte protection. Landmark references include the Peters' old place and Brunos.",
    "features": [
      "Territory of the Contes (Catholic mafia) vs the Gaglianos",
      "Lance and Lachesis's apartment above a storefront with a private back section",
      "Street-lamp corners where the gangs hold court after dark",
      "No business opens without a family's backing",
      "Devout-Catholic underworld culture (Sunday mass, confession)"
    ],
    "knownBy": [
      "lance",
      "lachesis",
      "aldo"
    ],
    "evidence": [
      "\"You might wanna get back home before the Gaglianos come around. They're much less kind than we Contes are to the neighborhood women.\" (oops-its-heaven)",
      "\"He bought a place above a storefront with a sectioned off area in the back that they could get privacy in.\" (oops-its-heaven)"
    ],
    "au_notes": "The city is never named; its telegraph-and-radio technology and mob-era texture read as a deliberately old-fashioned pocket of the mortal world chosen for Lance's 'human experience' trial."
  },
  {
    "id": "hell-snowy-village",
    "name": "Hell's Entrance — The Snowy Village & Lake of Lost Souls",
    "world": "Hell",
    "description": "The gateway to Lucifer's domain, which he built himself — 'There was no such place as Hell before I came down here. I created this place' — and redecorates whenever he gets bored. Arriving souls wake in a snowy village beside a bridge over murmuring water: 'This is the entrance to my domain. It is a snowy village. The bridge you crossed over is the lake of lost souls. Any poor sap who falls in there will be trapped, shouting out into the abyss for the rest of eternity.' Hell has nine circles, but it is not all torture: low-sinning souls live partially free lives in towns (bowing when Lucifer passes), reformed souls may settle in the town rather than serve the castle, and the demon district houses his salaried demons — with restaurants, television and family homes. Classic date spot: black coffee beside the pit of fire, watching two-faced souls burn. Lucifer calls the whole snowy region 'his little snowy edge of paradise.'",
    "features": [
      "Snowy village entrance and the bridge over the lake of lost souls",
      "Nine circles; punishment phase before souls may join hell's infrastructure",
      "Towns for low-sin souls; reformed souls settle there",
      "Demon district — restaurants, TV, paid demons with families",
      "The pit of fire where the two-faced burn",
      "The Spirit Union — hell's irritating afterlife labor union"
    ],
    "knownBy": [
      "lucifer",
      "atropos",
      "lachesis",
      "moros",
      "hades",
      "asrius",
      "lonnie",
      "tazariah",
      "renni",
      "lula"
    ],
    "evidence": [
      "\"There are nine circles of Hell. This is the entrance to my domain. It is a snowy village. The bridge you crossed over is the lake of lost souls.\" (atropos-in-hell)",
      "\"There was no such place as Hell before I came down here. I created this place.\" (atropos-in-hell)"
    ],
    "au_notes": "Hell is distinct from Hades' underworld and Nyx's realm — Moros searches 'Olympus and Heaven and Hades' before finding Atropos here. Lucifer periodically redesigns the whole realm to taste."
  },
  {
    "id": "lucifers-castle",
    "name": "Lucifer's Castle",
    "world": "Hell",
    "description": "Lucifer's palace at the heart of his snowy hell — called castle, mansion, and in the switched-roles telling 'Lucifer's grand palace,' so beautiful visitors are stunned that the pits of hell could look like this. Run by the long-suffering butler Lochley, it houses a servants' quarter of shared dormitories (four to a room — Lachesis draws Room 302 — with communal bathrooms down the hall and a strict no-fires-no-potions rule), a greenhouse where head gardener Lonnie keeps gardens blooming despite the snow, a main dining hall where Lucifer eats with his son Asrius, and private wings — 'All of the fallen angels in hell had their own wings. Ironically.' Beyond the walls stands the stadium where Lucifer stages the games: contests for sinners with rewards and punishments, watched from his rigid throne in the stands. His bedroom keeps a stone wall on which Atropos once scrawled in her own blood — he refuses to clean it off.",
    "features": [
      "Servants' dormitories: four per room, Room 302, shared showers, no fires or potions",
      "The greenhouse and indoor gardens in a snowy climate",
      "Main dining hall; family meals with Asrius and his nanny",
      "Private wings for the fallen angels",
      "The stadium and Lucifer's games for sinners, ruled from his throne",
      "Atropos's blood-writing preserved on the bedroom wall",
      "Lochley the butler; Lonnie the head gardener (Ramiel's twin)"
    ],
    "knownBy": [
      "lucifer",
      "lachesis",
      "atropos",
      "asrius",
      "lonnie",
      "tazariah",
      "renni",
      "lula",
      "moros",
      "cruz",
      "ford"
    ],
    "evidence": [
      "\"Room 302. There are shared bathrooms and showers down this hallway.\" (haggle-for-the-girl)",
      "\"Despite it being a snowy climate here in hell, we are able to maintain beautiful gardens inside of the castle.\" (haggle-for-the-girl)",
      "\"they were deep in the pits of hell. Which were actually really freaking beautiful. Because they were standing in the middle of Lucifer's grand palace.\" (switched-roles-lachesis)"
    ],
    "au_notes": "Appears across three routes: Atropos's deal-making stay (atropos-in-hell), Lachesis's servitude after Lucifer buys her soul from Hades for twenty thousand souls (haggle-for-the-girl), and the switched-roles war council. In haggle the throne shown on-screen is his seat at the games; a formal throne room is implied but not staged."
  },
  {
    "id": "the-endlands",
    "name": "The Endlands",
    "world": "Endlands",
    "description": "The realm where everything discarded ends up: 'Everything that gets destroyed or lost ends up here in the Endlands. Unwanted things. Projects. Missing socks.' Its people are outcast creations — alien-looking creatures, failed projects, the lost — who 'amongst themselves they had found community.' God tricked Ford into it, and time runs wildly fast relative to the mortal plane (days inside pass in minutes outside). The land is split between two feuding thrones: Prince Adrius's kingdom and the kingdom of his stepfather-king Ford, who married Adrius's mother the Queen for politics. Its geography spans snowy mountains (with a hotel where citizens breakfast together), the great city of Orfesr with its harvest festival, a hidden city built into caves — behind a curtain and down stone staircases lies a luxurious cavernous palace where Ford hides under another name — and a 'FREE THE ENDLANDS' rebellion rallying in warehouse lots. The royal line began with the first King of the Endlands, whose modest house survives inside a snow-globe keepsake. Endlands food is addictive, almost an aphrodisiac to outsiders; the realm's great treasure is the prism, which grants the power to will creation.",
    "features": [
      "Realm of discarded creations, lost things and outcasts-turned-community",
      "Two rival kingdoms: Prince Adrius vs stepfather King Ford",
      "Ford's fortress — 'his castle home was on the highest mountain in the Endlands' — with a throne room mapping the realm behind the thrones",
      "The royal summer castle (used by Adrius during the feud; named, never described in scene)",
      "Orfesr, one of the biggest cities, and its harvest festival",
      "Hidden cave city and Ford's luxurious cavern palace behind a cellar",
      "Snowy mountains and the travelers' hotel",
      "The prism — power to will creation; the FREE THE ENDLANDS rebellion",
      "Extreme time dilation vs the mortal plane; addictive native food"
    ],
    "knownBy": [
      "ford",
      "adrius",
      "lachesis",
      "clothos",
      "lucifer"
    ],
    "evidence": [
      "\"Everything that gets destroyed or lost ends up here in the Endlands. Unwanted things. Projects. Missing socks. You name it.\" (switched-roles-lachesis)",
      "\"his castle home was on the highest mountain in the Endlands and looked more like a fortress than a palace\" (switched-roles-lachesis)",
      "\"For Endlands sake you've been using the summer castle.\" (switched-roles-lachesis)"
    ],
    "au_notes": "Exists within the switched-roles AU (fallen-angel Ford). The summer castle is canon by name only — the Queen scolds Adrius for using it, but no scene is set inside it. Ford appears in two phases: reigning from the mountain fortress, and later hiding in the cave city under an alias while paying for look-alikes of his lost love."
  },
  {
    "id": "kingdom-of-welis",
    "name": "The Kingdom of Welis",
    "world": "Demon kingdoms",
    "description": "A wealthy demon kingdom of the Other World, ruled from a golden palace — the childhood home of Prince (later King) Eros of Welis, whose gardens little Eros sketched Aria in when her father served as head guard and her mother as lady-in-waiting. Welis 'treats its people very well'; after a thousand years of Aria's reincarnation trials the capital returns 'better developed. Bigger. Grander.' Its castle holds the throne room of Wellis (tile floors, an adorned throne and royal scepter, where queens are crowned and where the feared sorceress Batiuba comes to collect her bargains), a grand foyer and staircase, the royal bedchamber, dungeons, and the main hall where the citizenry gathers for royal weddings — including the forced wedding Caria stages at scepter-point, ended by King Solum's army breaching the gates.",
    "features": [
      "The golden palace and its gardens",
      "Throne room of Wellis — adorned throne, scepter, coronations, Batiuba's intrusion",
      "Main/grand hall for public royal weddings",
      "Castle doctor, dungeons, staircases, royal suites",
      "Paws, the royal tiger, lounging about the castle",
      "Prophecy: whoever weds Aria becomes the most powerful King in the Other World"
    ],
    "knownBy": [
      "aria",
      "caria",
      "batiuba",
      "solum",
      "benji",
      "bennett",
      "ragland",
      "ajax"
    ],
    "evidence": [
      "\"*Little Eros was sitting outside in the gardens of the golden palace that his family called their own.\" (eros-and-kent-wing)",
      "\"Upon further inspection of the room, Aria sees the hooded figure from the attack sat upon the throne of Wellis.\" (kingdom-of-wellis)",
      "\"The kingdom looked different. It was better developed. Bigger. Grander.\" (kingdom-of-welis)"
    ],
    "au_notes": "Spelled both 'Welis' and 'Wellis' across channels — same kingdom. The demon prince Eros of Welis is a separate character from the Greek god Eros/Cupid. Rival kingdoms of the Other World named in this arc: Temprum (the twins') and Penta, Ajax's prideful kingdom, named but never visited on screen."
  },
  {
    "id": "kingdom-of-temprum",
    "name": "The Kingdom of Temprum",
    "world": "Demon kingdoms",
    "description": "'Temprum--the home of all temptation. Lust, desire, intrigue... all of this and more was found in this hellish place in the demon's Other World.' One of the Other World's richest kingdoms, 'decorated with fine taste...but it seemed more seductive than threatening,' warm but never as hot as hell. Behind the main fortress a wall of rock conceals an underground network where the kingdom's succubi and citizens live, each playing a role in tyrant King Solum's games. The fortress is entered up red stairs through guarded front gates into a grand foyer with a large circular staircase; royals keep entire harem wings (Aria's doubles as her living quarters, staffed by thousands of volunteer 'fledglings' with individually themed rooms — Fawn's kink closet, Naveed's bare room with a mat and his snake), and the twins Benji and Bennett share a wing beside hers. The royal bloodline's unique gift is the Reset: with intricate golden pocket watches they restart willing lives from scratch, drawing petitioners from every kingdom — waiting list included.",
    "features": [
      "Main fortress: red stairs, front gates, grand foyer with circular staircase",
      "Underground succubi network behind the wall of rock",
      "Harem wings with themed fledgling rooms (Fawn, Naveed among Aria's)",
      "The Reset — golden pocket watches; clients come from all kingdoms",
      "Harem towers with reinforced, lubricated-steel BDSM rooms",
      "King Solum's harem court; Neitl, his intelligence officer",
      "Bennett's secret cage hall hidden in the fortress"
    ],
    "knownBy": [
      "aria",
      "benji",
      "bennett",
      "solum",
      "fawn",
      "naveed",
      "neitl",
      "ajax"
    ],
    "evidence": [
      "\"Temprum--the home of all temptation. Lust, desire, intrigue... all of this and more was found in this hellish place in the demon's Other World. Situated behind the main fortress was a wall of rock that contained an underground network of succubi and the rest of the Kingdom's citizens.\" (kingdom-of-temprum)",
      "\"One of my jobs as Prince is to Reset those who wish to be.\" (kingdom-of-temprum)"
    ],
    "au_notes": "The twins' mother was murdered by the King of Penta (Ajax's kingdom); Solum married into the bloodline, so only the twins carry the true Reset gift. Late in the arc the armies of Penta and Wellis march on Temprum."
  },
  {
    "id": "tir-na-nog",
    "name": "Tír na nÓg (The Otherworld of Kingdom Fallon)",
    "world": "Otherworld",
    "description": "The Celtic otherworld, entered through a cave in the woods outside Cork, Ireland — the tunnel walls turning to meshed multicolored crystals before opening onto 'the other side.' It is 'one of the greatest kept secrets from the mortal plane... known to the Irish in legend as Tír na nÓg--the land of the young. Here, time moves incredibly slowly. What is a week here is but a minute in the mortal planes.' Celtic gods, King Lir, and the hero-band the Fianna reside here; within it no one can truly kill another ('They would have only been able to do that in the old world'). At its center rises a large, shockingly bright white castle: bowing guards, a long hall, great double doors carved with swans, and a throne room holding the King's throne, an equal-height empty throne kept for his lost Queen Eva, and four smaller thrones for their children. The food hall serves delicacies like leprechaun flesh — a traitor 'from the westlands of Bitu' — which grants the eater two wishes to bestow. The sorcerer-guard Lughaid veils rooms so nothing passes in or out.",
    "features": [
      "Crystal-walled cave passage from the Cork woods",
      "Bright white castle at the otherworld's center",
      "Swan-carved double doors; twin thrones plus four children's thrones",
      "Food hall; leprechaun flesh delicacy granting two wishes",
      "The Fianna — sworn protectors of the otherworld (Fiann, Cailte, Goll, Diarmund, Lughaid, Finn)",
      "No true death within the realm",
      "Extreme time dilation vs the mortal world"
    ],
    "knownBy": [
      "evelyn",
      "fiann",
      "cailte",
      "goll",
      "diarmund",
      "lughaid",
      "finn"
    ],
    "evidence": [
      "\"This is the otherworld. One of the greatest kept secrets from the mortal plane. Though it is known to the Irish in legend as Tír na nÓg--the land of the young. Here, time moves incredibly slowly. What is a week here is but a minute in the mortal planes.\" (the-kingdom-fallon)",
      "\"It doesn't take the group long to get to a large castle in the middle of the otherworld. It was beautiful—shockingly bright and white.\" (the-kingdom-fallon)"
    ],
    "au_notes": "'Fallon' exists only in the channel title — the name never appears in scene text, where the realm is the otherworld/Tír na nÓg and the kingdom is once glossed as 'this kingdom—the world of eternal living beings' with 'the westlands of Bitu.' Route premise: Evelyn is retrieved as the reincarnation of Lir's dead Queen Eva — or possibly of Eva's twin Aoife, who is not welcome there."
  },
  {
    "id": "kingdom-of-infernio",
    "name": "The Kingdom of Infernio",
    "world": "Fairy realms",
    "description": "The fire (summer) fairy kingdom of the celestial realm, ruled by Prince Ashton's line from a castle with a carriage courtyard where crowds of subjects greet royal arrivals. Outsiders can visit the celestial realm but not eat its food, and must be escorted through the barrier by a resident or die — so brides like Kya are transformed on arrival. The castle has isolatable wings (convenient when Kya's prophetic glowing wings must be hidden behind a cover story of 'the trep,' a fairy flu), a dining hall, magical mirrors that show the lives of people on the mortal realm, and a garden where seedlings — babies bestowed by the Great One — are planted and watered, the servants planting their own beside the royals'. Furniture is wrapped in magical fire-resistant plastic while fire magic is trained. The kingdom's wilds hold beautiful volcanoes, tended in a cycle of aid with the other elemental kingdoms.",
    "features": [
      "Castle courtyard, isolatable wings, servants' quarters",
      "The garden where seedlings (fairy babies) are planted",
      "Magical mirrors showing mortal lives",
      "Volcanoes kept in check with water/ice fairy help",
      "Fire-resistant plastic wrap for magic lessons",
      "The oracle's house nearby, where the unification prophecy is read"
    ],
    "knownBy": [
      "kya_sg1",
      "ashton",
      "taren"
    ],
    "evidence": [
      "\"I am Prince Ashton of the Infernio.\" (kyas-choice)",
      "\"Yes. We do have a garden in the castle. The servants plant their own seedlings there as well since they live in the servants quarters.\" (kyas-choice)"
    ],
    "au_notes": "kyas-choice frames five fairy kingdoms in a dependency cycle — fire/summer (Infernio), water-ice/winter (Mizu), Spring (Prince Keller's palace), Fall (Prince Dash and the sacred matchmaking hippo; currency 'fairies'), and Spirit (King Gale, who lives alone in a spirit-crowded house) — plus a shunned sixth dream-invading fairy at the realm's center, where the original rulers once ruled. Kya, the prophesied all-element queen, ends up wed to all five princes."
  },
  {
    "id": "kingdom-of-mizu",
    "name": "The Kingdom of Mizu",
    "world": "Fairy realms",
    "description": "The water and ice fairy kingdom on the far side of the celestial realm from Infernio, ruled by young Prince Taren — 'a cold, selfish asshole, but that makes him the best at teaching water and ice magic, where you have to shut off your emotions to master.' Its seat 'seemed like a regular castle, like one would imagine out of a fairytale,' kept by a suited head caretaker and servants who can conjure a feast on a whim; Taren keeps a small personal library with a fireplace, an un-water-fairy indulgence. Mizu holds the celestial realm's only ocean besides the forest kingdom's — it dries up before reaching the desert-dwelling earth fairies — and Kya swims it to test her lingering siren self, meeting sea-dog pups. Feast invitations here formally mark alliance and friendship.",
    "features": [
      "Fairytale castle with head caretaker and feast-ready staff",
      "Taren's private library with fireplace",
      "The celestial ocean (shared only with the forest kingdom)",
      "Feast custom sealing alliances",
      "Center of water/ice magic instruction"
    ],
    "knownBy": [
      "kya_sg1",
      "taren",
      "ashton"
    ],
    "evidence": [
      "\"We'll be entering the kingdom of Mizu.\" (kyas-choice)",
      "\"Yes, there an ocean here, but it's only in my kingdom and the forest kingdom. It dries up before it reaches the earth fairies that reside in the deserts.\" (kyas-choice)"
    ],
    "au_notes": "Mizu doubles as the 'winter' seat in this route — Kya's title after wedding Ashton and Taren is 'Queen Kya of the Summer and… Winter fairies.'"
  },
  {
    "id": "winter-fairy-court",
    "name": "The Winter Fairy Court",
    "world": "Fairy realms",
    "description": "The winter fairies' kingdom from Kya and Zed's shared past-life regression (kya-college-2): a court that throws a monthly winter ball in the palace ballroom, bordered by gardens and a courtyard where prince and pixie steal away from the crowds. Prince Zander, 'the prince of the winter with one issue....he had no winter powers,' hides his magicless birth as the kingdom's most dangerous secret — a powerless winter king would invite invasion. Lily, a pixie of a higher order, serves in the Bells of Winter: 'It was her job to help the gods bring snow to earth during the winter months,' crafting snowflakes for the mortal world. Winter fairies wear ice like cloth — Lily conjures matching ice outfits to scandalize the court — and homes here can be sealed in ice. The pair eventually rule the winter kingdom together, and the realm connects onward to the Celestial Planes by portal.",
    "features": [
      "Monthly winter ball in the palace ballroom",
      "The Bells of Winter — snow-bearers for the mortal world",
      "Palace gardens and courtyard",
      "Ice-conjured fashion and ice-sealed cottages",
      "State secret: the magicless prince"
    ],
    "knownBy": [
      "kya_sg1",
      "prince_zed"
    ],
    "evidence": [
      "\"Zander was the prince of the winter with one issue....he had no winter powers.\" (kya-college-2)",
      "\"She was part of the Bells of Winter. It was her job to help the gods bring snow to earth during the winter months.\" (kya-college-2)"
    ],
    "au_notes": "Framed as a past life relived via a sea-sorceress's potion — Kya was Lily, Zed was Zander. Winter-court material appears in three unrelated guises across the corpus: this Bells-of-Winter court, Mizu as the 'winter' seat in kyas-choice, and Ramiel introduced as 'the prince of the winter fairies' in switched-roles-lachesis."
  },
  {
    "id": "fall-fairy-kingdom",
    "name": "The Fall Fairy Kingdom",
    "world": "Fairy realms",
    "description": "The autumn fairies' kingdom in the celestial realm, ruled from a Royal castle guarded by rival twin princes Damien and Anders. It is reached from the mortal world through the Super — a notorious supernatural rooftop bar in the city that hosts an annual winged Halloween party — via 'a room inside the bar. A secret room... the portal between realms.' The kingdom's sacred crown chooses the next queen: it is moved only once a year for the ball where all eligible women try it on, and when stolen and found by the human Autumn it fuses to her head and sprouts her wings. The castle grounds include a small door leading underground to surprisingly comfortable holding cells (soft bed, moon-lit window), a clock tower whose eleven chimes mark curfew, guest rooms with fireplaces and endless closets, and an enormous dining hall where 'Servants, royalty, everyone in the castle' eat together — the one time all social barriers drop. The court still whispers of the despair of Vurnen, the reign when every eternal one vanished without a trace.",
    "features": [
      "Royal castle of the twin princes; tutor Draven's lesson hall",
      "The sacred crown and its yearly queen-choosing ball",
      "Portal room hidden inside the Super rooftop bar (mortal side)",
      "Underground holding cells behind a small door",
      "Clock tower curfew at eleven chimes",
      "Enormous dining hall where rank dissolves at mealtime",
      "Purple-tinged grass; Joe, the pet raccoon 'trash goblin'"
    ],
    "knownBy": [
      "draven",
      "alistair"
    ],
    "evidence": [
      "\"We don't move it except for once a year when we hold the yearly ball where all of the eligible women try on the crown.\" (fall-fairies)",
      "\"Servants, royalty, everyone in the castle was in the enormous dining hall, enjoying a meal together. Here at food time, there were no social barriers.\" (fall-fairies)"
    ],
    "au_notes": "The crown theft that starts the route was a job for 'Alistair and his associates' — Anders stole it for them and lost it. Despite the assignment note, no winter court appears in this channel; the kingdom is explicitly the fall fairies' own."
  },
  {
    "id": "celestial-tournament-castle",
    "name": "The First Ruler's Castle & Tournament Grounds",
    "world": "Fairy realms",
    "description": "The great castle of the celestial realm's founding line — built in honor of a commoner from the village of Frell and the Lord of Leapald after 'The Great One blessed them to be the parents of the first ruler of the Celestial Realm.' Generations later Princess Daphne holds court here, and the castle fills with suitors when a traditional tournament is declared for her hand: 'traditional competitions to prove their worth. Magic, strength, intelligence,' fought on the castle grounds with the court watching from the walls and gardens, contenders like the hero-house scion Cair, gentle Feran of the Renningtons, and Lox — with real risk of death unless the princess decrees an end. Its courtyard, back doors, halls and balconies host the political scheming between bouts. The wider fae realm around it belongs to the earlier arc of Sabrina/Claire: the princes' home country Estrella, a realm where rain falls up under multiple suns, log-cabin homesteads, and an old-movie diner grown through with living trees serving glowing celestial milkshakes.",
    "features": [
      "Castle built in honor of Frell and Leapald, seat of the first ruler's line",
      "Tournament for the princess's hand — magic, strength and intelligence trials",
      "Suitor-crowded halls, courtyard and gardens",
      "Estrella, the princes' kingdom elsewhere in the fae realm",
      "Upward rain, multiple suns, tree-melded diner with glowing milkshakes"
    ],
    "knownBy": [
      "kieran"
    ],
    "evidence": [
      "\"After they joined The Great One blessed them to be the parents of the first ruler of the Celestial Realm and this castle was built in their honor.\" (new-fairy-route)",
      "\"I've come for the tournament like most of the men you'll be running into around the castle.\" (new-fairy-route)"
    ],
    "au_notes": "new-fairy-route spans two generations: the 2023 Sabrina/Claire prophecy arc (Holt, Archie, Dutch of Estrella) and the 2025 Princess Daphne tournament arc, where Kieran also appears at the princess's side."
  },
  {
    "id": "valerian-fairy-kingdom",
    "name": "Cordelia's Fairy Kingdom",
    "world": "Fairy realms",
    "description": "The celestial-realm kingdom that Summer — born Cordelia, its ruler — is brought home to by her betrothed, the wind fairy Valerian Sanguinar of the kingdom to the west. Arrival is blinding: 'large blue buildings reaching the sky, taller than any buildings they had in her home town with a river flowing around the city.' The grand palace has high painted ceilings, guest wings, and sisters Cyrena and Clairette waiting with a magic mirror they used to watch her mortal life. The kingdom town below offers royal perks — shopkeepers press free wares on the crown, a dress shop where Cordelia tries on gown after nearly identical gown while her long-suffering guard Ciel props up the wall, a fountain plaza where Valerian confers with his guards, and carriages between. The crown jewel of local leisure is the magic waterpark: 'It's controlled by magic, so it makes us feel energized when we go to it like we're one with our magic' — rides and waterslides that leave fairies buzzing with power.",
    "features": [
      "Sky-blue tower city ringed by a river",
      "Grand palace with painted ceilings; the princesses' quarters",
      "Kingdom town: dress shop, stores offering royals free wares, fountain plaza, carriages",
      "The magic waterpark — energizing rides and waterslides",
      "Ciel the guard; Lupe lurking in disguise"
    ],
    "knownBy": [
      "cyrena",
      "clairette",
      "valerian",
      "ciel",
      "lupe"
    ],
    "evidence": [
      "\"she sees large blue buildings reaching the sky, taller than any buildings they had in her home town with a river flowing around the city\" (an-accidentally-valerian-summer-with-cordelia)",
      "\"There's a water park we can go to. We love the water park. It's controlled by magic, so it makes us feel energized when we go to it like we're one with our magic.\" (an-accidentally-valerian-summer-with-cordelia)"
    ],
    "au_notes": "Route premise: changeling queen Cordelia was raised mortal as Summer in Cliff's Edge; Valerian courted her through shared dreams before bringing her home."
  },
  {
    "id": "court-of-shadows",
    "name": "The Court of Shadows",
    "world": "Fairy realms",
    "description": "Valen's fae domain beyond the enchanted forest — 'My name is Valen. I'm the Lord of the Court of Shadows.' Its border with his estranged brother Eryndor's rival court is administered from a shared meeting hall built directly on the line: a room split down the middle, 'thrones on both sides of the line, a few inches away from each other but clearly on separate sides,' inside an otherworldly palace grown into the side of a cliff behind golden gates and a long drive through the wylds. Valen's own castle flaunts his ego — a grand balcony across the front, enormous sculpture-lined gardens, bustling staff including the butler Frederick and eerie triplet maids who speak in unison, a lady-of-the-house suite with a store-sized closet, and the best mages and doctors in the region. His servants live free in the surrounding city; the human village of Raydonia lies across the enchanted forest, whose monsters kill most who wander in.",
    "features": [
      "Border meeting hall with line-split twin thrones",
      "Cliffside palace grown with nature, golden gates and forest drive",
      "Valen's castle: grand balcony, sculpture gardens, triplet maids, Frederick the butler",
      "Lady-of-the-house suite with mini-store closet",
      "Servants' city surrounding the castle",
      "The enchanted forest barrier and human Raydonia beyond"
    ],
    "knownBy": [
      "frederick"
    ],
    "evidence": [
      "\"My name is Valen. I'm the Lord of the Court of Shadows.\" (fae-courts)",
      "\"a large, otherworldly building comes into view built into the side of a cliff. It almost looked like a palace, but it was so grown with nature that she didn't know what she was looking at\" (fae-courts)"
    ],
    "au_notes": "The app's realm-level Fae Courts entry covers the courts broadly; this is the granular Court of Shadows of the 2024 Elara route, whose 'spark' wish-granter decides which brother takes the realm's throne. Eryndor's own court is visited only at the border hall."
  },
  {
    "id": "asgard-city",
    "name": "Asgard, City of the Gods",
    "world": "Norse divine",
    "description": "The Norse gods' home realm — 'they finally reach Asgard, the city of the gods' — one of the nine realms, reached from Midgard by driving to the Bifrost: a literal rainbow bridge spanning 'a large, ominous looking chasm,' watched by Heimdall, 'the guard of the Bifrost gate,' who sleeps less than birds, sees a hundred leagues and hears grass grow. Within the city are garden grounds with a stream where visitors wait out councils, and the large meeting building where Odin convenes the gods — non-Norse deities enter only by marriage or diplomatic invitation; Loki burns it down with a fireworks display spelling his own name, forcing a hasty rebuild. Above all towers Yggdrasil: 'the tree of life. The top of it rests here in Asgard, while the roots and branches connect the rest of the worlds,' home to Nidhoggr at the roots, the eagle and hawk Veofolnir at the crown, and Ratatoskr the gossip squirrel; touching its bark floods you with the love and energy of all the worlds. On its boughs Odin hanged himself, spear-struck, for nine days to win the runes — the source of Norn foresight. Time and meals barely exist here; Agnar and Agmund keep a small three-bedroom house nearby for their mortal-realm habits.",
    "features": [
      "The Bifrost rainbow bridge over the chasm; Heimdall's watch",
      "Garden grounds and stream outside the meeting building",
      "The gods' meeting hall — Odin presiding, Thor, Sif, Frigg and Freya in council",
      "Yggdrasil and its creatures; energy of all the worlds in its bark",
      "Odin's nine-day hanging, origin of the runes and Norn prophecy",
      "Agnar and Agmund's small house (3 bed, 1 bath)"
    ],
    "knownBy": [
      "agnar",
      "agmund",
      "sif",
      "thor",
      "odin",
      "loki",
      "kya_sg1",
      "freya",
      "frigg"
    ],
    "evidence": [
      "\"That is Yggdrasil, the tree of life. The top of it rests here in Asgard, while the roots and branches connect the rest of the worlds.\" (asgard)",
      "\"I am Heimdall, the guard of the Bifrost gate.\" (dorms)",
      "\"Since you are not a true goddess or a Norse goddess, we will have to use the bifrost bridge in order to pass\" (dorms)"
    ],
    "au_notes": "The app's realm list covers Valhalla; Asgard proper is the city around it. The Green on the Drewmore campus is treated as Norse sacred ground — Valkyries who fall there ferry home nightly — and Loki's Sleipnir and Fenrir appear in the connected dorms arc."
  },
  {
    "id": "nyx-realm-shadow-homes",
    "name": "Nyx's Underworld & the Shadow Homes",
    "world": "Underworld",
    "description": "The night goddess Nyx's corner of the underworld — 'The underworld is meant for supernaturals, greeks, and their heroes. I take the best sinners for myself.' Nyx intercepts choice souls like the crusader-killer Lance before hell can claim them and remakes them as her creatures, the Ralidanti: translucent shadow beings with ears and tails who 'only exist in the physical world at night' and hunt on her orders. In exchange each receives freedom in a shadow house of their own. The realm runs chill for anyone not born of darkness; a ledge along the walking paths overlooks most of the underworld, and cavern walls close in everywhere. The Fates, Nyx's daughters, live and work in its depths, honored by a single yearly festival. Physical access from the mortal side runs through public entrances — the nearest in Tennessee, deep in werewolf-patrolled forest — using an 'ez-ron pass from the River Styx'; even deities fear touching the Styx itself.",
    "features": [
      "Nyx's claim on the best sinners; creation of the Ralidanti",
      "Shadow houses granted to her creatures",
      "Overlook ledge and cavern paths",
      "Tennessee forest entrance and the River Styx ez-ron pass",
      "Cold that bites all but creatures of darkness",
      "Home of the Fates in its depths"
    ],
    "knownBy": [
      "nyx",
      "lance",
      "lachesis",
      "atropos",
      "moros",
      "clothos"
    ],
    "evidence": [
      "\"The underworld is meant for supernaturals, greeks, and their heroes. I take the best sinners for myself.\" (lance-origin-route)",
      "\"Then for the rest of my time... I have my freedom in this... shadow house, you say?\" (lance-origin-route)",
      "\"I have a ez-ron pass from the River Styx that I use to get back and forth.\" (lance-origin-route)"
    ],
    "au_notes": "The Ralidanti recur across routes: in Cliff's Edge they are the shadow creatures that hunt werewolves at night, and in oops-its-heaven Lance names himself a Ralidanti during his human trial. In lance-origin-route Nyx also employs Lance as heaven's contracted killer alongside God's Angel of Death."
  },
  {
    "id": "cliffs-edge",
    "name": "Cliff's Edge",
    "world": "Mortal world",
    "description": "A small mortal town that is secretly a werewolf haven — 'How did you find yourself an actual werewolf? By living in a town full of them.' Founded in part by the original Steele brothers, it has exactly three big houses: the Steele pack house on the mountainside by the woods (the pack lives beside the forest so their wolves can roam — and because 'Werewolves are hunted by the shadow creatures calling the Ralidanti,' who would slaughter townsfolk if the pack lived among them), the mayor's gated mansion (Summer's mother), and Alexander Wilburn's, owner of the Cliff's Edge radio station and Cliff's Edge Oil. In town: Cliff's Edge High School, 'a little red brick school in the middle of town only a street away from Summer's home' (its principal is a Steele), a roller-skating rink, kissing creek in the forest, cranberry street, and the neighboring town of Tree's Line. The night woods around it belong to the Ralidanti.",
    "features": [
      "The Steele pack house on the mountain by the woods",
      "The mayor's gated mansion and Wilburn's estate — the town's three big houses",
      "Cliff's Edge High School (red brick, Steele principal)",
      "Roller-skating rink, kissing creek, cranberry street",
      "Night woods stalked by the Ralidanti",
      "Neighboring town: Tree's Line"
    ],
    "knownBy": [
      "cruz",
      "lance",
      "nyx"
    ],
    "evidence": [
      "\"Cliff's Edge High School was a little red brick school in the middle of town only a street away from Summer's home.\" (an-accidentally-valerian-summer-with-cordelia)",
      "\"Werewolves are hunted by the shadow creatures calling the Ralidanti. The Ralidanti are formed from the souls of the dead who end up in the underworld.\" (an-accidentally-valerian-summer-with-cordelia)"
    ],
    "au_notes": "Cruz Steele and the Steele charitable foundation are known town-wide. One line calls Summer's mother 'the Mayor of Steele' while other passages name the town Cliff's Edge ('Cliff's Edge was very small'; 'She'd never left Cliff's Edge') — Cliff's Edge is the better-attested town name, with Steele as the founding family. The 'Cliff's Edge night woods' of the Lance material reflect the Ralidanti's nocturnal hunting grounds around such werewolf towns; lance-origin-route itself stages the equivalent woods at the Tennessee underworld entrance."
  },
  {
    "id": "turayne-cruise-ship",
    "name": "The Turayne Cruise Ship",
    "world": "Mortal world",
    "description": "Alistair Turayne's private charter liner — the stage of his 45-day contest-trap for the reincarnated Atropos. 'We pride ourselves on being the world's largest cruise ship, able to fit a miniature city in its confinements': an entire shopping quarter (with planted, pre-tailored wardrobes), premium and specialty suites, the owner's suite occupying the top floor ('a miniature apartment... Soundproof walls'), an exclusive restaurant Alistair shuts down for two, top-deck cabanas with butler service, a salon and spa, business conference rooms, and a library staffed by a full-time librarian with private soundproof glass rooms for high-ranking supernatural guests. On deck 4 aft sits a multi-faith church with an offering room, where Haley's hand-written song summons Apollo himself in a golden glow. Much of the crew is supernatural, and every 'coincidence' aboard is Alistair's stagecraft.",
    "features": [
      "Shopping quarter — a mini city at sea",
      "Owner's suite: top floor, soundproof, guarded",
      "Library with soundproof glass study rooms and supernatural collection",
      "Church offering room on deck 4 aft — working conduit to the gods",
      "Top-deck cabanas, salon, exclusive restaurant, conference rooms"
    ],
    "knownBy": [
      "alistair",
      "atropos",
      "lachesis",
      "apollo",
      "cupid_eros"
    ],
    "evidence": [
      "\"We pride ourselves on being the world's largest cruise ship, able to fit a miniature city in its confinements.\" (wtf-happened)",
      "\"We have an offering room in the church on board.\" ... \"It's on deck 4 on the aft side.\" (wtf-happened)"
    ],
    "au_notes": "The route where Haley (Atropos) and Nicole (Lachesis) are lured aboard; Apollo's shipboard courtship here feeds the app's canonical Atropos–Apollo romance."
  }
];

export const ENCYCLOPEDIA = {
  locations: LOCATIONS,
  species: SPECIES,
  realms: REALMS,
  magicSystems: MAGIC_SYSTEMS,
  curses: CURSES,
  artifacts: ARTIFACTS,
  alternateRoutes: ALTERNATE_ROUTES,
  loreEvents: LORE_EVENTS,
  culturalRules: CULTURAL_RULES,
  divineGovernance: DIVINE_GOVERNANCE,
  keyRelationships: KEY_RELATIONSHIPS,
};

// 🏰 Groups — the merged two-sweep corpus registry (see src/groupsData.js).
export { GROUPS } from './groupsData.js';
